import { useState, useEffect, useRef, useMemo } from 'react';

import { v4 as uuidv4 } from 'uuid';
import OpenAI from "openai";

import FetchModule from '../../Model/Share/Network/FetchModule';

// Step 1-A — Agent boot (audit + recovery handler 등록, idempotent)
import { bootAgent } from '../../Agent/boot.js';
// Step 1-B — OpenAI 호출 wrapper (transient retry + timeout)
import { aiFetch } from '../../Agent/Recovery/aiFetch.js';
// Step 1-C — CL-1 classifier + CL-5 RESUME_MARKER dispatch (catch 분기 자동 recovery)
//   aiUtils.classifyOpenAIError(OPENAI_RATE_LIMIT/...)와 enum이 다르므로 alias로 격리
import { classifyOpenAIError as classifyForRecovery } from '../../Agent/Recovery/errorClassifier.js';
import { dispatchRecoveryWithMarker } from '../../Agent/Recovery/resumeMarker.js';
// Step 1-D-1 — FL-2 finishPolicy.evaluateFinish 위임 (DISPLAY/OUTRO/NUMERIC 3 guard + [anchor 16] DETAIL_FALLBACK_MISLEADING)
import { evaluateFinish, buildRetryMessage, FINISH_REASONS, detectColumnEnumLeak } from '../../Agent/Output/finishPolicy.js';
import { detectUnfulfilledSpecifier } from '../../Agent/Tools/detailNotFoundGuide.js';
import { detectResponseLanguage } from '../../Agent/Tools/responseLanguage.js';
// [PLACEHOLDER-SUMMARY-SCOPE] explicit top-N intent detection — finish path scope-aware SUMMARY 차단용
import { detectExplicitTopNIntent } from '../../Agent/Tools/extremeIntentGuide.js';
// Step 1-D-2 — OL-6 outputReviewer Momus (env-var off default, A/B 측정 후 production gating 결정)
import { reviewOutput } from '../../Agent/Output/outputReviewer.js';

import {
  AI_MODELS,
  ERROR_CONFIG,
  MAX_OUTPUT_TOKENS,
  RESPONSE_FAILED_RETRY,
  CANCELLED_RESPONSE,
  TOOL_CALLING_LOG,
  buildTemperatureParam,
} from './aiConfig';

import { TOOL_DEFINITIONS, CALL_API_GUIDES, VISUALIZATION_TOOLS, INTENTION_DIMENSIONS, buildComparisonNextGuide } from '../Transaction/aiToolConfig';

import { getTodaySystemMessage, resolveRelativeMonthRange, detectStalePeriodRelabel } from '../Transaction/aiDateResolver';
import { parsePeriodRange } from '../../Model/Individual/StatisticsModel/salesPeriodCompute';
import { buildQueryPrompt } from '../Transaction/aiPromptBuilders';
import { resolvePrompt, setPromptMap, getPromptVersions } from '../Transaction/promptStore';
import { createLlmCollector, createLoggedCompletion } from '../Utils/aiLlmCollector';

import AgentSession from '../Utils/AgentSession';
import { detectTermAndModifier, lookupTermDefinition } from '../Utils/aiUserTermDictionary';
import { buildTurnFact, snapshotBuffers, leanLlmCalls, SCHEMA_VERSION as TRACE_SCHEMA_VERSION } from '../Utils/aiTraceSchema';
import { gzipBase64, decodeMaybeGzip } from '../Utils/aiGzip';
import { createSmsHandlers, buildSmsForSave, restoreSmsFromSave } from '../Transaction/smsHandlers';

import {
  classifyOpenAIError,
  compactMessages,
  neutralizeStaleFailedToolResults,
  decrypt,
  getFallbackMessage,
  renderDisplayAsText,
  stripColumnLabelEchoOutro,
  stripFalseAbsenceClaim,
  stripContradictorySuperlativeClaims,
  stripUnmetHistoryClaim,
  stripBase64Values,
  getBackoffDelay,
  augmentOutroWithChange,
  stripPoliteReproposal,
  stripMarkdownBold,
  stripDuplicateSentences,
  stripAssistantHarnessLeak,
  stripIntroOverlap,
  isIntroDuplicate,
  stripCountContradictionSentences,
  correctReRegScopeNarrative,
  substituteOutroPlaceholders,
  stripPlaceholderArtifacts,
  detectRawNumericLeak,
  detectIntroOutroNumericHallucination,
  stripRawNumberArtifacts,
  isAmbiguousCountKey,
  extractUnitFromFieldLabel,
  buildSpeechRegisterInject,
  detectSpeechRegister,
  detectToneChangeRequest,
  buildSpeechRegisterInjectForRegister,
  stripColumnEnumLeak,
  stripRawIsoDatetime,
  isGarbledInput,
  GARBLED_INPUT_MESSAGE,
  stripPeriodCountMismatchSentences,
  ensurePeriodCountsInIntro,
  ensurePeriodInIntro,
  buildFinanceSummaryIntro,
  normalizeGroupedUnits,
  injectPerMemberRevenueCapabilityDisclosure,
  shouldFireMemberDetailRecovery,
  augmentMemberDetailAspectsFinalText,
  detectRequestedMemberDetailAspects,
  detectVagueMemberSearchName,
  detectVizToolNeed,
  detectSmsToolNeed,
  extractGuardMarker,
  computeFalseNoDataRisk,
  buildMemberTrendFinalText,
  buildMemberStatusFinalText,
  buildMemberRevenueTrendFinalText,
  COMPARISON_REGEX,
} from '../Utils/aiUtils';

bootAgent();

const { AGENT_MODEL, TITLE_MODEL } = AI_MODELS;
const API_KEY_TIMEOUT_MS = 15000;
// [ITERATION CAP D4] 30→14 하향 — 실측 54턴 분포 96%≤7·최대 11(가드 데드락 케이스), 14=관측최대+3 마진. 폭주(L11-INTERSECT류) 비용 상한 >2× 절감 (dual sync with nodeAgent.mjs) //
const MAX_TOOL_ITERATIONS = 14;

// 토큰 소진 안내 — 잔액 0 이하 시 추가 질의 차단 메시지 //
const TOKEN_EXHAUSTED_MESSAGE = '이번 달 AI 토큰을 모두 사용했어요. 다음 달에 다시 이용하시거나 토큰을 충전해 주세요.';

// [VIZ 연결] 시각화 도구 → 핸들러 반환 필드 매핑. 이 필드가 toolResult에 있으면 메시지 vizData로 렌더.
//   AiChattingView가 vizData.type(=필드명)으로 렌더러 dispatch. show_display/sales_view/revenue_chart는 기존 전용 경로 유지(여기 제외).
const VIZ_RESULT_FIELDS = {
  show_attendance_chart: 'attendanceChartResult',
  show_attendance_ranking: 'attendanceRankingResult',
  show_class_attendance: 'classAttendanceResult',
  show_engagement_chart: 'engagementChartResult',
  show_ltv: 'ltvResult',
  show_member_status: 'memberStatusResult',
  show_membership_preference: 'membershipPreferenceResult',
  show_period_sales: 'periodSalesResult',
  show_point_mange: 'pointResult',
  show_member_trend: 'memberTrendResult',
  show_member_flow_trend: 'memberFlowTrendResult',
};

// [L16-SQL-INTERSECT-EMPTY-EXPLAIN] SQL INTERSECT/EXCEPT 0건일 때 finalText에
// slot 단위 breakdown이 누락된 경우 결정적 augment. DATA_READY_PROMPT의 [ZERO-ITEM SLOT BREAKDOWN]
// inject가 LLM 무시 시 safety net. 범용 — case ID 참조 없음.
// dual sync with nodeAgent.mjs augmentSqlEmptyFinalText
function augmentSqlEmptyFinalText(finalText, session) {
  try {
    const eb = session?._sqlEmptyBreakdown;
    if (!eb || !Array.isArray(eb.slots) || eb.slots.length < 2) return finalText;
    const text = String(finalText || '').trim();
    // 이미 slot breakdown 표현 포함하면 skip
    const hasBreakdownSignal = /조건\s*\d/.test(text)
      || /각각\s*\d+\s*명/.test(text)
      || eb.slots.every(s => text.includes(`${s.count}명`))
      || /(교집합|공통|겹치는|중복)/.test(text);
    if (hasBreakdownSignal) return finalText;
    const slotLines = eb.slots.map((s, i) => {
      const cond = s.where ? ` ${s.where}` : '';
      return `  - 조건 ${i + 1} (${s.table}${cond}): ${s.count}명`;
    }).join('\n');
    const operatorKor = eb.mode === 'except' ? 'EXCEPT' : 'INTERSECT';
    const suffix = `\n\n각 조건별 회원 수:\n${slotLines}\n${operatorKor} 결과: 0명 (공통으로 해당되는 회원이 없어요).\n조건을 완화하거나 기간을 조정해 보시겠어요?`;
    if (text.length < 25 || !text.includes('0명')) {
      return `조건에 맞는 회원은 0명이에요.${suffix}`;
    }
    return `${text}${suffix}`;
  }
  catch (_e) {
    return finalText;
  }
}

export default function useAiChat() {
  // API 인증 //
  const [apikey, setApikey] = useState('');
  const [storeName, setStoreName] = useState('');

  // 토큰 소진 상태 — 잔액 0 이하 시 true (UI 노출용 state + 동기 가드용 ref) //
  const [tokenExhausted, setTokenExhausted] = useState(false);
  const tokenExhaustedRef = useRef(false);

  // 사용량 게이지 — 백엔드가 내려주는 사용% (0~100). 미회신(미배포) 시 0 유지 //
  const [usagePercent, setUsagePercent] = useState(0);

  // 세션 관리 //
  const [sessionId, setSessionId] = useState(() => `session_${uuidv4()}`);
  const [sessionTitle, setSessionTitle] = useState(null);
  const [sessions, setSessions] = useState([]);

  // 채팅 상태 //
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isResponding, setIsResponding] = useState(false);

  // 메시지별 사용자 평가 상태 { [messageId]: { score, reason, comment } } — 세션 전환 시 초기화 //
  const [messageRatings, setMessageRatings] = useState({});

  // 프롬프트 (DB API 우선, 실패 시 상수 fallback) //
  const [prompts, setPrompts] = useState({
    basePrompt: '',
    categoryPrompt: '',
    displayGuide: '',
    intentionGuide: '',
    filterGuide: '',
    comparisonGuide: '',
    workflowGuide: '',
    callApiUniversalRules: '',
    toolGuideCallApi: '',
    toolGuideCallApiSms: '',
    toolGuideFilterEmpty: '',
    toolGuideAggregate: '',
    toolGuideDerive: '',
    toolGuideDefault: '',
    toolGuideSms: '',
    toolGuideSmsRetry: '',
    toolGuideMerge: '',
    toolGuideSwitchSlot: '',
    toolGuideGetGuideContent: '',
    toolGuideGetGuideSlugs: '',
    toolGuideGetGuideDirect: '',
    crossReferenceGuide: '',
  });

  // 세션 객체 (버퍼 + 도구 상태) //
  const sessionRef = useRef(new AgentSession());

  // API 인증 ref //
  const apiKeyTimedOutRef = useRef(false);
  const clientRef = useRef(null);

  // Agent Loop ref //
  const agentMessagesRef = useRef(null);
  const isCancelledRef = useRef(false);

  // 채팅 ref //
  const chatMessagesContainerRef = useRef(null);
  const chatMessagesRef = useRef([]);
  chatMessagesRef.current = chatMessages;

  // 메시지별 대화 저장 메타 { [messageId]: { conversationId, userInput, intention, elapsedMs } } //
  const conversationMetaRef = useRef({});

  // SMS 추천 대기 상태 (recommend_sms → show_display 사이 임시 저장) //
  const pendingSmsRef = useRef(null);

  // 테스트용 안정 참조 (dev 환경에서만) //
  const testRunAgentRef = useRef(null);

  // ============================================
  // API Key 로드 //
  // ============================================
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (apikey) return;
      apiKeyTimedOutRef.current = true;
    }, API_KEY_TIMEOUT_MS);

    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2('ai_manage/permission', 'GET');
          if (TOOL_CALLING_LOG) console.log('[Agent] permission response:', responseData.status);

          if (responseData.status === 200 && responseData.data) {
            const data = responseData.data;

            // 백엔드가 잔액 소진 신호(tokenExhausted=true) 시 키 미발급 — 키 없이 소진 상태로 진입 //
            if (data.tokenExhausted === true) {
              if (apiKeyTimedOutRef.current) return;
              setStoreName(data.storeName || '');
              setUsagePercent(100);
              tokenExhaustedRef.current = true;
              setTokenExhausted(true);
              if (TOOL_CALLING_LOG) console.log('[Agent] ⛔ token exhausted — key not issued');
              return;
            }

            const decryptedKey = await decrypt(data.apiKey);
            if (apiKeyTimedOutRef.current) return;
            setStoreName(data.storeName || '');
            if (typeof data.usagePercent === 'number') setUsagePercent(data.usagePercent);   // 진입 시 게이지 초기값
            setApikey(decryptedKey.replace(/\s/g, ''));
            if (TOOL_CALLING_LOG) console.log('[Agent] ✅ Ready! store:', data.storeName);
          }
          else {
            if (TOOL_CALLING_LOG) console.log('[Agent] ❌ permission failed:', responseData.status);
          }
        }
        catch (error) {
          if (TOOL_CALLING_LOG) console.error('[Agent] ❌ permission error:', error);
          if (apiKeyTimedOutRef.current) return;
        }
        finally {
          clearTimeout(timeoutId);
        }
      }
    )();

    return () => { clearTimeout(timeoutId); };
  }, []);

  // ============================================
  // OpenAI 클라이언트 초기화 //
  // ============================================
  useEffect(() => {
    clientRef.current = apikey
      ? new OpenAI({ apiKey: apikey, dangerouslyAllowBrowser: true })
      : null;
  }, [apikey]);

  const client = clientRef.current;

  // ============================================
  // 토큰 소진 처리 — 받아온 API 키를 메모리에서 제거해 추가 LLM 호출 원천 차단 //
  // ============================================
  // 잔액(차감)은 백엔드가 권위. 본 함수는 회신된 잔액 0 이하 시에만 호출 — clientRef를 즉시 null로 만들어
  // 진행 중 getClient()까지 차단하고, setApikey('')로 state의 키도 비워 클라이언트 재생성을 막는다.
  function wipeApiKeyForExhaustion() {
    if (tokenExhaustedRef.current) return;

    tokenExhaustedRef.current = true;
    clientRef.current = null;   // 즉시 하드 차단 — 이후 getClient()는 null 반환
    setApikey('');              // 메모리에서 키 제거 + 클라이언트 재생성 차단
    setTokenExhausted(true);
  }

  // ============================================
  // 시임1 — LLM 호출 콜렉터 + 단일 통과 지점 //
  // ============================================
  // 모든 chat.completions.create는 loggedCompletion 경유 (토큰 전수 포착). reset은 턴 시작(runAgent)에서만 —
  // 턴 종료 후 SMS 비동기 호출도 현재 턴에 누적(V-2). 텔레메트리 실패는 동작 무영향.
  const llmCollectorRef = useRef(null);
  if (!llmCollectorRef.current) llmCollectorRef.current = createLlmCollector();

  // 라이브 5곳용 (aiFetch 재시도 경유 — 기존 동작 보존) //
  const loggedCompletion = useMemo(() => createLoggedCompletion({
    getClient: () => clientRef.current,
    collector: llmCollectorRef.current,
    fetcher: aiFetch,
  }), []);

  // SMS 2곳용 (기존 비-aiFetch 직접 호출 동작 보존) //
  const smsLoggedCompletion = useMemo(() => createLoggedCompletion({
    getClient: () => clientRef.current,
    collector: llmCollectorRef.current,
    fetcher: null,
  }), []);

  // ============================================
  // 프롬프트 로드 (DB API 우선, 실패 시 상수 fallback)
  // ============================================
  useEffect(() => {
    (
      async () => {
        try {
          const fetchModule = new FetchModule();
          const response = await fetchModule.getDataVersion2('ai_manage/prompts', 'GET');
          if (response.status === 200 && Array.isArray(response.data)) {
            // promptKey(=aiPrompts export 상수명) 기준 맵. 레거시 행(promptKey=null)은 제외 //
            const promptMap = Object.fromEntries(
              response.data.filter(p => p.promptKey).map(p => [p.promptKey, p])
            );
            setPromptMap(promptMap);
            // DB 우선 + 상수 fallback (이관 byte-faithful → DB 미적재 키는 상수로 동일 거동) //
            setPrompts({
              basePrompt: promptMap.BASE_PROMPT?.text ?? '',
              categoryPrompt: promptMap.CATEGORY_PROMPT?.text ?? '',
              displayGuide: promptMap.DISPLAY_GUIDE?.text ?? '',
              intentionGuide: promptMap.INTENTION_GUIDE?.text ?? '',
              filterGuide: promptMap.FILTER_GUIDE?.text ?? '',
              comparisonGuide: promptMap.COMPARISON_GUIDE?.text ?? '',
              workflowGuide: promptMap.WORKFLOW_GUIDE?.text ?? '',
              callApiUniversalRules: promptMap.CALL_API_UNIVERSAL_RULES?.text ?? '',
              toolGuideCallApi: promptMap.DEFAULT_CALL_API_GUIDE?.text ?? '',
              toolGuideCallApiSms: promptMap.DEFAULT_CALL_API_GUIDE_SMS?.text ?? '',
              toolGuideFilterEmpty: promptMap.TOOL_GUIDE_FILTER_EMPTY?.text ?? '',
              toolGuideAggregate: promptMap.TOOL_GUIDE_AGGREGATE?.text ?? '',
              toolGuideDerive: promptMap.TOOL_GUIDE_DERIVE?.text ?? '',
              toolGuideDefault: promptMap.TOOL_GUIDE_DEFAULT?.text ?? '',
              toolGuideSms: promptMap.TOOL_GUIDE_SMS?.text ?? '',
              toolGuideSmsRetry: promptMap.TOOL_GUIDE_SMS_RETRY?.text ?? '',
              toolGuideMerge: promptMap.TOOL_GUIDE_MERGE?.text ?? '',
              toolGuideSwitchSlot: promptMap.TOOL_GUIDE_SWITCH_SLOT?.text ?? '',
              toolGuideGetGuideContent: promptMap.TOOL_GUIDE_GET_GUIDE_CONTENT?.text ?? '',
              toolGuideGetGuideSlugs: promptMap.TOOL_GUIDE_GET_GUIDE_SLUGS?.text ?? '',
              toolGuideGetGuideDirect: promptMap.TOOL_GUIDE_GET_GUIDE_DIRECT?.text ?? '',
            });
            if (TOOL_CALLING_LOG) console.log(`[Agent] ✅ Prompts loaded (DB ${Object.keys(promptMap).length}키 우선, 상수 fallback)`);
          }
        }
        catch (e) {
          if (TOOL_CALLING_LOG) console.log('[Agent] ⚠️ Prompts fallback to constants');
        }
      }
    )();
  }, []);

  // ============================================
  // SMS 핸들러 초기화 //
  // ============================================
  // resendTurnWithSms는 렌더마다 새 함수 — ref 경유로 항상 최신 클로저 호출 (useMemo 재생성 불필요) //
  const resendTurnWithSmsRef = useRef(null);
  resendTurnWithSmsRef.current = resendTurnWithSms;

  const {
    updateConversationSms,
    generateSmsTemplates,
    handleIndividualSmsGenerate,
    handleSmsSend,
    handleIndividualSmsSend,
  } = useMemo(() =>
    createSmsHandlers({
      clientRef,
      storeName,
      sessionId,
      chatMessagesRef,
      conversationMetaRef,
      updateAiMessage,
      loggedCompletion: smsLoggedCompletion,
      resendTurnWithSms: (...args) => resendTurnWithSmsRef.current?.(...args),
    }),
    [storeName, sessionId, smsLoggedCompletion]
  );

  // ============================================
  // 텔레메트리 턴 적재 (P2c — 단일 POST ai-log/turn, 구 ai-log/conversation 대체) //
  // ============================================
  // SMS 비동기 갱신 재전송용 — conversationId별 최근 턴 payload 보관 (cap 초과 시 oldest evict) //
  const turnPayloadRef = useRef(new Map());
  const TURN_PAYLOAD_CACHE_MAX = 5;

  // 영속 POST 재시도 — V-4: 대화기록 유일 영속 경로라 transient 실패 2회 백오프 재시도 //
  async function postWithRetry(endpoint, payload, label) {
    const fetchModule = new FetchModule();
    const delays = [0, 1000, 3000];

    for (let attempt = 0; attempt < delays.length; attempt++) {
      try {
        if (delays[attempt] > 0) await new Promise(resolve => setTimeout(resolve, delays[attempt]));

        const res = await fetchModule.postDataVersion2(endpoint, 'POST', payload);

        if (res?.status === 200) return res;   // 응답 본문(잔액 등) 전달 — 호출처는 truthy 여부로도 사용 가능
        if (TOOL_CALLING_LOG) console.warn(`[Telemetry] ${label} 저장 실패 (status=${res?.status}, attempt=${attempt + 1})`);
      }
      catch (error) {
        if (TOOL_CALLING_LOG) console.warn(`[Telemetry] ${label} 저장 오류 (attempt=${attempt + 1}):`, error?.message);
      }
    }

    return null;
  }

  // 콜렉터 llmCalls → /turn DTO 매핑 (raw는 gzip+base64 — 백엔드 무해석 저장) //
  async function buildLlmCallDtos(calls) {
    return Promise.all((calls || []).map(async (c) => ({
      respId: c.respId ?? null,
      callType: c.callType ?? null,
      iteration: c.iteration ?? null,
      promptId: null,
      // 구 ai_response 테이블 model_used 레거시 NOT NULL — 'unknown' 폴백 (e2e 실측 2026-06-11: null 시 500) //
      modelUsed: c.modelUsed ?? 'unknown',
      maxOutputTokens: c.maxOutputTokens ?? null,
      inputTokens: c.inputTokens ?? null,
      outputTokens: c.outputTokens ?? null,
      reasoningTokens: c.reasoningTokens ?? null,
      cachedTokens: c.cachedTokens ?? null,
      totalTokens: c.totalTokens ?? null,
      createdAt: c.createdAt ?? null,
      completedAt: null,
      elapsedMs: c.elapsedMs ?? null,
      error: c.error === true,
      errorDetails: c.errorDetails ?? null,
      rawRequest: c.rawRequest ? ((await gzipBase64(c.rawRequest)) ?? c.rawRequest) : null,
      rawResponse: c.rawResponse ? ((await gzipBase64(c.rawResponse)) ?? c.rawResponse) : null,
      apiEndpoint: null,
      apiStatusCode: null,
      modelParams: c.modelParams ?? null,
      generatedContent: c.generatedContent ?? null,
    })));
  }

  // 턴 텔레메트리 적재 — turn-fact(gzip) + llmCalls 분해 + renderJson(화면 재현) 단일 POST //
  async function sendTurnTelemetry({ conversationId, userInput, finalText, intention, elapsedMs, renderJson, agentResult }) {
    try {
      const collector = llmCollectorRef.current;
      const llmCalls = collector.getCalls();
      const backendCalls = collector.getBackendCalls();
      const log = agentResult?.log || [];

      const turnFact = buildTurnFact({ input: userInput }, {
        log,
        finalText: finalText || '',
        intention: intention || null,
        displayResult: agentResult?.displayResult || null,
        extraDisplays: agentResult?.extraDisplays || null,
        smsRecommendation: agentResult?.smsRecommendation || null,
        bufferSnapshot: agentResult?.bufferSnapshot || null,
        // [OB-1 가드 트레이스] 가드 발동 전수 + 거짓 no-data 리스크 — replay 판독용 (v1.2)
        guardFirings: agentResult?.guardFirings || null,
        hadFalseNoDataRisk: agentResult?.hadFalseNoDataRisk ?? null,
        llmCalls,
        backendCalls,
        elapsedMs,
      });
      turnFact.promptVersions = collector.getMeta()?.promptVersions || null;

      const traceJsonPlain = JSON.stringify(turnFact);
      const payload = {
        conversationId,
        sessionId,
        requestMessage: userInput,
        finalText: finalText || '',
        renderJson: renderJson || null,
        intention: intention || null,
        promptId: null,
        turnElapsedMs: elapsedMs ?? null,
        llmMs: llmCalls.reduce((sum, c) => sum + (c.elapsedMs || 0), 0),
        backendMs: backendCalls.reduce((sum, c) => sum + (c.latencyMs || 0), 0),
        backendCallCount: backendCalls.length,
        toolCount: log.filter(l => l?.type === 'tool_call').length,
        hadError: log.some(l => l?.type === 'error') || llmCalls.some(c => c.error === true),
        // 문자발송 식별용 plain 플래그 — call_type(sms_*) 타이밍/유실과 무관하게 백엔드가 문자발송 분류 가능 (traceJson gzip 내부에만 있던 정보를 평문으로 노출) //
        hadSmsRecommendation: !!agentResult?.smsRecommendation,
        schemaVersion: TRACE_SCHEMA_VERSION,
        traceJson: (await gzipBase64(traceJsonPlain)) ?? traceJsonPlain,
        llmCalls: await buildLlmCallDtos(llmCalls),
      };

      // SMS 비동기 갱신 재전송용 캐시 (turnFact 원본 + payload — renderJson/llmCalls만 갱신해 재조립) //
      turnPayloadRef.current.set(conversationId, { payload, turnFact, collectorConversationId: collector.getMeta()?.conversationId || null });
      if (turnPayloadRef.current.size > TURN_PAYLOAD_CACHE_MAX) {
        const oldestKey = turnPayloadRef.current.keys().next().value;
        turnPayloadRef.current.delete(oldestKey);
      }

      const res = await postWithRetry('ai-log/turn', payload, 'turn');

      // 차감 후 백엔드가 내려주는 사용%로 게이지 갱신 + 소진(tokenExhausted) 시 API 키 폐기로 추가 호출 차단.
      // 필드 부재(백엔드 미배포) 시 무동작 — 기존 동작 그대로 보존 //
      const turnData = res?.data;
      if (typeof turnData?.usagePercent === 'number') setUsagePercent(turnData.usagePercent);
      if (turnData?.tokenExhausted === true) wipeApiKeyForExhaustion();
    }
    catch (error) {
      if (TOOL_CALLING_LOG) console.warn('[Telemetry] turn 조립 실패:', error?.message);
    }
  }

  // SMS 비동기 완료 후 턴 재전송 — idempotent upsert 활용 (V-2). renderJson + sms llmCalls 갱신 //
  async function resendTurnWithSms(conversationId, renderJson, smsRecommendation) {
    try {
      const cached = turnPayloadRef.current.get(conversationId);
      if (!cached) return;

      const collector = llmCollectorRef.current;
      // 콜렉터가 아직 같은 턴이면 SMS 호출 포함 최신 전수, 다음 턴으로 넘어갔으면 1차 POST 시점 보존본 사용 //
      const isSameTurn = collector.getMeta()?.conversationId === cached.collectorConversationId && cached.collectorConversationId !== null;
      const sameTurnCalls = isSameTurn ? collector.getCalls() : null;

      // 백엔드는 재전송 시 ai_response를 전량 DELETE 후 llmCalls로 재삽입 → sms_* 빠진 세트로 덮으면 문자발송이 '에이전트'로 오분류.
      // 다음 턴으로 넘어간 경우(1차 POST 스냅샷 사용)엔 conversationId별 보존 SMS 호출을 합류시켜 sms_* 행 유실 차단 //
      const isSmsCall = (c) => c?.callType === 'sms_bulk' || c?.callType === 'sms_individual';
      const callKey = (c) => c?.respId || `${c?.callType}#${c?.iteration ?? ''}#${c?.createdAt ?? ''}`;

      let llmCallDtos = null;
      let llmMsTotal = null;
      let hadErrorMerged = null;
      let missingSms = [];

      if (sameTurnCalls) {
        llmCallDtos = await buildLlmCallDtos(sameTurnCalls);
        llmMsTotal = sameTurnCalls.reduce((sum, c) => sum + (c.elapsedMs || 0), 0);
        hadErrorMerged = cached.payload.hadError || sameTurnCalls.some(c => c.error === true);
      }
      else {
        const base = cached.payload.llmCalls || [];
        const preservedSms = collector.getSmsCallsByConv?.(conversationId) || [];
        const seen = new Set(base.filter(isSmsCall).map(callKey));

        missingSms = preservedSms.filter(c => !seen.has(callKey(c)));

        if (missingSms.length > 0) {
          const missingDtos = await buildLlmCallDtos(missingSms);
          llmCallDtos = [...base, ...missingDtos];
          llmMsTotal = (cached.payload.llmMs || 0) + missingSms.reduce((sum, c) => sum + (c.elapsedMs || 0), 0);
          hadErrorMerged = cached.payload.hadError || missingSms.some(c => c.error === true);
        }
      }

      const turnFact = { ...cached.turnFact, smsRecommendation: smsRecommendation || cached.turnFact.smsRecommendation };

      if (sameTurnCalls) turnFact.llmCalls = leanLlmCalls(sameTurnCalls);
      else if (missingSms.length > 0) turnFact.llmCalls = [...(cached.turnFact.llmCalls || []), ...leanLlmCalls(missingSms)];

      const traceJsonPlain = JSON.stringify(turnFact);
      const payload = {
        ...cached.payload,
        renderJson: renderJson || cached.payload.renderJson,
        traceJson: (await gzipBase64(traceJsonPlain)) ?? traceJsonPlain,
        // 재전송은 항상 문자발송 컨텍스트 — plain 플래그 보존 (1차 POST 누락분도 여기서 복구) //
        hadSmsRecommendation: cached.payload.hadSmsRecommendation || !!(smsRecommendation || cached.turnFact.smsRecommendation),
        ...(llmCallDtos ? {
          llmCalls: llmCallDtos,
          llmMs: llmMsTotal,
          hadError: hadErrorMerged,
        } : {}),
      };

      turnPayloadRef.current.set(conversationId, { ...cached, payload, turnFact });

      const res = await postWithRetry('ai-log/turn', payload, 'turn(sms)');

      // SMS 비동기 추가분 차감까지 게이지에 반영 (재전송 delta 차감 가정) //
      const smsData = res?.data;
      if (typeof smsData?.usagePercent === 'number') setUsagePercent(smsData.usagePercent);
      if (smsData?.tokenExhausted === true) wipeApiKeyForExhaustion();
    }
    catch (error) {
      if (TOOL_CALLING_LOG) console.warn('[Telemetry] SMS turn 재전송 실패:', error?.message);
    }
  }

  // AI 응답 사용자평가 — POST ai-log/rating (conversationId 키 upsert, 재클릭 갱신) //
  const handleRateMessage = async (messageId, score, reason = null, comment = null) => {
    const meta = conversationMetaRef.current[messageId];
    if (!meta?.conversationId) return;

    const prevRating = messageRatings[messageId] || null;

    setMessageRatings(prev => ({ ...prev, [messageId]: { score, reason, comment } }));

    const isSaved = await postWithRetry('ai-log/rating', {
      conversationId: meta.conversationId,
      sessionId,
      score,
      reason,
      comment,
    }, 'rating');

    // 저장 실패 시 조용히 이전 상태로 복귀 (채팅 동작 무영향) //
    if (!isSaved) setMessageRatings(prev => ({ ...prev, [messageId]: prevRating }));
  };

  // 평가 가능 여부 — 저장된 turn(conversationId 보유)만 평가 UI 노출 //
  const isMessageRatable = (messageId) => !!conversationMetaRef.current[messageId]?.conversationId;

  // ============================================
  // 채팅 스크롤 처리 //
  // ============================================
  // 사용자 메시지 전송 시 해당 메시지를 뷰포트 상단으로 스크롤
  const scrollTargetIdRef = useRef(null);
  const activeAiMsgIdRef = useRef(null);
  const initialSpacerHeightRef = useRef(0);

  function resetSpacer() {
    activeAiMsgIdRef.current = null;
    initialSpacerHeightRef.current = 0;
    const container = chatMessagesContainerRef.current;
    if (container) container.style.setProperty('--after-spacer-height', '0px');
  }

  useEffect(() => {
    // 1. 사용자 메시지 전송 직후 → 빈공간 생성 + 상단 스크롤
    const targetId = scrollTargetIdRef.current;
    if (targetId) {
      scrollTargetIdRef.current = null;
      const container = chatMessagesContainerRef.current;
      if (container) {
        const spacer = container.clientHeight;
        initialSpacerHeightRef.current = spacer;
        container.style.setProperty('--after-spacer-height', `${spacer}px`);
        requestAnimationFrame(() => {
          const target = container.querySelector(`[data-message-id="${targetId}"]`);
          if (target) {
            target.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }
        });
      }
      return;
    }

    // 2. AI 응답 스트리밍 중 → 응답 높이만큼 빈공간 차감
    if (activeAiMsgIdRef.current && initialSpacerHeightRef.current > 0) {
      const container = chatMessagesContainerRef.current;
      if (container) {
        const aiMsgEl = container.querySelector(`[data-message-id="${activeAiMsgIdRef.current}"]`);
        const aiHeight = aiMsgEl ? aiMsgEl.offsetHeight : 0;
        const spacerHeight = Math.max(0, initialSpacerHeightRef.current - aiHeight);
        container.style.setProperty('--after-spacer-height', `${spacerHeight}px`);
      }
    }
  }, [chatMessages]);

  // 3. 응답 종료 시 → 빈공간 제거
  useEffect(() => {
    if (!isResponding) resetSpacer();
  }, [isResponding]);

  // 4. 화면 리사이즈 시 → 빈공간 제거
  useEffect(() => {
    function onResize() { resetSpacer(); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 5. 언마운트 cleanup — 진행 중 스트리밍 중단 + 세션 초기화
  useEffect(() => {
    return () => {
      isCancelledRef.current = true;  // 진행 중인 스트리밍 중단
      sessionRef.current.destroy();   // 버퍼 + intentionCache 초기화
    };
  }, []);

  // ============================================
  // 세션 제목 생성 //
  // ============================================
  async function generateTitle(userInput) {
    if (!client) return userInput.slice(0, 20);
    try {
      const response = await loggedCompletion('title', {
        model: TITLE_MODEL,
        max_completion_tokens: 50,
        temperature: 0.2,
        messages: [
          { role: "system", content: `${getTodaySystemMessage()} 사용자의 첫 메시지를 보고 10자 이내의 짧은 제목을 생성하세요. 제목만 출력하세요.` },
          { role: "user", content: userInput },
        ],
      });

      return response.choices[0]?.message?.content?.trim() || userInput.slice(0, 20);
    }
    catch (error) {
      // 토큰/에러는 시임1 콜렉터가 기록 — 구 ai-log 개별 POST 폐기 (P2c) //
      return userInput.slice(0, 20);
    }
  }

  // ============================================
  // 채팅 메시지 관리 (추가/업데이트)
  // ============================================
  // 사용자 메시지 추가 //
  function addUserMessage(text) {
    const id = `msg_${uuidv4()}`;
    setChatMessages(prev => [...prev, { id, text, isAi: false, timestamp: Date.now() }]);
    return id;
  }

  // AI 메시지 추가 //
  function addAiMessage(text = '') {
    const id = `msg_${uuidv4()}`;
    setChatMessages(prev => [...prev, { id, text, isAi: true, timestamp: Date.now() }]);
    return id;
  }

  // AI 메시지 업데이트 (flags 값이 함수이면 이전 값을 인자로 전달하여 부분 업데이트) //
  function updateAiMessage(id, text, flags = {}) {
    setChatMessages(prev => prev.map(msg => {
      if (msg.id !== id) return msg;

      const resolved = {};
      for (const [key, val] of Object.entries(flags)) {
        resolved[key] = typeof val === 'function' ? val(msg[key]) : val;
      }

      return {
        ...msg,
        ...(text !== undefined ? { text } : {}),
        ...resolved,
      };
    }));
  }

  // 메시지 전송 //
  const handleSend = async () => {
    if (TOOL_CALLING_LOG) console.log('[Agent] handleSend', { input: chatInput.trim().slice(0, 30), hasClient: !!client });

    // 토큰 소진 — 키가 폐기되어 호출 불가. 입력은 비우고 안내 메시지만 노출 //
    if (tokenExhaustedRef.current) {
      if (!chatInput.trim() || isResponding) return;

      setChatInput('');
      addAiMessage(TOKEN_EXHAUSTED_MESSAGE);
      return;
    }

    if (!chatInput.trim() || isResponding || !client) return;

    const userInput = chatInput.trim();
    setChatInput('');
    setIsResponding(true);
    isCancelledRef.current = false;

    const userMsgId = addUserMessage(userInput);
    scrollTargetIdRef.current = userMsgId;
    const aiMessageId = addAiMessage('');
    activeAiMsgIdRef.current = aiMessageId;

    // [로딩 즉시 표시] 첫 LLM 응답을 기다리지 않고 제출 직후 로딩 문구 노출 (지연 체감 제거) //
    showToolLoading(aiMessageId, userInput);

    // 비정상 입력 가드 — 깨진 자모/보이지 않는 문자만으로 구성된 입력 차단 //
    if (isGarbledInput(userInput)) {
      updateAiMessage(aiMessageId, GARBLED_INPUT_MESSAGE, { isToolLoading: false });
      setIsResponding(false);
      return;
    }

    try {
      const result = await runAgent(userInput, aiMessageId);
      const finalResponse = result.finalText || (isCancelledRef.current ? CANCELLED_RESPONSE : RESPONSE_FAILED_RETRY);

      saveSession(userInput, finalResponse, result.intention, result.elapsedMs, result.conversationId, result.displayResult, aiMessageId, result.smsRecommendation, result.salesViewData, result.revenueChartData, result.vizData, result.extraDisplays, result);
    }
    catch (error) {
      console.error('handleSend error:', error);
      const fallbackFlag = Math.floor(Math.random() * 3) + 1;
      const fallbackResponse = getFallbackMessage(fallbackFlag);
      updateAiMessage(aiMessageId, fallbackResponse, { isToolLoading: false });
      saveSession(userInput, fallbackResponse, null, Date.now() - Date.now(), `conv_${uuidv4()}`);
    }
    finally {
      setIsResponding(false);
    }
  };

  // 응답 중단 //
  const handleStop = () => {
    isCancelledRef.current = true;
    setIsResponding(false);
  };

  // 로딩 문구 — speech register 연동(반말/존댓말) + 다국어(ko/en/ja) 큐레이션 세트 //
  //   LLM 자유생성 X(드리프트 차단) → 입력 언어 감지 후 세트 순환. 한국어는 답변과 동일한 register 감지(buildSpeechRegisterInject) 재사용 → 반말이면 koInformal.
  //   [G1] 답변 말투(반말) ↔ 로딩 말투 싱크. 입력 언어는 detectResponseLanguage(userInput) (한글→ko / 가나→ja / 라틴→en / 기본 ko).
  const LOADING_MESSAGES = {
    ko: [
      "잠깐만요!",
      "확인해볼게요!",
      "찾아보고 있어요!",
      "거의 다 됐어요!",
      "준비하고 있어요!",
      "금방 알려드릴게요!",
      "조금만 기다려주세요!",
      "열심히 찾고 있어요!",
    ],
    koInformal: [
      "잠깐만!",
      "확인해볼게!",
      "찾아보고 있어!",
      "거의 다 됐어!",
      "준비하고 있어!",
      "금방 알려줄게!",
      "조금만 기다려!",
      "열심히 찾고 있어!",
    ],
    en: [
      "One moment, please!",
      "Let me check that!",
      "Looking it up!",
      "Almost there!",
      "Getting it ready!",
      "I'll let you know shortly!",
      "Just a moment, please!",
      "Searching now!",
    ],
    ja: [
      "少々お待ちください！",
      "確認いたしますね！",
      "お調べしています！",
      "もうすぐです！",
      "準備しております！",
      "すぐにお伝えしますね！",
      "少しお待ちください！",
      "お探ししています！",
    ],
  };
  const loadingIndexRef = useRef(0);

  // 로딩 메시지 표시 (shimmer 애니메이션) — userInput 언어 감지 → 해당 언어 존댓말 세트에서 순환 //
  function showToolLoading(id, userInput) {
    const lang = detectResponseLanguage(userInput || '');
    // [G1] 한국어 반말이면 로딩도 반말 세트 — 답변과 동일 register. [TONE PERSIST] 영속 pref 우선(존댓말 전환 시 로딩도 존댓말) → drift 0
    const _effRegLoad = (sessionRef.current?._speechRegisterPref) || detectSpeechRegister(userInput || '');
    const key = (lang === 'ko' && _effRegLoad === 'informal') ? 'koInformal' : lang;
    const set = LOADING_MESSAGES[key] || LOADING_MESSAGES.ko;
    const msg = set[loadingIndexRef.current % set.length];
    loadingIndexRef.current++;
    updateAiMessage(id, msg, { isToolLoading: true });
  }

  // 로딩 해제 → 일반 메시지로 전환 //
  function clearToolLoading(id) {
    setChatMessages(prev => prev.map(msg => msg.id === id ? { ...msg, isToolLoading: false } : msg));
  }

  // 타이핑 효과 (최종 응답을 한 글자씩) //
  async function typeMessage(id, text, chunkSize = 3, delayMs = 15) {
    clearToolLoading(id);
    let current = '';
    for (let i = 0; i < text.length; i += chunkSize) {
      if (isCancelledRef.current) break;
      current += text.slice(i, i + chunkSize);
      updateAiMessage(id, current);
      await new Promise(r => setTimeout(r, delayMs));
    }
    if (!isCancelledRef.current) {
      updateAiMessage(id, text);
    }
  }
  
  // ============================================
  // 에이전트 메인 루프 //
  // ============================================
  async function runAgent(userInput, aiMessageId) {
    const conversationId = `conv_${uuidv4()}`;
    if (TOOL_CALLING_LOG) console.log('[Agent] 🧠 New run: "', userInput, '" (ID:', aiMessageId, ')');
    const startTime = Date.now();

    // [시임1] 턴 시작 — LLM 콜렉터 초기화 (직전 턴의 SMS 후속 호출 누적은 여기서 마감) //
    llmCollectorRef.current.reset({ conversationId, sessionId, promptVersions: getPromptVersions() });
    // [시임2] 세션에 콜렉터 주입 — callApiInternal/get_intentions 백엔드 호출 계측 (idempotent, 세션 재생성에도 안전) //
    sessionRef.current.setTelemetryCollector?.(llmCollectorRef.current);

    // 새 run 시작
    sessionRef.current.resetComparisonFlag();
    // [L5-CROSS-004 fix 2026-05-25] turn 경계에서 1회 한정 가드 플래그 reset (state leak 차단)
    sessionRef.current.resetTurnFlags();
    sessionRef.current.clearStaleSlots();
    sessionRef.current.setUserInput(userInput);

    // 최초 요청이면 시스템 프롬프트로 초기화 (DB API 우선, 실패 시 상수 fallback)
    const storeContext = storeName ? `\nYour center name: "${storeName}". Only answer questions about this center. Reject requests about other centers.` : '';

    if (!agentMessagesRef.current) {
      agentMessagesRef.current = [
        { role: "system", content: `${getTodaySystemMessage()}\n${prompts.basePrompt}${storeContext}` },
        { role: "system", content: prompts.categoryPrompt },
        { role: "system", content: prompts.displayGuide },
      ];
    }

    // [STALE GUARD PRUNE] 직전 turn 실패 tool 결과(success:false) 중립화 — 가드 교정 지시문이 후속 turn 누출 차단 (dual sync with nodeAgent.mjs) //
    neutralizeStaleFailedToolResults(agentMessagesRef.current);

    // [E] 직전 turn buffer가 small entity list && 사용자 input에 entity 매칭 의도가 명확할 때만 emit
    try {
      const prevBuf = sessionRef.current.getBuffer?.();
      if (prevBuf?.items?.length > 0 && prevBuf.items.length <= 20) {
        const entityNames = new Set();
        const ENTITY_FIELDS = /^(name|title|label|teacherNames|lessonName|viewMembershipName|membershipName|memberShipName|coachName|productName|itemName)$/;
        for (const item of prevBuf.items) {
          for (const [k, v] of Object.entries(item)) {
            if (typeof v !== 'string' || v.length === 0 || v.length > 80) continue;
            if (v.startsWith('http') || /^[\d.-]+$/.test(v)) continue;
            if (ENTITY_FIELDS.test(k)) entityNames.add(v);
          }
        }
        if (entityNames.size > 0 && entityNames.size <= 30) {
          // 정밀화: 사용자 input에 entity 매칭 의도가 명확한 경우만 emit
          const hasEnglishProperNoun = /[A-Z][a-zA-Z]{1,}/.test(userInput);
          let hasEntityTokenMatch = false;
          for (const ename of entityNames) {
            if (userInput.includes(ename)) { hasEntityTokenMatch = true; break; }
            const firstTok = ename.split(/[,\s]+/)[0];
            if (firstTok.length >= 2 && userInput.includes(firstTok)) { hasEntityTokenMatch = true; break; }
          }

          if (hasEnglishProperNoun || hasEntityTokenMatch) {
            const list = [...entityNames].join(', ');
            agentMessagesRef.current.push({
              role: 'system',
              content: `[PREVIOUS BUFFER ENTITIES] Directly previous turn loaded ${prevBuf.items.length} item(s). Entities present: ${list}.\n\nThe user's input mentions a name. Verify before action:\n  • If the name IS in the list above → proceed (filter, cross-reference, etc).\n  • If the name is NOT in the list → respond in the user's language naturally indicating the name is not registered, then show the current list. For Korean: use the correct particle (받침 검사) and the format "[name][조사] 등록되어 있지 않습니다. 현재 등록 목록: ${list}" — NEVER output "은(는)" dual-disclosure. Ask user to clarify. DO NOT silently call_api another intention hoping the entity appears — that produces the WRONG target.`
            });

            // [L9b-CROSS-009 cycle 1] ENTITY NOT IN PREV BUFFER — deterministic context for outro auto-injection
            //   dual sync with nodeAgent.mjs (line ~7008)
            //   가드: all-uppercase acronym 제외 + input에 person/entity descriptor keyword 필수 (false positive 방지)
            let _missingNameToken = null;
            if (!hasEntityTokenMatch) {
              const _engMatch = userInput.match(/\b[A-Z][a-zA-Z]{2,}\b/);
              if (_engMatch) {
                const _candidate = _engMatch[0];
                const _isAllUpper = _candidate === _candidate.toUpperCase();
                const _hasPersonEntityKw = /코치|강사|선생|회원|회원권|수업|클래스|멤버|직원|스태프|teacher|coach|member|class|lesson|staff/i.test(userInput);
                if (!_isAllUpper && _hasPersonEntityKw) {
                  _missingNameToken = _candidate;
                }
                else if (TOOL_CALLING_LOG) {
                  console.log(`[Agent] 📝 ENTITY NOT FOUND CONTEXT skipped: candidate="${_candidate}", allUpper=${_isAllUpper}, hasPersonKw=${_hasPersonEntityKw}`);
                }
              }
            }
            if (_missingNameToken) {
              sessionRef.current._entityNotFoundContext = {
                missingName: _missingNameToken,
                entityNames: [...entityNames],
                prevBufItemCount: prevBuf.items.length,
              };
              if (TOOL_CALLING_LOG) console.log(`[Agent] 📝 ENTITY NOT FOUND CONTEXT set: missing="${_missingNameToken}", entities=[${[...entityNames].join(', ')}]`);
            }
          }
        }
      }
    } catch {}

    // [F] Ranking reference — "N위 회원 상세" 등 이전 display 순위 참조 시 top items 주입
    try {
      // display items 우선 (show_display 실행 순서 반영), 없으면 buffer items fallback
      const prevDisplayMsgR = chatMessagesRef.current.findLast(m => m.displayItems);
      const prevDisplayR = prevDisplayMsgR?.displayItems?.items;
      const prevBufR = sessionRef.current.getBuffer?.();
      const rankSourceItems = (prevDisplayR?.length > 0) ? prevDisplayR : prevBufR?.items;
      if (rankSourceItems?.length > 0 && /[1-9]\d*위/.test(userInput)) {
        const rankMatch = userInput.match(/(\d+)위/);
        const rankN = rankMatch ? parseInt(rankMatch[1], 10) : null;
        if (rankN && rankN <= rankSourceItems.length) {
          const ENTITY_FIELDS_R = /^(name|title|label|teacherNames|lessonName|viewMembershipName|membershipName|memberShipName|coachName|productName|itemName)$/;
          const prevColsR = Object.keys(rankSourceItems[0] || {});
          const identColR = prevColsR.find(c => {
            const sample = rankSourceItems[0]?.[c];
            return typeof sample === 'string' && !/^\d{4}-/.test(sample) && ENTITY_FIELDS_R.test(c);
          }) || prevColsR.find(c => {
            const sample = rankSourceItems[0]?.[c];
            return typeof sample === 'string' && !/^\d{4}-/.test(sample);
          });
          if (identColR) {
            const targetName = rankSourceItems[rankN - 1]?.[identColR] || '';
            const topPreview = rankSourceItems.slice(0, Math.min(5, rankSourceItems.length)).map((item, i) =>
              `${i + 1}위: ${item[identColR] || '(unknown)'}`
            ).join(', ');
            agentMessagesRef.current.push({
              role: 'system',
              content: `[RANKING REFERENCE DETECTED] User said "${userInput}" — "${rankN}위" refers to the ${rankN}th item in the previous display (ordered list). Previous display top items: ${topPreview}. The ${rankN}위 item is "${targetName}". If user asks for "상세/정보/알려줘", proceed with MEMBER_DETAIL: call_api(CHECK_MEMBER_SEARCH, params:{keyword:"${targetName}"}) → then use memberId for detail. Do NOT ask "which 1위?" — the ranking context is already established.`
            });
            if (TOOL_CALLING_LOG) console.log(`[Agent] 📋 Ranking reference injected: ${rankN}위 = "${targetName}"`);
          }
        }
      }
    } catch {}

    // [USER TERM AUTO-INJECT] 사용자 input에 사전 등록된 어휘 + 모디파이어 매칭 시 정의 + 처리 룰 자동 주입 //
    let _detectedTermInject = null;
    try {
      const detected = detectTermAndModifier(userInput);
      if (detected) {
        const def = lookupTermDefinition(detected.term, detected.modifier);
        if (def) {
          const lines = [];
          lines.push(`[USER TERM DETECTED — CRITICAL — DO NOT IGNORE] 사용자 input "${userInput}"에서 사전 등록 어휘 감지:`);
          lines.push(`  term: "${detected.term}", modifier: "${detected.modifier}"`);
          lines.push(`  의미: ${def.meaning}`);
          if (def.relatedIntention) {
            lines.push(`  사전 명시 인텐션: ${def.relatedIntention} (반드시 이 정확한 키 사용 — 다른 비슷한 키 추측 금지)`);
          }
          else if (def.axisHint) {
            lines.push(`  [MULTI-AXIS TERM] ${def.axisHint}`);
            lines.push(`  → Use CATEGORY_PROMPT [Cross-Category Priorities] routing rules to determine correct category and intention.`);
          }
          lines.push(`  metric 필드: ${def.metricField}, 평균 키: ${def.averageKey}`);
          if (def.operationalDef && def.relatedIntention) {
            // [MODIFIER DIRECTION] 낮은=평균 미만(lte)+오름차순, 높은=평균 이상(gte)+내림차순 (autoApplyTermDefinition와 동일 분기)
            const _filterType = detected.modifier === '낮은' ? 'numeric_lte' : 'numeric_gte';
            const _sortDir = detected.modifier === '낮은' ? 'asc' : 'desc';
            lines.push(`\n[모디파이어 처리 룰 — ${def.modifier} — MANDATORY]`);
            lines.push(`  실행 정의: ${def.operationalDef}`);
            lines.push(`  필수 도구 chain (생략 금지):`);
            lines.push(`    1) call_api({intention:"${def.relatedIntention}"})`);
            lines.push(`    2) filter({field:"${def.metricField}", condition:{type:"${_filterType}", value:<${def.averageKey} from summary>}})`);
            lines.push(`    3) sort({field:"${def.metricField}", direction:"${_sortDir}"})`);
            lines.push(`    4) show_display`);
            lines.push(`  비즈니스 맥락: ${def.businessContext}`);
          }
          if (def.crossReferenceRule) {
            lines.push(`\n[CROSS-REFERENCE 룰]`);
            lines.push(`  패턴: ${def.crossReferenceRule.pattern}`);
            lines.push(`  실행: ${def.crossReferenceRule.action}`);
          }
          _detectedTermInject = lines.join('\n');
          if (TOOL_CALLING_LOG) console.log(`[runAgent] USER TERM AUTO-INJECT: term="${detected.term}", modifier="${detected.modifier}"`);
        }
      }
    } catch (e) {
      if (TOOL_CALLING_LOG) console.log('[runAgent] term auto-inject failed:', e.message);
    }
    if (_detectedTermInject) {
      agentMessagesRef.current.push({ role: 'system', content: _detectedTermInject });
    }

    // [AUTO USER TERM] 사전 매칭 시 자동 도구 chain (LLM bypass)
    let _autoTermHandled = false;
    try {
      const termResult = await sessionRef.current.autoApplyTermDefinition(userInput);
      if (termResult?.success) {
        _autoTermHandled = true;
        agentMessagesRef.current.push({
          role: 'system',
          content: `[AUTO USER TERM 처리 완료] "${termResult.term} ${termResult.modifier}" 처리:\n  - intention: ${termResult.intention} 호출\n  - filter: ${termResult.field} ${termResult.modifier === '낮은' ? '<=' : '>='} ${termResult.threshold}(평균)\n  - sort: ${termResult.field} ${termResult.modifier === '낮은' ? 'asc' : 'desc'}\n  - 결과: ${termResult.before}건 → ${termResult.after}건 적재 완료\n\ncall_api/filter/sort 재호출 금지. 사용자 의도에 맞는 intro로 show_display + finish만 호출하세요.`
        });
      }
    } catch (e) {
      if (TOOL_CALLING_LOG) console.log('[runAgent] auto term failed:', e.message);
    }

    // [AUTO CROSS-REFERENCE] buffer 회원권 + demonstrative+보유 패턴 시 자동 cross-merge (LLM bypass)
    let _autoCrossRefHandled = false;
    try {
      const crossRef = await sessionRef.current.autoCrossReferenceIfMatched(userInput);
      if (crossRef?.success) {
        _autoCrossRefHandled = true;
        agentMessagesRef.current.push({
          role: 'system',
          content: `[AUTO CROSS-REFERENCE 완료] 직전 buffer의 ${crossRef.enums.length}개 ${crossRef.enumField} 값(${crossRef.enums.slice(0,5).join(', ')}${crossRef.enums.length>5?'...':''})을 회원의 ${crossRef.targetField}으로 매칭하여 회원 ${crossRef.memberCount}명 자동 적재 (slot="${crossRef.slot}", active). call_api/filter/sort 재호출 금지 — 이미 처리됨. 사용자 의도에 맞는 intro로 show_display + finish만 호출하세요.`
        });
      }
    } catch (e) {
      if (TOOL_CALLING_LOG) console.log('[runAgent] auto cross-ref failed:', e.message);
    }

    // [CONVERSION-FUNNEL-ORCHESTRATION] "X 쓴/산 사람 중 Y 구매" 전환 퍼널 — 코드 결정 계산 후 show_display 유도 (dual sync nodeAgent.mjs) //
    //   회원별 usages 타임라인(member/all/statistics)에서 기간 내 from 구매 모수 → 이후 첫 타상품 구매 = 전환. 표 rows/수치 전부 코드 산출 — LLM 날조 여지 0.
    let _conversionFunnelHandled = false;
    let _conversionCohort0 = null; // {kind, fromName, startDate, endDate} — 모수 0은 코드 결정 finish (tool loop skip)
    if (!_autoCrossRefHandled) {
      try {
        const _cf = await sessionRef.current.autoConversionFunnelIfMatched(userInput);
        if (_cf?.success) {
          _conversionFunnelHandled = true;
          agentMessagesRef.current.push({
            role: 'system',
            content: `[CONVERSION FUNNEL 처리 완료] 회원별 회원권 이력 타임라인으로 전환 퍼널을 코드가 결정 계산해 slot="_conversion_funnel"(active)에 적재했습니다 (rows: 전환 회원권|인원|비율|전환 소요일 ${_cf.rowCount}행). call_api/filter/compute/merge 재호출 금지 — 이미 처리됨. show_display를 columns 파라미터 없이 호출(전체 컬럼 자동)한 뒤 finish하세요. intro는 다음 문장을 그대로 사용: "${_cf.introSentence}" 수치를 새로 계산하거나 바꾸지 말고, "지원되지 않는다/조회할 수 없다" 류 문구는 절대 금지 — 계산은 이미 완료되었습니다.`,
          });
          if (TOOL_CALLING_LOG) console.log(`[CONVERSION-FUNNEL] from="${_cf.fromName}" ${_cf.startDate}~${_cf.endDate} cohort=${_cf.cohortCount} converted=${_cf.convertedCount}(${_cf.conversionRate}%)`);
        }
        else if (_cf && _cf.success === false && _cf.cohortCount === 0) {
          // 모수 0은 프롬프트 지시 아닌 코드 결정 finish — LLM이 지시 무시하고 무관 조회(매출 등)로 hijack하는 것 차단 //
          _conversionCohort0 = { kind: 'funnel', fromName: _cf.fromName, startDate: _cf.startDate, endDate: _cf.endDate };
          if (TOOL_CALLING_LOG) console.log(`[CONVERSION-FUNNEL] cohort 0 — from="${_cf.fromName}" ${_cf.startDate}~${_cf.endDate}`);
          _conversionFunnelHandled = true;
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[runAgent] conversion funnel failed:', e.message);
      }
    }


    // [CONVERSION-ROSTER v2] 명단 모드 — 코호트 명단("X 산 사람들 목록") + 추가구매 follow-up("이 사람들 중 추가 구매") (dual sync nodeAgent.mjs) //
    if (!_autoCrossRefHandled && !_conversionFunnelHandled) {
      try {
        const _cr = await sessionRef.current.autoConversionRosterIfMatched(userInput);
        if (_cr?.success) {
          _conversionFunnelHandled = true;
          const _crHead = _cr.mode === 'converted-roster'
            ? `[CONVERSION ROSTER 처리 완료] 직전 전환 퍼널 컨텍스트의 '추가 구매(전환)' 회원 명단 ${_cr.rowCount}명을 코드가 결정 산출해 slot="_conversion_roster"(active)에 적재했습니다 (이름|전화번호|기존 구매일|추가 구매 회원권|추가 구매일).`
            : `[CONVERSION ROSTER 처리 완료] ${_cr.startDate}~${_cr.endDate} '${_cr.fromName}' 구매 회원 명단 ${_cr.rowCount}명을 코드가 결정 산출해 slot="_conversion_roster"(active)에 적재했습니다 (이름|전화번호|구매일).`;
          agentMessagesRef.current.push({
            role: 'system',
            content: _cr.rowCount > 0
              ? `${_crHead} call_api/filter/compute/merge 재호출 금지 — 이미 처리됨. show_display를 columns 파라미터 없이 호출(전체 컬럼 자동)한 뒤 finish하세요. intro는 다음 문장을 그대로 사용: "${_cr.introSentence}" 수치·명단을 새로 만들거나 바꾸지 말고, "지원되지 않는다/조회할 수 없다" 류 문구는 절대 금지 — 계산은 이미 완료되었습니다.`
              : `${_crHead} 해당자가 0명이므로 표 없이 다음 문장을 그대로 안내하고 finish하세요: "${_cr.introSentence}" 날조 금지.`,
          });
          if (TOOL_CALLING_LOG) console.log(`[CONVERSION-ROSTER] mode=${_cr.mode} from="${_cr.fromName}" ${_cr.startDate}~${_cr.endDate} rows=${_cr.rowCount}`);
        }
        else if (_cr && _cr.success === false && _cr.cohortCount === 0) {
          // 모수 0은 프롬프트 지시 아닌 코드 결정 finish — LLM hijack 차단 (funnel 분기와 동형) //
          _conversionCohort0 = { kind: 'roster', fromName: _cr.fromName, startDate: _cr.startDate, endDate: _cr.endDate };
          if (TOOL_CALLING_LOG) console.log(`[CONVERSION-ROSTER] cohort 0 — from="${_cr.fromName}" ${_cr.startDate}~${_cr.endDate}`);
          _conversionFunnelHandled = true;
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[runAgent] conversion roster failed:', e.message);
      }
    }

    // [NEW-TO-PAID CONVERSION] bare "전환율/신규 전환율/정규 전환" (특정 상품 미지정) — 신규 유료 전환율 코드 결정 계산 후 show_display 유도 (dual sync nodeAgent.mjs) //
    //   회원별 최초 회원권(기간 내)=신규 모수, 그 중 유료 usage(isPaid/결제금액>0) 보유=전환. 수치·표 전부 코드 산출 — full 조기포기·mini 모수 혼합 모순 원천 차단.
    if (!_autoCrossRefHandled && !_conversionFunnelHandled) {
      try {
        const _np = await sessionRef.current.autoNewToPaidConversionIfMatched(userInput);
        if (_np?.success) {
          _conversionFunnelHandled = true;
          agentMessagesRef.current.push({
            role: 'system',
            content: `[NEW-TO-PAID CONVERSION 처리 완료] 회원별 회원권 이력 타임라인으로 신규 유료 전환율을 코드가 결정 계산해 slot="_conversion_funnel"(active)에 적재했습니다 (rows: 구분|인원|비율 ${_np.rowCount}행). call_api/filter/compute/merge 재호출 금지 — 이미 처리됨. show_display를 columns 파라미터 없이 호출(전체 컬럼 자동)한 뒤 finish하세요. intro는 다음 문장을 그대로 사용: "${_np.introSentence}" 수치를 새로 계산하거나 바꾸지 말고, "지원되지 않는다/정의되지 않았다/조회할 수 없다" 류 문구는 절대 금지 — 계산은 이미 완료되었습니다.`,
          });
          if (TOOL_CALLING_LOG) console.log(`[NEW-TO-PAID] ${_np.startDate}~${_np.endDate} new=${_np.newCount} converted=${_np.convertedCount}(${_np.conversionRate}%)`);
        }
        else if (_np && _np.success === false && _np.newCount === 0) {
          // 신규 진입 0명 — 코드 결정 finish (LLM hijack 차단, funnel 분기와 동형) //
          _conversionCohort0 = { kind: 'newconv', startDate: _np.startDate, endDate: _np.endDate };
          if (TOOL_CALLING_LOG) console.log(`[NEW-TO-PAID] new cohort 0 — ${_np.startDate}~${_np.endDate}`);
          _conversionFunnelHandled = true;
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[runAgent] new-to-paid conversion failed:', e.message);
      }
    }

    // [DEICTIC EMPTY BUFFER GUARD] deictic reference + 0건 buffer 감지 시 독립 조회 차단 (deterministic) //
    let _deicticEmptyBlocked = false;
    if (!_autoCrossRefHandled && !_autoTermHandled && !_conversionFunnelHandled) {
      try {
        if (sessionRef.current.detectDeicticEmptyBuffer(userInput)) {
          _deicticEmptyBlocked = true;
          if (TOOL_CALLING_LOG) console.log('[runAgent] DEICTIC EMPTY BUFFER GUARD triggered — deterministic finish');
        }
      }
      catch (e) {
        if (TOOL_CALLING_LOG) console.log('[runAgent] deictic empty buffer guard failed:', e.message);
      }
    }

    // [RE-FIRE CONTEXT] 직전 turn buffer가 있고 사용자가 재요청 신호 + 시간/조건 변경어 사용 시 강제 inject //
    // LLM이 "어떤 내역인지" 되묻기 방지 — 동일 intention 재호출 안내
    try {
      const slotSummaries = sessionRef.current?.getSlotSummaries?.() || {};
      const slotEntries = Object.entries(slotSummaries).filter(([_, s]) => s.intention && (s.count || 0) > 0);
      if (slotEntries.length > 0) {
        const RE_FIRE_SIGNAL = /(다시|재조회|재계산|지금부터|이번에는|이번엔|새로|산정해서|산정해|새로이|다시봐|다시 봐|돌려봐|돌려서)/;
        const CHANGE_PARAM = /(개월|주일|일\b|일전|일간|년|연도|기간|범위|최근|지금|이번|지난|작년|올해|당월|이전|어제|오늘|상위|이하|이상|초과|미만)/;
        if (RE_FIRE_SIGNAL.test(userInput) && CHANGE_PARAM.test(userInput)) {
          const [slotName, info] = slotEntries[slotEntries.length - 1];
          const dr = info.dateRange?.startDate && info.dateRange?.endDate
            ? ` (직전 dateRange: ${info.dateRange.startDate}~${info.dateRange.endDate})`
            : '';
          agentMessagesRef.current.push({
            role: 'system',
            content: `[RE-FIRE CONTEXT — 컨텍스트 명확함] 직전 turn에서 intention="${info.intention}" + slot="${slotName}" + count=${info.count}건 적재됨${dr}.\n현재 input "${userInput}"은 동일 데이터를 다른 조건(시간/범위/정렬 등)으로 재조회 의미입니다.\n  → 동일 intention="${info.intention}"으로 call_api 재호출 (변경된 args만 추가). 정렬만 변경이면 sort 도구만 호출.\n  → "어떤 내역인지/무엇을 다시 볼지" 되묻기 금지. 즉시 도구 호출.`
          });
          if (TOOL_CALLING_LOG) console.log(`[runAgent] RE-FIRE CONTEXT inject: slot="${slotName}", intention="${info.intention}"`);
        }
      }
    } catch (e) {
      if (TOOL_CALLING_LOG) console.log('[runAgent] re-fire context failed:', e.message);
    }

    // [SPEECH REGISTER INJECT + TONE PERSIST] 사용자 입력 말투 미러링 + 명시적 톤 요청 영속 (dual sync — nodeAgent.mjs). rid114/rid200.
    //   명시적 "존댓말로 해"/"반말로 해" 감지 → 세션 영속 pref 설정. effective = pref ‖ 입력 어미 감지. 다음 명시 요청까지 유지.
    {
      const _toneReq = detectToneChangeRequest(userInput);
      if (_toneReq && sessionRef.current) sessionRef.current._speechRegisterPref = _toneReq;
      const _inputReg = detectSpeechRegister(userInput);
      const _pref = sessionRef.current?._speechRegisterPref;
      const _effReg = _pref || _inputReg;
      const _speechInject = buildSpeechRegisterInjectForRegister(_effReg, _pref ? { conflictInputRegister: _inputReg } : null);
      if (_speechInject) {
        agentMessagesRef.current.push({ role: 'system', content: _speechInject });
        if (TOOL_CALLING_LOG) console.log(`[runAgent] SPEECH REGISTER inject: ${_effReg} mode active${sessionRef.current?._speechRegisterPref ? ' (persisted pref)' : ''}`);
      }
    }

    // [PERIOD-RESTRICTION FOLLOW-UP — EMPTY PREV BUFFER PRE-INJECT] L9a-MEMBERSHIP-002 cycle 1 (2026-05-30 dual sync with nodeAgent.mjs)
    //   본질: prev turn 결과 0건 + current turn TIME_KW only follow-up 시, 후행 text-only nudge로는 LLM이 finish(reverse question) 우회. 첫 iteration 전에 system message 주입해 LLM의 instinct를 미리 차단.
    //   패턴: buffer.intention 존재 + buffer.dateRange 존재 + buffer.items===0 + current input에 TIME_KW + (반응형 nudge와 동일 조건). 단일 case hardcode X — 모든 empty-result + period-narrow follow-up에 범용 적용.
    {
      const _PRE_TIME_KW = /오늘|어제|내일|지난주|이번주|다음주|지난달|이번달|다음달|\d+\s*월|\d+\s*일|\d{4}-\d{2}-\d{2}|최근|지난\s*\d+|\d+일\s*전|작년|올해|금주|금월/;
      const _PRE_SHORT_AGREE = /^(네|응|좋아|좋아요|부탁|부탁해|부탁해요|해줘|해주세요|고고|진행|그래|오케이|ok|okay)[\s!.…?\n]*$/i;
      const _preInputRaw = (userInput || '').trim();
      const _preSession = sessionRef.current;
      if (_preSession?.buffers && _PRE_TIME_KW.test(_preInputRaw) && !_PRE_SHORT_AGREE.test(_preInputRaw)) {
        const _emptyBufWithContext = [..._preSession.buffers.values()].find(b => !!b?.intention && !!b?.dateRange?.startDate && (b?.items?.length || 0) === 0);
        if (_emptyBufWithContext) {
          const _dr = _emptyBufWithContext.dateRange;
          const _intent = _emptyBufWithContext.intention;
          const _preInject = `[CONTEXT — PREVIOUS QUERY EMPTY + PERIOD FOLLOW-UP] Previous turn called intention="${_intent}" with dateRange=${_dr.startDate}~${_dr.endDate} and returned 0 items. User's current input "${_preInputRaw}" contains a time keyword (period-narrow follow-up). MANDATORY action this turn: call_api with EXACTLY intention="${_intent}" and updated startDate/endDate matching the user's requested period. KEEP the intention identical — do NOT switch to a different intention (e.g., do NOT switch to CHECK_MEMBER+ALIVE_LIST or CHECK_MEMBER+EXPIRED_LIST). Do NOT respond with a reverse question ("어떤 항목 보고 싶으신가요" / "무엇을 다시 볼지" etc) — the user's intent is clear: re-check the same list for the new period. If after the call the result is still 0 items, finish() with a concise message stating the new period also has 0 ${_intent} results, so subsequent turns can reference this context. EXCEPTION: if the current input explicitly names a DIFFERENT target than the previous query (새 대상 명사 — 예: 직전이 신규 회원인데 "만료 회원은?", 직전이 회원인데 "매출은?"), this is a NEW request, not a period-narrow follow-up — route normally via get_intentions and pick the intention matching the newly named target instead of forcing the same one.`;
          agentMessagesRef.current.push({ role: 'system', content: _preInject });
          if (TOOL_CALLING_LOG) console.log(`[runAgent] PERIOD-RESTRICTION PRE-INJECT (empty prev buf): intention=${_intent} period=${_dr.startDate}~${_dr.endDate}`);
        }
      }
    }

    // 세션 히스토리에 유저 메시지 추가
    agentMessagesRef.current.push({ role: "user", content: userInput });

    // 히스토리가 너무 길면 atomic unit 단위로 정리 (tool_calls/tool 쌍 보장) //
    const MAX_HISTORY = 30;

    let compacted = false;

    if (agentMessagesRef.current.length > MAX_HISTORY) {
      const before = agentMessagesRef.current.length;
      agentMessagesRef.current = compactMessages(agentMessagesRef.current, MAX_HISTORY);
      compacted = true;
      if (TOOL_CALLING_LOG) console.log(`[Agent] History compacted: ${before} → ${agentMessagesRef.current.length} messages`);
    }

    const messages = agentMessagesRef.current;

    // compaction으로 소실된 가이드 재주입 //
    if (compacted) {
      messages.push({
        role: "system",
        content: `[Guide Refresh — context was compacted]\n${prompts.workflowGuide}\n\n${prompts.filterGuide}\n\n${prompts.callApiUniversalRules}`,
      });
      if (TOOL_CALLING_LOG) console.log('[Agent] 📋 Post-compaction guide re-injected');
    }

    // 단계별 프롬프트 주입 추적
    let intentionGuideInjected = compacted;
    const _injectedCatGuides = new Set();  // [CATEGORY-SPLIT] BY_CATEGORY 라우팅 카테고리별 1회 주입 추적 (dual sync nodeAgent)
    let universalRulesInjected = compacted;  // [GUIDE DEDUP D2] universalRules turn당 1회 — compaction Guide Refresh에 이미 포함되면 skip (dual sync nodeAgent)
    const _injectedCallApiGuides = new Set();  // [GUIDE DEDUP D2] call_api 인텐션 가이드 "인텐션|변형" 키별 1회 주입 추적 (dual sync nodeAgent)

    // [LAZY TOOL-SET D1] viz 11종/recommend_sms 조건부 게이트 래치 — 신호 없으면 tools 페이로드 제외 (토큰 다이어트, dual sync with nodeAgent.mjs) //
    //   turn 시작 신호: userInput 광역 regex(aiUtils 단일소스) + 직전 turn viz 렌더(chart follow-up 보호) + SMS 진행 상태/직전 SMS 추천(재생성 보호).
    //   mid-turn 신호(래치 ON만 가능, OFF 불가): call_api defaultDisplay/visualizationOnly 버퍼, SMS nudge·rid192 가드 발동.
    let vizToolsNeeded = detectVizToolNeed(userInput) || sessionRef.current?._lastTurnUsedViz === true;
    let smsToolNeeded = detectSmsToolNeed(userInput) || !!pendingSmsRef.current || !!sessionRef.current?._lastSmsRecommendation;

    const log = [];
    let iteration = 0;
    let finalText = '';
    let lastIntention = null;
    let lastDisplayResult = null;
    let lastExtraDisplays = [];   // [MULTI-AXIS DISPLAY] primary 외 추가 축 표 (요일별 AND 시간대별 등)
    const displaysThisTurn = [];  // [MULTI-AXIS DISPLAY] {axisKey, display} 누적 — 같은 축=교체, 다른 축=추가
    const groupedSlotsThisTurn = new Set();  // [MULTI-AXIS AUTO-RENDER] 본 turn call_api로 로드된 grouped(_groupBy) 축 슬롯
    const calledTools = new Set();
    let showDisplayResult = null; // show_display 도구 호출 결과
    let sqlDataReady = false;     // SQL intersect 성공 → show_display/finish만 허용
    let salesViewResult = null;   // show_sales_view 도구 호출 결과
    let revenueChartResult = null; // show_revenue_chart 도구 호출 결과
    let vizResult = null;         // 출석/랭킹/수업/연령(engagement)/LTV/회원상태 차트 결과 — 단일 vizData dispatch
    let _sqlPeriodRange = null;   // SQL이 파싱한 기간 {start,end} — PERIOD-SALES-ORCHESTRATION 다년도 범위용 (외부 스코프 hoist)
    // [VIZ PLACEHOLDER RESOLVE] 차트/캘린더/viz 렌더 텍스트의 {{...}} placeholder를 active/summary 버퍼 기준 결정적 치환.
    //   show_display finish 경로(substituteOutroPlaceholders)와 동일 정책을 viz 렌더 분기(content 있음/없음/finish fallback)에도 적용.
    //   placeholder 부재 시 no-op, 버퍼 부재/실패 시 원본 보존(안전 fallback). 키 하드코딩 없음 — 범용.
    const _resolveVizText = (raw) => {
      if (!raw || !/\{\{[^}]+\}\}/.test(raw)) return raw;
      const _buf = sessionRef.current?.getActiveBuf?.()
        || [...(sessionRef.current?.buffers?.values() || [])].find(b => b.summary || (b.items?.length || 0) >= 1);
      if (!_buf) return raw;
      try {
        const _r = substituteOutroPlaceholders(raw, _buf, sessionRef.current?.getSlotSummaryMap?.());
        return (_r && typeof _r.text === 'string') ? _r.text : raw;
      } catch { return raw; }
    };
    let finishCalled = false;     // finish 도구 호출 플래그
    let finishResponse = '';      // finish 도구의 최종 응답
    let consecutiveTextOnly = 0;  // 연속 텍스트 전용 응답 카운터
    const MAX_TEXT_ONLY = 3;      // 안전장치: N회 연속이면 강제 종료

    // display 렌더링 공통 함수 — 정상 응답/빈 응답 모두 동일하게 처리
    const renderDisplay = async (displayResult, postText) => {
      // [REPROPOSAL SANITIZE — renderDisplay] postText(stop path / finish path) 재제안 strip //
      postText = stripPoliteReproposal(postText || '') || postText;

      // [ROOT_CAUSE_7_META_LEAK renderDisplay path] postText에 LLM harness format(`response to=functions.X{...}`) 또는 trailing JSON tail leak 시 strip
      //   본질: postText가 displayResult.outro에 merge되면 UI displayItems.outro 렌더링에 leak 노출 (finalText line 1013 sanitize 후이지만 UI는 outro 직접 사용)
      //   dual sync with nodeAgent.mjs (_stopPathContent + line 8342 finalText 양쪽)
      if (postText) {
        const _strippedHarness = stripAssistantHarnessLeak(postText);
        if (_strippedHarness !== postText) {
          if (TOOL_CALLING_LOG) console.log('[Agent] [ROOT_CAUSE_7_META_LEAK renderDisplay] postText harness leak stripped');
          postText = _strippedHarness;
        }
      }

      // [L9a-MEMBER-005 cycle 3 — count-contradiction safety net]
      // isIntroDuplicate missing 케이스 (verb 어미 변형, intro 단문 등)에서도 displayCount 모순 sentence 강제 strip
      if (postText && typeof displayResult?.totalCount === 'number') {
        const _stripped = stripCountContradictionSentences(postText, displayResult.totalCount);
        if (_stripped !== postText) {
          if (TOOL_CALLING_LOG) console.log(`[Agent] [Cat8-REPEAT count-contradiction] post-display overshoot stripped (displayCount=${displayResult.totalCount})`);
          postText = _stripped;
        }
      }

      // [REREG-SCOPE GUARD renderDisplay path] postText 재등록 서술 교정 — 배타 모수 혼합·stale rate (dual sync nodeAgent.mjs) //
      if (postText && sessionRef.current?._reRegMetaThisTurn) {
        const _rrCorrected = correctReRegScopeNarrative(postText, sessionRef.current._reRegMetaThisTurn);
        if (_rrCorrected !== postText) {
          if (TOOL_CALLING_LOG) console.log('[Agent] [REREG-SCOPE GUARD] renderDisplay postText corrected');
          postText = _rrCorrected;
        }
      }

      // [L3-COMPUTE-GROUP-002 renderDisplay path] grouped buffer entity classifier normalization (postText + displayResult.intro/outro)
      //   본질: compute(group) 적용 후 LLM postText/intro/outro에 "총 N명/건/회" 같은 entity classifier 들어가면 group context와 mismatch.
      //   detection: session.getBuffer()._aggregateMetaFields includes '_count'. dual sync with nodeAgent.mjs.
      {
        const _bufForGroupNorm = sessionRef.current?.getBuffer?.();
        if (_bufForGroupNorm) {
          if (postText) {
            const _normalized = normalizeGroupedUnits(postText, _bufForGroupNorm);
            if (_normalized !== postText) {
              if (TOOL_CALLING_LOG) console.log('[Agent] [L3-COMPUTE-GROUP-002 renderDisplay] grouped buffer unit normalized in postText');
              postText = _normalized;
            }
          }
          if (displayResult?.intro) {
            const _normalized = normalizeGroupedUnits(displayResult.intro, _bufForGroupNorm);
            if (_normalized !== displayResult.intro) displayResult.intro = _normalized;
          }
          if (displayResult?.outro) {
            const _normalized = normalizeGroupedUnits(displayResult.outro, _bufForGroupNorm);
            if (_normalized !== displayResult.outro) displayResult.outro = _normalized;
          }
        }
      }

      // [L9c-COMPARE-006 cycle 1 — period count mismatch safety net]
      //   본질: PERIOD COUNTS HINT 강제로 outro에 per-period count 강제 명시되지만, intro/postText의 single count phrase mismatch는 차단 없음
      //   해소: session._periodCountHintMeta.perPeriod (multi-period merge 시 set됨) ground truth로 mismatch sentence strip
      {
        const _sessionPCM = sessionRef.current;
        if (postText && _sessionPCM?._periodCountHintMeta?.perPeriod?.length >= 2) {
          const _strippedPCM = stripPeriodCountMismatchSentences(postText, _sessionPCM._periodCountHintMeta.perPeriod);
          if (_strippedPCM !== postText) {
            if (TOOL_CALLING_LOG) console.log(`[Agent] [L9c-COMPARE-006] period count mismatch sentence stripped from post-display text`);
            postText = _strippedPCM;
          }
        }
        // displayResult.intro/outro도 동일 sanitize
        if (_sessionPCM?._periodCountHintMeta?.perPeriod?.length >= 2) {
          if (displayResult?.intro) {
            const _strippedIntro = stripPeriodCountMismatchSentences(displayResult.intro, _sessionPCM._periodCountHintMeta.perPeriod);
            if (_strippedIntro !== displayResult.intro) {
              if (TOOL_CALLING_LOG) console.log(`[Agent] [L9c-COMPARE-006] period count mismatch sentence stripped from displayResult.intro`);
              displayResult.intro = _strippedIntro;
            }
          }
          if (displayResult?.outro) {
            const _strippedOutro = stripPeriodCountMismatchSentences(displayResult.outro, _sessionPCM._periodCountHintMeta.perPeriod);
            if (_strippedOutro !== displayResult.outro) {
              if (TOOL_CALLING_LOG) console.log(`[Agent] [L9c-COMPARE-006] period count mismatch sentence stripped from displayResult.outro`);
              displayResult.outro = _strippedOutro;
            }
          }
          // [L9c-COMPARE-006 F13 Issue C cycle 1] intro per-period count 누락 시 결정적 inject (dual sync with AgentSession.js + nodeAgent.mjs)
          if (displayResult) {
            const _introBeforeInject = displayResult.intro || '';
            const _injected = ensurePeriodCountsInIntro(_introBeforeInject, _sessionPCM._periodCountHintMeta.perPeriod);
            if (_injected !== _introBeforeInject) {
              if (TOOL_CALLING_LOG) console.log(`[Agent] [L9c-COMPARE-006 F13] per-period count sentence injected into displayResult.intro`);
              displayResult.intro = _injected;
            }
          }
        }

        // [PERIOD-IN-INTRO] RANKING 인텐션 intro에 산정 기간 결정적 보강 (multi-period 가드 밖, dual sync with nodeAgent.mjs)
        if (displayResult) {
          const _periodBuf = _sessionPCM?.getBuffer?.();
          const _isRankingIntent = INTENTION_DIMENSIONS[_periodBuf?.intention]?.aggregation === 'RANKING';

          if (_isRankingIntent && _periodBuf?.summary) {
            const _introBeforePeriod = displayResult.intro || '';
            const _injectedPeriod = ensurePeriodInIntro(_introBeforePeriod, _periodBuf.summary);

            if (_injectedPeriod !== _introBeforePeriod) {
              if (TOOL_CALLING_LOG) console.log(`[Agent] [PERIOD-IN-INTRO] data period injected into ranking displayResult.intro`);
              displayResult.intro = _injectedPeriod;
            }
          }
        }
      }

      // [L11-SMS-FULL-001 cycle 1] post-table outro adjacent-dedup
      //   본질: LLM이 finish() postText에 동일 문장 stochastic 중복 emit (예: "문자 문구를 준비했어요.\n문자 문구를 준비했어요.")
      //   기존 stripDuplicateSentences universal final-pass(line ~3149)는 table head 영역만 처리 → post-table outro zone 미처리
      //   해소: postText를 outro에 merge 전에 pre-dedup. 단일 fragment(table marker 없음)라 head=전체 → adjacent/global dup 정상 제거.
      //   범용: LLM 어느 phrasing이든 자기 반복 문장이면 적용. case ID 하드코딩 X. dual sync with nodeAgent.mjs.
      if (postText) {
        const _deduped = stripDuplicateSentences(postText);
        if (_deduped !== postText) {
          if (TOOL_CALLING_LOG) console.log('[Agent] [Cat8-REPEAT post-table] postText adjacent dup stripped before outro merge');
          postText = _deduped;
        }
      }

      // LLM 후속 텍스트가 있으면 outro에 합침 (intro 중복 감지 포함) //
      // [Cat8-REPEAT digit-agnostic] aiUtils.isIntroDuplicate canonical impl 사용 (LLM이 동일 문장에서 다른 숫자 사용해도 detect)
      const introText = displayResult.intro || '';
      const introDuplicate = isIntroDuplicate(introText, postText);

      if (postText && introDuplicate) {
        // [Cat8-REPEAT] intro와 겹치는 prefix 문장 전부 제거 (첫 문장만이 아니라 다중 문장 overlap) //
        const remaining = stripIntroOverlap(introText, postText);

        // [L23-FILTER-FOCUS-001] 기존 outro (autoGenerateDisplay capNotice 등) 보존 — finish stripped 결과로 overwrite 차단
        //   본질: cap 적용 시 outro에 "(전체 N건 중 상위 M건 표시)" 자동 부가되는데 intro-duplicate finish path에서 그게 손실되어 사용자 절단 안내 부재 (CALC_INCONSISTENT)
        // [L1-SCHEDULE-004 cycle 2] subset-aware merge — existingOutro와 remaining이 substring 관계면 superset만 채택 (자기 반복 차단)
        //   본질: LLM이 show_display outro에 짧은 안내 + finish() text에 같은 안내 + 추가 정보 emit → stripIntroOverlap 후 remaining이 existingOutro superset 되는 stochastic 패턴 (post-table outro 중복 잔존)
        //   해소: substring 검사 후 superset만 채택. dual sync with nodeAgent.mjs. case ID literal 없음, 한국어 keyword 의존 X.
        const existingOutro = displayResult.outro;
        if (remaining) {
          if (existingOutro && remaining.includes(existingOutro)) {
            displayResult.outro = remaining;
            if (TOOL_CALLING_LOG) console.log('[Agent] [Cat8-REPEAT subset intro-overlap path] outro replaced by remaining superset');
          }
          else if (existingOutro && existingOutro.includes(remaining)) {
            if (TOOL_CALLING_LOG) console.log('[Agent] [Cat8-REPEAT subset intro-overlap path] remaining is subset of outro — merge skipped');
          }
          else {
            displayResult.outro = existingOutro
              ? `${existingOutro}\n\n${remaining}`
              : remaining;
          }
        }
      }
      else if (postText
        && postText !== displayResult.intro
        && postText !== displayResult.outro
        && !displayResult.outro?.includes(postText)
        && !postText.includes(displayResult.outro)
      ) {
        displayResult.outro = displayResult.outro
          ? `${displayResult.outro}\n\n${postText}`
          : postText;
      }
      else if (postText && !displayResult.outro && !introDuplicate) {
        displayResult.outro = postText;
      }

      // [L1-SCHEDULE-004 cycle 1] merged outro post-dedup
      //   본질: displayResult.outro (LLM show_display outro arg) 와 postText (LLM finish() text)이 별도 fragment로
      //         들어오면 위 if/else 분기에서 단순 concat (`outro\n\n${postText}`)으로 합쳐짐. 양쪽 모두 동일/거의-동일
      //         문장(LLM stochastic 자기 반복)이면 merge 후 결과 outro 안에 동일 sentence 2회 잔존 → renderDisplay
      //         출력 finalText에서 post-table outro zone 중복. universal final-pass `stripDuplicateSentences`는
      //         table head 영역만 처리해서 post-table 잔존.
      //   해소: merged outro 자체를 stripDuplicateSentences로 정규화. outro는 단일 fragment (table marker 없음)
      //         → head=전체 → adjacent/global dup 모두 정상 제거. dual sync with nodeAgent.mjs.
      //   범용: case ID literal 없음, 한국어 keyword 의존 X. punctuation-insensitive + decimal 보호.
      if (displayResult.outro) {
        const _outroDeduped = stripDuplicateSentences(displayResult.outro);
        if (_outroDeduped !== displayResult.outro) {
          if (TOOL_CALLING_LOG) console.log('[Agent] [Cat8-REPEAT merged-outro] displayResult.outro dup stripped after intro/post merge');
          displayResult.outro = _outroDeduped;
        }
      }

      // [L9b-CROSS-009 cycle 2] ENTITY NOT FOUND — outro auto-append AFTER LLM stop-content merge (dual sync with nodeAgent.mjs)
      //   placement: after intro/outro/postText merged → already-acked detection sees the FULL rendered text
      if (sessionRef.current?._entityNotFoundContext) {
        const { missingName, entityNames: _entityListArrSD } = sessionRef.current._entityNotFoundContext;
        const _existingOutroSD = displayResult.outro || '';
        const _existingIntroSD = displayResult.intro || '';
        const _combinedTextSD = `${_existingIntroSD}\n${_existingOutroSD}`;
        const _alreadyAckedSD = _combinedTextSD.includes(missingName) &&
          /등록(?:되어\s*있지\s*않|되지\s*않|이?\s*없|이?\s*안\s*돼)|찾을\s*수\s*없|미등록|존재하지\s*않/.test(_combinedTextSD);
        if (!_alreadyAckedSD) {
          const _lastChSD = missingName.charCodeAt(missingName.length - 1);
          const _isKoreanSyllableSD = _lastChSD >= 0xAC00 && _lastChSD <= 0xD7A3;
          const _hasJongseongSD = _isKoreanSyllableSD && ((_lastChSD - 0xAC00) % 28 !== 0);
          const _particleSD = _hasJongseongSD ? '은' : '는';
          const _listShortSD = _entityListArrSD.slice(0, 5).join(', ') + (_entityListArrSD.length > 5 ? ' 외' : '');
          const _ackLineSD = `${missingName}${_particleSD} 등록되어 있지 않아요. 현재 등록 목록은 ${_listShortSD}이에요.`;
          displayResult.outro = _existingOutroSD
            ? `${_existingOutroSD}\n\n${_ackLineSD}`
            : _ackLineSD;
          if (TOOL_CALLING_LOG) console.log(`[Agent] 📝 ENTITY NOT FOUND renderDisplay outro append: "${missingName}"`);
        }
        else {
          if (TOOL_CALLING_LOG) console.log(`[Agent] 📝 ENTITY NOT FOUND already acked in intro/outro — skip auto-append`);
        }
        sessionRef.current._entityNotFoundContext = null;
      }

      // [PLACEHOLDER CHOKE-POINT — UI 경계] displayResult.intro/outro 최종 placeholder 강제 정리 //
      //   본질: 표가 있는 응답(renderDisplay 경로)의 outro는 finish(2576)/text-only(1863) substitute 경로를 안 타서
      //         LLM이 작성한 {{TABLE_COLS}}/{{SUMMARY:...}} 등이 UI(displayResult.outro 직접 렌더)에 raw 누수.
      //   choke-point: 경로 무관 UI 출력 직전 1회 substitute → value placeholder(SUMMARY/COUNT 등)는 값 치환,
      //         미해결 placeholder clause는 drop. resolvedColumns 제외 전달로 {{TABLE_COLS}}는 컬럼 나열 대신 drop
      //         (기존 _dropColumnEnumSentences 컬럼 enum 회피 철학과 일관). dual sync with nodeAgent.mjs.
      {
        const _bufCP = sessionRef.current?.getActiveBuf?.()
          || [...(sessionRef.current?.buffers?.values() || [])].find(b => b.summary || b.items?.length);
        if (_bufCP) {
          const _bufCPnoCols = { ..._bufCP, resolvedColumns: undefined, resolvedColumnLabels: undefined };
          if (displayResult.intro) displayResult.intro = substituteOutroPlaceholders(displayResult.intro, _bufCPnoCols, sessionRef.current?.getSlotSummaryMap?.()).text;
          if (displayResult.outro) displayResult.outro = substituteOutroPlaceholders(displayResult.outro, _bufCPnoCols, sessionRef.current?.getSlotSummaryMap?.()).text;
        }
      }

      // [MULTI-AXIS DISPLAY] 다축 질문(요일별 AND 시간대별 등)에서 한 turn에 표 2개 이상 표출.
      //   각 show_display의 buffer axis(_currentAxisKey) 또는 activeSlot으로 dedup: 같은 축=교체(중복방지), 다른 축=추가.
      //   primary(displayItems)는 첫 표(기존 단일표 경로 무변경), 추가 축만 extraDisplays로 가산(하위호환).
      const _axisKey = (() => {
        const _b = sessionRef.current?.getActiveBuf?.() || sessionRef.current?.getBuffer?.();
        return (_b && typeof _b._currentAxisKey === 'string' && _b._currentAxisKey)
          || sessionRef.current?.activeSlot
          || displayResult.intention
          || `__idx${displaysThisTurn.length}`;
      })();
      const _existIdx = displaysThisTurn.findIndex(d => d.axisKey === _axisKey);
      if (_existIdx >= 0) displaysThisTurn[_existIdx] = { axisKey: _axisKey, display: displayResult };
      else displaysThisTurn.push({ axisKey: _axisKey, display: displayResult });
      const _primaryDisplay = displaysThisTurn[0].display;
      const _extraDisplays = displaysThisTurn.slice(1).map(d => d.display);
      const _isPrimaryRender = (displaysThisTurn.length === 1);

      // primary(첫 표)일 때만 message 본문에 intro 타이핑 — 추가 표의 intro/outro는 각자 DisplayRenderer가 렌더
      if (_isPrimaryRender && displayResult.intro) {
        await typeMessage(aiMessageId, displayResult.intro);
      }

      const messageFlags = { displayItems: _primaryDisplay, isNewResponse: true };
      if (_extraDisplays.length > 0) messageFlags.extraDisplays = _extraDisplays;

      if (pendingSmsRef.current) {
        messageFlags.smsRecommendation = pendingSmsRef.current;
        pendingSmsRef.current = null;
        if (TOOL_CALLING_LOG) console.log('[Agent] 📱 SMS attached to display');
      }

      updateAiMessage(aiMessageId, _primaryDisplay.intro || '', messageFlags);

      lastDisplayResult = _primaryDisplay;
      lastExtraDisplays = _extraDisplays;
      // [MULTI-AXIS DISPLAY] finalText는 누적된 모든 표를 순서대로 렌더 (renderDisplay 호출마다 idempotent — 마지막 표만 저장되는 문제 방지)
      finalText = stripAssistantHarnessLeak(stripRawIsoDatetime(
        displaysThisTurn.map(d => renderDisplayAsText(d.display)).filter(Boolean).join('\n\n') || postText || ''
      ));
      // [L10-COL-LABEL-LEAK-OUTRO renderDisplay path] column label echo outro strip (dual sync with nodeAgent.mjs stop+finish paths) //
      //   본질: detail/profile 응답에서 표 1줄에 이미 노출된 column 라벨/값이 outro에 "- 이름: X\n- 성별: Y" 형태로
      //         재listing되는 LLM stochastic 패턴. 표 + outro listing 동시 노출 시 redundant echo.
      //   적용: finalText 합성 후 (표 + outro 합성된 상태). columnLabels + columns를 displayResult로부터 전달.
      if (finalText && displayResult) {
        const _stripped = stripColumnLabelEchoOutro(
          finalText,
          displayResult.columnLabels,
          displayResult.columns,
        );
        if (_stripped !== finalText) {
          if (TOOL_CALLING_LOG) console.log('[Agent] [L10-COL-LABEL-LEAK-OUTRO renderDisplay] column label echo outro stripped after table render');
          finalText = _stripped;
        }
      }

      // [rid55 FALSE_ACK] availableFields에 존재하나 표 컬럼 제외된 필드를 "표에 안 나와" 식 부재 진술 strip (dual sync with nodeAgent.mjs) //
      //   본질: profile(코치 5/6 URL 보유)을 표에서 제외(URL 누출 방지)한 뒤 "프로필 정보는 표에 안 나와" 거짓 부정 → 존재 데이터 부재 오인.
      if (finalText && displayResult) {
        const _absStripped = stripFalseAbsenceClaim(
          finalText,
          displayResult.availableFields,
          displayResult.columns,
          displayResult.columnLabels,
        );
        if (_absStripped !== finalText) {
          if (TOOL_CALLING_LOG) console.log('[Agent] [rid55 FALSE_ACK] false absence claim about available field stripped');
          finalText = _absStripped;
        }
      }

      // [DEMO-12-PEAK B] superlative/extreme 주장 검증 — outro가 정렬 top과 다른/없는 entity를 "가장 ~"로 지목 시 strip (dual sync with nodeAgent.mjs)
      if (finalText) {
        const _supStripped = stripContradictorySuperlativeClaims(finalText, sessionRef.current?.getBuffer?.());
        if (_supStripped !== finalText) {
          if (TOOL_CALLING_LOG) console.log('[Agent] [DEMO-12-PEAK B] contradictory superlative claim stripped');
          finalText = _supStripped;
        }
      }

      // [UNMET HISTORY-COND finish-path] 운영 store445 T3/T7: show_display intro가 disclosure로 교체된 turn에서
      //   LLM finish/outro의 날조 조건 주장("드랍인 쓴 사람 중 ... 명단이야") 재출현 strip (dual sync with nodeAgent.mjs)
      if (finalText && sessionRef.current?._unmetHistoryCondApplied) {
        const _uhStripped = stripUnmetHistoryClaim(finalText, sessionRef.current._unmetHistoryCondApplied);
        if (_uhStripped !== finalText) {
          if (TOOL_CALLING_LOG) console.log('[Agent] [UNMET HISTORY-COND finish-path] fabricated condition claim stripped');
          finalText = _uhStripped;
        }
      }

      // 후속 질문 대응용 컨텍스트 주입 (하이브리드: 데이터 내용 + 버퍼 상태)
      const session = sessionRef.current;
      const buffer = session.getBuffer();

      // [1] Display 내용 — 10건 이하: 전체 텍스트, 초과: 상위 5건 요약
      let displayContext;
      const FULL_TEXT_THRESHOLD = 10;

      if (displayResult.totalCount <= FULL_TEXT_THRESHOLD) {
        displayContext = resolvePrompt('DISPLAY_DATA_PROMPT', '').replace('{data}', renderDisplayAsText(displayResult) || '(empty)');
      }
      else {
        const columns = displayResult.columns || [];
        const labelMap = displayResult.columnLabels || {};
        const top5 = displayResult.items.slice(0, 5).map((item, i) => {
          const vals = columns.map(c => {
            const v = item[c];
            return v != null ? v : '';
          }).filter(Boolean).slice(0, 3);
          return `${i + 1}. ${vals.join(' | ')}`;
        });
        displayContext = resolvePrompt('DISPLAY_SUMMARY_PROMPT', '').replace('{{COUNT}}', displayResult.totalCount).replace('{{TOP5}}', top5.join('\n'));
      }

      // [1-2] Item Reference — 상위 항목 이름 매핑 (최대 3개)
      const refColumns = displayResult.columns || [];
      const refItems = (displayResult.items || []).slice(0, 3).map((item, i) => {
        const nameVal = item.name || item.memberShipName || item.itemName || item.lessonName || item[refColumns[0]];
        return nameVal ? `${i + 1}위="${nameVal}"` : null;
      }).filter(Boolean);
      if (refItems.length > 0) {
        displayContext += resolvePrompt('ITEM_REFERENCE_PROMPT', '').replace('{items}', refItems.join(', '));
      }

      // [2] Buffer 상태 — 항상
      const slots = session.getSlotNames();
      const fieldKeys = buffer.availableFields ? Object.keys(buffer.availableFields).slice(0, 10) : [];
      const _slotLabel = slots.includes('_merged') ? '_merged' : (buffer.intention || 'default');
      const _bufCategory = session.getCategoryForIntention(buffer.intention);
      const bufferParts = [
        _bufCategory ? `activeSlot: ${_slotLabel} [category: ${_bufCategory}]` : `activeSlot: ${_slotLabel}`,
        slots.length > 1 ? `slots: [${slots.join(', ')}]` : null,
        fieldKeys.length > 0 ? `fields: ${fieldKeys.join(', ')}` : null,
        buffer.appliedFilters?.length > 0 ? `filters: ${buffer.appliedFilters.map(f => f.field).join(', ')}` : null,
        buffer.appliedSort ? `sort: ${buffer.appliedSort.field} ${buffer.appliedSort.direction}` : null,
      ].filter(Boolean).join(' | ');

      const isMerged = slots.includes('_merged');
      const mergeWarning = isMerged ? resolvePrompt('MERGE_CAUTION_PROMPT', '') : '';
      const prevResponse = postText ? resolvePrompt('PREVIOUS_RESPONSE_PROMPT', '').replace('{response}', postText) : '';

      // enum 필드 고유값 추출 (gender, isActive 등) //
      const enumParts = [];

      if (buffer.items && buffer.items.length > 0) {
        const sample = buffer.items[0];

        for (const field of Object.keys(sample)) {
          if (field.startsWith('_') || typeof sample[field] === 'object' || Array.isArray(sample[field])) continue;

          const values = [...new Set(buffer.items.map(i => i[field]).filter(v => v != null))];

          if (values.length > 0 && values.length <= 10
            && values.every(v => typeof v === 'boolean' || (typeof v === 'string' && v.length <= 20))) {
            enumParts.push(`${field}=[${values.join(',')}]`);
          }
        }
      }

      const enumInfo = enumParts.length > 0 ? `\nenumValues: ${enumParts.join(', ')}` : '';

      // [TOKEN-ANCHORED] buffer.items에 _change_*/_changeRate_* 필드 존재 시 outro placeholder 가이드 inject + 사용 가능 base field 키 동적 주입
      const changeBaseFields = new Set();
      if (Array.isArray(buffer.items)) {
        for (const it of buffer.items) {
          if (!it || typeof it !== 'object') continue;
          for (const k of Object.keys(it)) {
            if (k.startsWith('_change_') && !k.startsWith('_changeRate_')) changeBaseFields.add(k.slice('_change_'.length));
          }
        }
      }
      const placeholderGuide = changeBaseFields.size > 0
        ? `\n${resolvePrompt('OUTRO_PLACEHOLDER_PROMPT', '').replace('{availableChangeFields}', [...changeBaseFields].join(', '))}`
        : '';

      // [B1 BACKEND SUMMARY] expose buf.summary scalars from all slots (active first) — blocks intro/outro numeric hallucination
      const _summarySlotPartsP = [];
      const _allSlotSummariesP = session.getSlotSummaries?.() || {};
      const _activeSlotNameP = slots.includes('_merged') ? '_merged' : (buffer.intention || 'default');
      const _orderedSlotsP = Object.keys(_allSlotSummariesP).sort((a, b) => (a === _activeSlotNameP ? -1 : b === _activeSlotNameP ? 1 : 0));
      for (const _slotName of _orderedSlotsP) {
        const _s = _allSlotSummariesP[_slotName]?.summary;
        if (!_s || typeof _s !== 'object') continue;
        // [P1-D2] generic count keys 제외 — 도메인 의미 모호 + LLM "총 N건" 환각 차단
        // [P1-D4] availableFields lookup으로 단위 토큰 부착 — LLM이 placeholder 뒤 단위(%/원/명 등) 추론 보조
        const _afieldsP = _allSlotSummariesP[_slotName]?.availableFields || {};
        const _entries = Object.entries(_s)
          .filter(([k, v]) => typeof v !== 'object' && v !== null && v !== undefined && !isAmbiguousCountKey(k))
          .slice(0, 12)
          .map(([k, v]) => {
            const _unit = extractUnitFromFieldLabel(_afieldsP[k]);
            return _unit ? `${k}(${_unit})=${v}` : `${k}=${v}`;
          });
        if (_entries.length === 0) continue;
        _summarySlotPartsP.push(`${_slotName}: {${_entries.join(', ')}}`);
      }
      // [MULTI-SLOT] ≥2 슬롯 시 slot-qualified placeholder 안내 (ISSUE #1) — bare {{SUMMARY:key}}는 active 슬롯으로만 resolve되어
      //   기간 비교 시 모든 placeholder가 한 슬롯 값으로 collapse됨. 슬롯명은 위 {summaryParts}에 노출된 이름 그대로 사용.
      const _multiSlotPlaceholderGuideP = _summarySlotPartsP.length >= 2
        ? `\n[MULTI-SLOT PLACEHOLDER] Multiple data slots are loaded (see slot names above). To cite a specific slot's scalar, write {{SUMMARY:<slot>:<key>}} using those exact slot names (e.g. {{SUMMARY:${_summarySlotPartsP[0].split(':')[0].trim()}:<key>}}). Bare {{SUMMARY:<key>}} resolves to the ACTIVE slot ONLY — when slots have different values, you MUST use the slot-qualified form for EACH slot, never the bare form.`
        : '';
      const backendSummaryGuide = (_summarySlotPartsP.length > 0
        ? resolvePrompt('BACKEND_SUMMARY_PROMPT', '').replace('{summaryParts}', _summarySlotPartsP.join(' | '))
        : '') + _multiSlotPlaceholderGuideP;

      // [L12-SMS-REGEN-001 fix] SMS 맥락 주입 — 이전 turn에서 SMS 추천 시 다음 turn에서 재생성/톤변경 인식 (dual sync with nodeAgent.mjs)
      const _smsMetaP = session._lastSmsRecommendation;
      const smsContextP = _smsMetaP
        ? `\n[SMS Context] Previous turn generated SMS for ${_smsMetaP.recipientCount} recipients (mode: ${_smsMetaP.mode}, target: ${_smsMetaP.targetDescription || 'GENERAL'}). If user asks to rewrite/redo/change tone of SMS (Korean: 다시 써줘/다시 만들어줘/톤 바꿔줘/N번 다시), call recommend_sms again with updated context/tone. Do NOT try to write SMS text directly.`
        : '';

      // cross-ref hint: gender/birthDay 부재 + memberId 존재 시 ALIVE_LIST 교차참조 안내 //
      let crossRefHint = '';
      if (buffer.items && buffer.items.length > 0) {
        const sampleKeys = Object.keys(buffer.items[0]);
        const hasMemberId = sampleKeys.includes('memberId') || sampleKeys.includes('memberName');
        const hasGender = sampleKeys.includes('gender');
        const hasBirthDay = sampleKeys.includes('birthDay');
        if (hasMemberId && (!hasGender || !hasBirthDay)) {
          const activeSlotName = slots.includes('_merged') ? '_merged' : (session.activeSlot || 'default');
          crossRefHint = resolvePrompt('CROSS_REFERENCE_FIELD_HINT', '').replace('{activeSlot}', activeSlotName);
        }
      }

      messages.push({
        role: "system",
        content: `${displayContext}\n\n${resolvePrompt('BUFFER_STATE_PROMPT', '').replace('{bufferParts}', bufferParts).replace('{enumValues}', enumInfo)}${backendSummaryGuide}${mergeWarning}${placeholderGuide}${crossRefHint}${smsContextP}${prevResponse}`,
      });

      if (TOOL_CALLING_LOG) console.log('[Agent] 🎨 Display:', `${displayResult.totalCount}건`);
      log.push({ type: 'final', responseType: 'display', model: AGENT_MODEL });
      showDisplayResult = null;
    };

    // [0단계] NL-to-SQL — 복합 조건(INTERSECT)만 SQL 엔진으로 처리 //
    // skip 조건: (1) auto cross-ref (2) auto term (3) 사전 inject 발동 시 //
    if (!_autoCrossRefHandled && !_autoTermHandled && !_detectedTermInject && !_deicticEmptyBlocked && !_conversionFunnelHandled) try {
      const session = sessionRef.current;
      const ALL_CATEGORIES = ['MEMBER','MEMBER_DETAIL','MEMBERSHIP','SCHEDULE','SALES','PRODUCT','COMMUNICATION','INFRA','WORKOUT'];

      // 인텐션 사전 로드 (첫 호출 시 캐시) //
      for (const cat of ALL_CATEGORIES) {
        if (!session.intentionCache[cat]) {
          await session.executeGetIntentions({ category: cat });
        }
      }

      // SQL 생성 (버퍼 상태 포함 — 후속 질문 판단용) //
      const queryPrompt = buildQueryPrompt(session.intentionCache, session.getSlotSummaries(), resolvePrompt('QUERY_PROMPT_TEMPLATE', ''));

      const queryResponse = await loggedCompletion('sql_gen', {
        model: AGENT_MODEL,
        messages: [
          { role: "system", content: `${getTodaySystemMessage()}\n${queryPrompt}` },
          { role: "user", content: userInput },
        ],
        ...buildTemperatureParam(AGENT_MODEL, 0.1),
        max_completion_tokens: 300,
      });

      let sqlQuery = (queryResponse.choices?.[0]?.message?.content || '').trim();
      llmCollectorRef.current.attachGeneratedContent(queryResponse?.id, sqlQuery);
      if (TOOL_CALLING_LOG) console.log(`[Agent] SQL generated (input="${userInput.slice(0,40)}"): "${sqlQuery.slice(0, 300)}"`);
      // [PERIOD-SALES] SQL이 파싱한 기간('YYYY-MM-DD','YYYY-MM-DD')을 외부 스코프로 hoist — 다년도 매출 순위 범위용 //
      const _sqlDateMatch = sqlQuery.match(/\('(\d{4}-\d{2}-\d{2})'\s*,\s*'(\d{4}-\d{2}-\d{2})'\)/);
      if (_sqlDateMatch) _sqlPeriodRange = { start: _sqlDateMatch[1], end: _sqlDateMatch[2] };

      // [SQL FIRST SOURCE GUARD] cross-ref 시 SQL 첫 FROM intention이 buffer active intention과 일치해야 함.
      // LLM이 prev intention을 generalize (NEW → ALIVE 등) 하는 stochastic 실패 차단.
      // 단, SQL의 어떤 소스도 buffer active intention을 참조하지 않으면 독립 질의 → guard skip.
      if (sqlQuery && sqlQuery.toUpperCase().includes('INTERSECT')) {
        const _allSources = [...sqlQuery.matchAll(/FROM\s+([A-Z][A-Z0-9_+\-]*)/gi)].map(m => m[1]);
        const _firstSourceMatch = sqlQuery.match(/FROM\s+([A-Z][A-Z0-9_+\-]*)/i);
        const _sqlFirstSource = _firstSourceMatch?.[1] || null;
        const _activeIntention = session.getBuffer?.()?.intention;
        const _anySourceMatchesBuffer = _activeIntention && _allSources.some(s => s === _activeIntention);

        if (_sqlFirstSource && _activeIntention && _sqlFirstSource !== _activeIntention && _anySourceMatchesBuffer) {
          if (TOOL_CALLING_LOG) console.log(`[Agent] SQL first source mismatch: SQL="${_sqlFirstSource}" vs buffer active="${_activeIntention}". Retry with explicit hint.`);
          const retryResponse = await loggedCompletion('sql_retry', {
            model: AGENT_MODEL,
            messages: [
              { role: "system", content: `${getTodaySystemMessage()}\n${queryPrompt}\n\n[CRITICAL CORRECTION] Previous SQL used "${_sqlFirstSource}" but the buffer's active intention is EXACTLY "${_activeIntention}". REGENERATE the SQL using EXACTLY "${_activeIntention}" as the first FROM source (preserve its dateRange). Do NOT substitute with similar/generalized intentions.` },
              { role: "user", content: userInput },
            ],
            ...buildTemperatureParam(AGENT_MODEL, 0.1),
            max_completion_tokens: 300,
          });
          sqlQuery = (retryResponse.choices?.[0]?.message?.content || '').trim();
          llmCollectorRef.current.attachGeneratedContent(retryResponse?.id, sqlQuery);
          if (TOOL_CALLING_LOG) console.log(`[Agent] SQL retry: "${sqlQuery.slice(0,300)}"`);
          const _retryFirstSource = sqlQuery.match(/FROM\s+([A-Z][A-Z0-9_+\-]*)/i)?.[1] || null;
          if (_retryFirstSource && _retryFirstSource !== _activeIntention) {
            if (TOOL_CALLING_LOG) console.log(`[Agent] SQL retry still mismatch (${_retryFirstSource}) — skip to tool loop`);
            sqlQuery = 'NONE';
          }
        }
        else if (_sqlFirstSource && _activeIntention && !_anySourceMatchesBuffer) {
          if (TOOL_CALLING_LOG) console.log(`[Agent] SQL independent query (no source matches buffer "${_activeIntention}") — guard skip`);
        }
      }

      if (sqlQuery.toUpperCase().trim() === 'NONE') {
        if (TOOL_CALLING_LOG) console.log('[Agent] SQL: NONE — follow-up question, skipping to tool loop');
      }
      else if (sqlQuery && sqlQuery.toUpperCase().includes('INTERSECT')) {
        if (TOOL_CALLING_LOG) console.log('[Agent] SQL:', sqlQuery);

        // 후속 turn에 시간 표현 없으면 buffer dateRange 결정적 inherit (LLM 비결정성 방어)
        sqlQuery = session.applyBufferDateRangeToSqlString(sqlQuery, userInput);

        // MERGED 비교 데이터가 이미 있으면 SQL 스킵 → tool loop가 기존 데이터와 intersect
        // NOTE: same-intention period comparison은 executeSqlIntersect 내부에서 mode='inner'로 자동 처리
        const hasMergedComparison = session.getBuffer('_merged')?.items?.some(i =>
          Object.keys(i).some(k => k.startsWith('_change_'))
        );

        if (hasMergedComparison) {
          if (TOOL_CALLING_LOG) console.log('[Agent] SQL skipped: merged comparison data exists → tool loop will intersect');
        }
        else {
          const queryResult = await session.executeSqlQuery(sqlQuery);

          if (queryResult.success) {
            // 데이터는 버퍼에 저장됨 — LLM에게 show_display만 시킴 //
            sqlDataReady = true;

            // SQL 쿼리를 조건 요약으로 변환 (LLM이 "왜?" 질문에 근거를 댈 수 있도록) //
            const conditions = sqlQuery
              .replace(/SELECT \* FROM /gi, '')
              .replace(/INTERSECT/gi, ' AND ')
              .replace(/WHERE /gi, '→ ')
              .replace(/\n/g, ' ')
              .trim();

            messages.push({ role: "system", content: resolvePrompt('DATA_READY_PROMPT', '').replace('{{COUNT}}', queryResult.count).replace('{{FIELDS}}', queryResult.fields?.join(', ') || '').replace('{{CONDITIONS}}', conditions) });
            // [L16-SQL-INTERSECT-EMPTY-EXPLAIN] count=0 시 slot 단위 breakdown inject
            // — LLM에게 "X명 ∩ Y명 = 0명" 형태로 응답하도록 가이드 (intro 보강 + 조건 완화 추천)
            if (queryResult.count === 0 && queryResult.emptyBreakdown?.slots?.length >= 2) {
              const _eb = queryResult.emptyBreakdown;
              const _slotLines = _eb.slots.map((s, i) => `  - 조건 ${i + 1} (${s.table}${s.where ? ' WHERE ' + s.where : ''}): ${s.count}명`).join('\n');
              const _operatorKor = _eb.mode === 'except' ? 'AND NOT (X인데 Y 없는)' : 'AND (교집합)';
              messages.push({ role: "system", content: `[ZERO-ITEM SLOT BREAKDOWN — MANDATORY INCLUDE]\nEach SQL condition loaded independently, then combined with ${_operatorKor}. Final ${_eb.mode === 'except' ? 'EXCEPT' : 'INTERSECT'} = 0명.\n\nSlot-by-slot counts:\n${_slotLines}\n\n[FINISH RESPONSE FORMAT — MANDATORY]\nYour finish response MUST include ALL of the following (do NOT drop):\n1. Each slot's individual count with its condition (예: "출석 조건(attendedCount >= 8): 54명, 매출 조건(totalPrice 1~100000): 1명")\n2. The ${_eb.mode === 'except' ? 'EXCEPT' : 'INTERSECT'} result = 0명 (with operator name)\n3. A short suggestion for the user to relax conditions or check different time range (예: "조건을 완화하거나 기간을 조정해 보시겠어요?")\n\nDo NOT respond with only "조건에 맞는 회원은 0명이에요." — that omits the breakdown and the user cannot tell which condition narrowed too much.` });
            }
            // [TOKEN-ANCHORED T0 FIX] SQL intersect 자동 merge로 buffer에 _change_*/_changeRate_* 보유 시
            // LLM 첫 호출 직전 placeholder 가이드 즉시 inject
            // — LLM이 multi tool_call(show_display + finish)로 첫 응답 시 show_display 후 inject가 finish에 미반영
            {
              const _bufT0 = sessionRef.current?.getBuffer?.();
              if (_bufT0?.items?.length) {
                const _changeFields = new Set();
                for (const it of _bufT0.items) {
                  if (it && typeof it === 'object') {
                    for (const k of Object.keys(it)) {
                      if (k.startsWith('_change_') && !k.startsWith('_changeRate_')) {
                        _changeFields.add(k.slice('_change_'.length));
                      }
                    }
                  }
                }
                if (_changeFields.size > 0) {
                  messages.push({
                    role: "system",
                    content: resolvePrompt('OUTRO_PLACEHOLDER_PROMPT', '').replace('{availableChangeFields}', [..._changeFields].join(', '))
                  });
                }
              }
            }
            if (TOOL_CALLING_LOG) console.log(`[Agent] SQL intersect success: ${queryResult.count}건, handing to LLM for display`);
          }
          else {
            if (TOOL_CALLING_LOG) console.log('[Agent] SQL intersect failed, falling back to tool loop:', queryResult.message);
          }
        }
      }
    }
    catch (e) {
      if (TOOL_CALLING_LOG) console.log('[Agent] SQL failed, falling back to tool loop:', e.message);
    }

    // [DEICTIC EMPTY BUFFER GUARD] deterministic finish — tool loop skip //
    if (_deicticEmptyBlocked) {
      finalText = '직전 조회 결과가 0건이어서 해당 요청을 처리할 수 없습니다. 먼저 데이터를 조회해 주세요.';
      updateAiMessage(aiMessageId, finalText, { isToolLoading: false });
      log.push({ type: 'deictic_empty_guard', iteration: 0, message: 'Deictic reference with 0-item buffer — deterministic finish, tool loop skipped' });
      if (TOOL_CALLING_LOG) console.log('[runAgent] DEICTIC EMPTY BUFFER GUARD — tool loop skipped, deterministic finish');
    }

    // [CONVERSION COHORT-0] 모수 0 결정적 finish — tool loop skip (dual sync with nodeAgent.mjs) //
    //   기존: 프롬프트 지시("구매자 없다고 안내하고 finish") 주입 → LLM ~1/2 확률로 무시하고 매출/재등록 등 무관 조회 hijack. 코드가 finish 텍스트 확정으로 차단.
    if (_conversionCohort0) {
      finalText = _conversionCohort0.kind === 'funnel'
        ? `${_conversionCohort0.startDate}~${_conversionCohort0.endDate}에 '${_conversionCohort0.fromName}' 구매 회원이 없어서 전환율을 계산할 수 없어요. 기간을 넓혀서 다시 확인해 볼까요?`
        : _conversionCohort0.kind === 'newconv'
        ? `${_conversionCohort0.startDate}~${_conversionCohort0.endDate}에 신규 진입 회원이 없어서 전환율을 계산할 수 없어요. 기간을 넓혀서 다시 확인해 볼까요?`
        : `${_conversionCohort0.startDate}~${_conversionCohort0.endDate}에 '${_conversionCohort0.fromName}' 구매 회원이 없어요. 기간을 넓혀서 다시 확인해 볼까요?`;
      updateAiMessage(aiMessageId, finalText, { isToolLoading: false });
      log.push({ type: 'final', responseType: 'text', content: finalText, model: AGENT_MODEL, reason: 'conversion_cohort0_deterministic' });
      if (TOOL_CALLING_LOG) console.log('[CONVERSION-COHORT0] deterministic finish — tool loop skipped');
    }

    // [LTV-ORCHESTRATION] LTV 값/데이터 요청 시 코드 결정성 short-circuit (detect → prefetch → executeShowLtv) //
    //   본질: gpt가 show_ltv 라우팅에 실패(show_display 후 포기 / 도구 0개)하는 VIZ-LTV-001/002 차단. LTV는 격리 지표 →
    //         사용자 승인 하 hardcode. _detectLtvDataIntent가 유일 gate(개념/방법 질문은 detector가 FALSE → 일반 flow). dual sync with nodeAgent.mjs.
    //   non-fabrication: executeShowLtv success=false면 short-circuit X → 일반 loop fall-through (가짜 LTV emit X).
    let _ltvOrchestrated = false;
    if (!_deicticEmptyBlocked && !_conversionCohort0 && !_autoCrossRefHandled && !_autoTermHandled) {
      try {
        if (sessionRef.current._detectLtvDataIntent(userInput)) {
          if (TOOL_CALLING_LOG) console.log('[LTV-ORCHESTRATION] detected → prefetch + executeShowLtv (deterministic)');
          const _ltvMonthMatch = String(userInput).match(/(\d{4})[.\-/년\s]*\s*(\d{1,2})\s*월?/);
          const _ltvArgs = _ltvMonthMatch
            ? { month: `${_ltvMonthMatch[1]}-${String(_ltvMonthMatch[2]).padStart(2, '0')}` }
            : {};
          await sessionRef.current._prefetchVizRequiredSources('show_ltv', _ltvArgs);
          const _ltv = await sessionRef.current.executeShowLtv(_ltvArgs);
          if (_ltv?.success && _ltv.ltvResult) {
            const _r = _ltv.ltvResult;
            const _fmt = (n) => (typeof n === 'number' ? n.toLocaleString('ko-KR') : n);
            const _membersCount = (typeof _r.activeMembers === 'number') ? _r.activeMembers : _r.membersCount;
            const _periodLabel = _r.periodLabel || '올해';
            finalText = `우리 체육관 회원 LTV(생애가치)는 약 ${_fmt(_r.ltv)}원이에요. (${_periodLabel} 기준 월 평균 매출 ${_fmt(_r.priceAvg)}원 × 평균 이용 ${_r.durationAvg}개월, 유효 회원 ${_membersCount}명 기준)`;
            _ltvOrchestrated = true;
            updateAiMessage(aiMessageId, finalText, { isNewResponse: true });
            log.push({ type: 'tool_call', tool: 'show_ltv', args: _ltvArgs, iteration: 0, orchestrated: true });
            log.push({ type: 'tool_result', tool: 'show_ltv', success: true, iteration: 0, message: _ltv.message || null });
            log.push({ type: 'final', responseType: 'ltv', content: finalText, model: AGENT_MODEL, reason: 'ltv_orchestration_deterministic' });
            if (TOOL_CALLING_LOG) console.log(`[LTV-ORCHESTRATION] deterministic finish: "${finalText.slice(0, 80)}"`);
          }
          else if (TOOL_CALLING_LOG) {
            console.log(`[LTV-ORCHESTRATION] executeShowLtv success=false → fall through (no fabrication): ${_ltv?.message || 'n/a'}`);
          }
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[LTV-ORCHESTRATION] failed (fall through to normal loop):', e.message);
        _ltvOrchestrated = false;
      }
    }

    // [ATTENDANCE-RANKING-ORCHESTRATION] 출석 순위 요청 시 코드 결정성 short-circuit (detect → prefetch → executeShowAttendanceRanking) //
    //   본질: gpt가 plain 출석 랭킹을 non-MONTHLY+sort+show_display로 격하(L1-VIZ-ATTENDANCE-RANK 4/5 진동, VIZ-ATTENDANCE-RANKING-001 0/5)하는 것 차단.
    //         프롬프트(663/667/3080)는 상호보완 유지. _detectAttendanceRankingDataIntent가 유일 gate(threshold/축별/성별/비교/표 제외). dual sync with nodeAgent.mjs.
    //   non-fabrication: executeShowAttendanceRanking success=false면 short-circuit X → 일반 loop fall-through.
    let _rankingOrchestrated = false;
    if (!_deicticEmptyBlocked && !_conversionCohort0 && !_autoCrossRefHandled && !_autoTermHandled && !_ltvOrchestrated) {
      try {
        if (sessionRef.current._detectAttendanceRankingDataIntent(userInput)) {
          if (TOOL_CALLING_LOG) console.log('[ATTENDANCE-RANKING-ORCHESTRATION] detected → prefetch + executeShowAttendanceRanking (deterministic)');
          // [#12] 기간/인원 결정 — 명시월(YYYY월) > 상대기간(지난/최근 N개월·지난달) > 현재월 default. topN = 상위 N명 표시 cap. dual sync with nodeAgent.mjs //
          const _rankMonthMatch = String(userInput).match(/(\d{4})[.\-/년\s]*\s*(\d{1,2})\s*월?/);
          const _rankArgs = {};
          if (_rankMonthMatch) {
            _rankArgs.month = `${_rankMonthMatch[1]}-${String(_rankMonthMatch[2]).padStart(2, '0')}`;
          }
          else if (typeof resolveRelativeMonthRange === 'function') {
            const _relRange = resolveRelativeMonthRange(userInput);
            if (_relRange) { _rankArgs.startDate = _relRange.startDate; _rankArgs.endDate = _relRange.endDate; }
          }
          const _capMatch = String(userInput).match(/상위\s*(\d+)|top\s*(\d+)|(\d+)\s*명(?!\s*중)/i);
          const _capN = _capMatch ? Number(_capMatch[1] || _capMatch[2] || _capMatch[3]) : null;
          if (_capN && _capN > 0 && _capN <= 100) _rankArgs.topN = _capN;
          await sessionRef.current._prefetchVizRequiredSources('show_attendance_ranking', _rankArgs);
          const _rank = await sessionRef.current.executeShowAttendanceRanking(_rankArgs);
          if (_rank?.success && _rank.attendanceRankingResult) {
            const _r = _rank.attendanceRankingResult;
            const _rk = _r.ranking || [];
            const _textN = _rank.topN ? _rk.length : Math.min(3, _rk.length);
            const _textList = _rk.slice(0, _textN).map((m, i) => `${i + 1}위 ${m.name} ${m.attendCount}회`).join(', ');
            const _avgPart = (_rank.avgAttended > 0) ? ` 출석 회원 ${_rank.attendedCount}명의 평균 출석은 ${_rank.avgAttended}회예요.` : '';
            finalText = `${_r.periodLabel || '이번 기간'} 회원 출석 순위${_rank.topN ? ` 상위 ${_rank.topN}명` : ''}이에요. ${_textList}.${_avgPart}`;
            _rankingOrchestrated = true;
            vizResult = { type: 'attendanceRankingResult', payload: _r };
            updateAiMessage(aiMessageId, finalText, { vizData: vizResult, isNewResponse: true });
            log.push({ type: 'tool_call', tool: 'show_attendance_ranking', args: _rankArgs, iteration: 0, orchestrated: true });
            log.push({ type: 'tool_result', tool: 'show_attendance_ranking', success: true, iteration: 0, message: _rank.message || null });
            log.push({ type: 'final', responseType: 'attendanceRankingResult', content: finalText, model: AGENT_MODEL, reason: 'attendance_ranking_orchestration_deterministic' });
            if (TOOL_CALLING_LOG) console.log(`[ATTENDANCE-RANKING-ORCHESTRATION] deterministic finish: "${finalText.slice(0, 80)}"`);
          }
          else if (TOOL_CALLING_LOG) {
            console.log(`[ATTENDANCE-RANKING-ORCHESTRATION] executeShowAttendanceRanking success=false → fall through (no fabrication): ${_rank?.message || 'n/a'}`);
          }
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[ATTENDANCE-RANKING-ORCHESTRATION] failed (fall through to normal loop):', e.message);
        _rankingOrchestrated = false;
      }
    }

    // [MEMBER-STATUS-ORCHESTRATION #10] "{이름} 회원 마지막 출석일이랑 회원권 상태" 식 멀티-aspect 회원조회 코드 결정성 short-circuit. //
    //   LLM 표출 비결정(어느 aspect 표·intro 라벨·records[0] 환각, 2/5) 우회 → 양 aspect 핵심사실을 코드가 결정 라인 생성. //
    //   gate: "{이름}회원"(회원권 제외) 앵커 + 등재 aspect 2+ 요청. member 1매치 + 양 aspect fetch 성공 시만(비조작). dual sync (nodeAgent.mjs). //
    //   [L12-SQL-INTERSECT 간섭 fix] 3중 가드: (A) sqlDataReady 시 skip — SQL 경로가 이번 턴 데이터를 이미 확정(도구도 show_display/finish로
    //   제한됨)인데 probe가 activeSlot을 clobber하면 SQL 결과(_merged) 렌더 불가. (B) 이름 앵커가 aspect term 자체("유효회원"의 "유효")거나
    //   도메인 noun이면 사람 이름 오인 — probe 자체 skip (detectVagueMemberSearchName 단일소스 재사용). (C) 미발동 fall-through 시
    //   activeSlot 복원 — probe의 CHECK_MEMBER_SEARCH(0건/동명이인)가 직전 active buffer 컨텍스트를 빈 슬롯으로 clobber하는 것 차단
    //   (viz prefetch #B active-slot 오염 fix 동형 패턴). //
    let _memberStatusOrchestrated = false;
    if (!_deicticEmptyBlocked && !_conversionCohort0 && !_autoCrossRefHandled && !_autoTermHandled && !_ltvOrchestrated && !_rankingOrchestrated && !sqlDataReady) {
      let _msPrevActiveSlot = null;
      let _msProbeExecuted = false;
      try {
        const _msNameMatch = String(userInput).match(/([가-힣]{2,4})\s*회원(?!권)/);
        const _msNameRaw = _msNameMatch?.[1];
        const _msAspects = (typeof detectRequestedMemberDetailAspects === 'function') ? detectRequestedMemberDetailAspects(userInput) : [];
        const _msNameIsAspectTerm = _msNameRaw && _msAspects.some(a => Array.isArray(a.terms) && a.terms.includes(_msNameRaw));
        const _msNameIsDomainNoun = _msNameRaw && typeof detectVagueMemberSearchName === 'function' && detectVagueMemberSearchName({ name: _msNameRaw }) != null;
        const _msName = (_msNameIsAspectTerm || _msNameIsDomainNoun) ? null : _msNameRaw;

        if (_msNameRaw && !_msName && TOOL_CALLING_LOG) console.log(`[MEMBER-STATUS-ORCHESTRATION] name anchor "${_msNameRaw}" rejected (${_msNameIsAspectTerm ? 'aspect term' : 'domain noun'}) → skip`);
        if (_msName && _msAspects.length >= 2) {
          if (TOOL_CALLING_LOG) console.log(`[MEMBER-STATUS-ORCHESTRATION] detected name="${_msName}" aspects=${_msAspects.map(a => a.intention).join('+')} → resolve + prefetch (deterministic)`);
          _msPrevActiveSlot = sessionRef.current.activeSlot;
          _msProbeExecuted = true;
          await sessionRef.current.executeCallApi({ intention: 'CHECK_MEMBER_SEARCH', params: { name: _msName } });
          const _msMemberId = sessionRef.current._lastMember?.memberId;
          if (_msMemberId != null) {
            const _findBufMS = sessionRef.current.findBufferByIntention || sessionRef.current._findBufferByIntention;
            const _msFacts = [];
            for (const _asp of _msAspects) {
              let _buf = typeof _findBufMS === 'function' ? _findBufMS.call(sessionRef.current, _asp.intention) : null;
              if (!_buf?.items?.length) {
                const _aspSlot = `_aspect_${_asp.intention.replace(/[^A-Za-z0-9]/g, '_').toLowerCase()}`;
                await sessionRef.current.executeCallApi({ intention: _asp.intention, slot: _aspSlot, params: { memberId: _msMemberId } });
                _buf = typeof _findBufMS === 'function' ? _findBufMS.call(sessionRef.current, _asp.intention) : null;
              }
              const _fact = (_buf?.items?.length) ? _asp.headline(_buf.items) : null;
              if (_fact) _msFacts.push(_fact);
            }
            if (_msFacts.length >= 2) {
              const _msDisplayName = sessionRef.current._lastMember?.name || _msName;
              finalText = `${_msDisplayName} 회원이야. ${_msFacts.join(' ')}`;
              _memberStatusOrchestrated = true;
              updateAiMessage(aiMessageId, finalText, { isNewResponse: true });
              log.push({ type: 'tool_call', tool: 'member_status_orchestration', args: { name: _msName, memberId: _msMemberId }, iteration: 0, orchestrated: true });
              log.push({ type: 'final', responseType: 'text', content: finalText, model: AGENT_MODEL, reason: 'member_status_orchestration_deterministic' });
              if (TOOL_CALLING_LOG) console.log(`[MEMBER-STATUS-ORCHESTRATION] deterministic finish: "${finalText.slice(0, 100)}"`);
            }
            else if (TOOL_CALLING_LOG) {
              console.log(`[MEMBER-STATUS-ORCHESTRATION] aspect fact 부족(${_msFacts.length}/2) → fall through (no fabrication)`);
            }
          }
          else if (TOOL_CALLING_LOG) {
            console.log('[MEMBER-STATUS-ORCHESTRATION] member 1-match 아님(0/동명이인) → fall through');
          }
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[MEMBER-STATUS-ORCHESTRATION] failed (fall through to normal loop):', e.message);
        _memberStatusOrchestrated = false;
      }

      // [C] 미발동 fall-through 시 activeSlot 복원 — probe(CHECK_MEMBER_SEARCH 0건/동명이인)가 직전 active buffer를 빈 슬롯으로 clobber하는 것 차단 //
      if (!_memberStatusOrchestrated && _msProbeExecuted && sessionRef.current.activeSlot !== _msPrevActiveSlot) {
        if (_msPrevActiveSlot == null || sessionRef.current.buffers?.has?.(_msPrevActiveSlot)) {
          sessionRef.current.activeSlot = _msPrevActiveSlot;
          if (TOOL_CALLING_LOG) console.log(`[MEMBER-STATUS-ORCHESTRATION] fall-through → activeSlot 복원 ("${_msPrevActiveSlot}")`);
        }
      }
    }

    // [MEMBERSHIP-PREFERENCE-ORCHESTRATION] 회원권 선호도/인기/랭킹 요청 시 코드 결정성 short-circuit (detect → prefetch → executeShowMembershipPreference) //
    //   본질: gpt가 '회원권 선호도'를 CHECK_MEMBERSHIP+TOP+SALES_STATISTICS_BY+PERIOD(서버집계 랭킹)로 격하 + 가이드가 show_display 강제 → 차트 미표출. //
    //         _detectMembershipPreferenceDataIntent가 유일 gate(연령/참여도·표 명시 제외). dual sync with nodeAgent.mjs. //
    //   non-fabrication: executeShowMembershipPreference success=false면 short-circuit X → 일반 loop fall-through. //
    let _membershipPrefOrchestrated = false;
    if (!_deicticEmptyBlocked && !_conversionCohort0 && !_autoCrossRefHandled && !_autoTermHandled && !_ltvOrchestrated && !_rankingOrchestrated && !_memberStatusOrchestrated) {
      try {
        if (sessionRef.current._detectMembershipPreferenceDataIntent(userInput)) {
          if (TOOL_CALLING_LOG) console.log('[MEMBERSHIP-PREFERENCE-ORCHESTRATION] detected → prefetch + executeShowMembershipPreference (deterministic)');
          // 기간 결정 — 명시월(YYYY월) > 상대기간(지난/최근 N개월·지난달) > 현재월 default. dual sync with nodeAgent.mjs //
          const _mpMonthMatch = String(userInput).match(/(\d{4})[.\-/년\s]*\s*(\d{1,2})\s*월?/);
          const _mpArgs = {};
          if (_mpMonthMatch) {
            const _mpY = _mpMonthMatch[1];
            const _mpM = String(_mpMonthMatch[2]).padStart(2, '0');
            const _mpLast = new Date(Number(_mpY), Number(_mpM), 0).getDate();
            _mpArgs.startDate = `${_mpY}-${_mpM}-01`;
            _mpArgs.endDate = `${_mpY}-${_mpM}-${String(_mpLast).padStart(2, '0')}`;
          }
          else if (typeof resolveRelativeMonthRange === 'function') {
            const _mpRel = resolveRelativeMonthRange(userInput);
            if (_mpRel) { _mpArgs.startDate = _mpRel.startDate; _mpArgs.endDate = _mpRel.endDate; }
          }
          await sessionRef.current._prefetchVizRequiredSources('show_membership_preference', _mpArgs);
          const _mp = await sessionRef.current.executeShowMembershipPreference(_mpArgs);
          if (_mp?.success && _mp.membershipPreferenceResult) {
            const _mpr = _mp.membershipPreferenceResult;
            const _mpTop = _mpr.topCards?.mostSelected;
            const _mpRevenue = _mpr.topCards?.topRevenue;
            finalText = `회원권 선호도 결과예요. 가입 회원 ${(_mpr.totalCount || 0).toLocaleString('ko-KR')}명 기준`
              + (_mpTop ? `, 가장 많이 선택된 회원권은 ${_mpTop.name}(${_mpTop.count}명)` : '')
              + (_mpRevenue ? `, 매출 기여 1위는 ${_mpRevenue.name}` : '')
              + `이에요.`;
            _membershipPrefOrchestrated = true;
            vizResult = { type: 'membershipPreferenceResult', payload: _mpr };
            clearToolLoading(aiMessageId);
            updateAiMessage(aiMessageId, finalText, { vizData: vizResult, isNewResponse: true });
            log.push({ type: 'tool_call', tool: 'show_membership_preference', args: _mpArgs, iteration: 0, orchestrated: true });
            log.push({ type: 'tool_result', tool: 'show_membership_preference', success: true, iteration: 0, message: _mp.message || null });
            log.push({ type: 'final', responseType: 'membershipPreferenceResult', content: finalText, model: AGENT_MODEL, reason: 'membership_preference_orchestration_deterministic' });
            if (TOOL_CALLING_LOG) console.log(`[MEMBERSHIP-PREFERENCE-ORCHESTRATION] deterministic finish: "${finalText.slice(0, 80)}"`);
          }
          else if (TOOL_CALLING_LOG) {
            console.log(`[MEMBERSHIP-PREFERENCE-ORCHESTRATION] executeShowMembershipPreference success=false → fall through (no fabrication): ${_mp?.message || 'n/a'}`);
          }
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[MEMBERSHIP-PREFERENCE-ORCHESTRATION] failed (fall through to normal loop):', e.message);
        _membershipPrefOrchestrated = false;
      }
    }

    // [PERIOD-SALES-ORCHESTRATION] 월별 매출 순위(기간별 매출) 요청 시 코드 결정성 short-circuit (detect → prefetch → executeShowPeriodSales) //
    //   본질: gpt가 '월별 매출 순위'를 통계 인텐트 + show_display 표로 격하 → 막대 차트 미표출. //
    //         _detectPeriodSalesDataIntent가 유일 gate(회원권·추이·표 명시 제외). dual sync with nodeAgent.mjs. //
    //   non-fabrication: executeShowPeriodSales success=false면 short-circuit X → 일반 loop fall-through. //
    let _periodSalesOrchestrated = false;
    if (!_deicticEmptyBlocked && !_conversionCohort0 && !_autoCrossRefHandled && !_autoTermHandled && !_ltvOrchestrated && !_rankingOrchestrated && !_memberStatusOrchestrated && !_membershipPrefOrchestrated) {
      try {
        if (sessionRef.current._detectPeriodSalesDataIntent(userInput)) {
          if (TOOL_CALLING_LOG) console.log('[PERIOD-SALES-ORCHESTRATION] detected → prefetch + executeShowPeriodSales (deterministic)');
          // 날짜 범위 — LLM SQL이 파싱한 기간('YYYY-MM-DD','YYYY-MM-DD') 우선, 없으면 올해(1/1~오늘) //
          //   executor가 연도별로 sales/revenue/year를 직접 호출·병합하므로 다년도 범위 지원 (prefetch 불필요) //
          const _psNow = new Date();
          const _psYear = _psNow.getFullYear();
          const _psToday = `${_psYear}-${String(_psNow.getMonth() + 1).padStart(2, '0')}-${String(_psNow.getDate()).padStart(2, '0')}`;
          let _psStart = `${_psYear}-01-01`;
          let _psEnd = _psToday;
          // 결정적 한국어 기간 파서 우선 → LLM SQL 파싱 → 올해(1/1~오늘) default //
          const _psRange = parsePeriodRange(userInput, { year: _psYear, month: _psNow.getMonth() + 1, day: _psNow.getDate() }) || _sqlPeriodRange;
          if (_psRange) { _psStart = _psRange.start; _psEnd = _psRange.end; }
          const _psArgs = { startDate: _psStart, endDate: _psEnd };
          const _ps = await sessionRef.current.executeShowPeriodSales(_psArgs);
          if (_ps?.success && _ps.periodSalesResult) {
            const _psr = _ps.periodSalesResult;
            const _psTop = (_psr.monthData || [])[0];
            const _psTopMonth = (_psTop && _psTop.date && typeof _psTop.date.format === 'function') ? _psTop.date.format('YYYY.MM') : '';
            finalText = `${_psStart.slice(0, 7)} ~ ${_psEnd.slice(0, 7)} 월별 매출 순위예요. 기간 매출 합계는 ${(_psr.periodSalesPrice || 0).toLocaleString('ko-KR')}원`
              + (_psTop && _psTopMonth ? `, 매출이 가장 높은 달은 ${_psTopMonth}(${(_psTop.salesPrice || 0).toLocaleString('ko-KR')}원)` : '')
              + `이에요.`;
            _periodSalesOrchestrated = true;
            vizResult = { type: 'periodSalesResult', payload: _psr };
            clearToolLoading(aiMessageId);
            updateAiMessage(aiMessageId, finalText, { vizData: vizResult, isNewResponse: true });
            log.push({ type: 'tool_call', tool: 'show_period_sales', args: _psArgs, iteration: 0, orchestrated: true });
            log.push({ type: 'tool_result', tool: 'show_period_sales', success: true, iteration: 0, message: _ps.message || null });
            log.push({ type: 'final', responseType: 'periodSalesResult', content: finalText, model: AGENT_MODEL, reason: 'period_sales_orchestration_deterministic' });
            if (TOOL_CALLING_LOG) console.log(`[PERIOD-SALES-ORCHESTRATION] deterministic finish: "${finalText.slice(0, 80)}"`);
          }
          else if (TOOL_CALLING_LOG) {
            console.log(`[PERIOD-SALES-ORCHESTRATION] executeShowPeriodSales success=false → fall through (no fabrication): ${_ps?.message || 'n/a'}`);
          }
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[PERIOD-SALES-ORCHESTRATION] failed (fall through to normal loop):', e.message);
        _periodSalesOrchestrated = false;
      }
    }

    // [POINT-MANAGE-ORCHESTRATION] 포인트 관리 현황 요청 시 코드 결정성 short-circuit (detect → executeShowPointManagement) //
    //   본질: gpt가 '포인트 관리/현황'을 통계 인텐트 + show_display 표로 격하 → 포인트 대시보드 미표출. //
    //         _detectPointManagementDataIntent가 유일 gate(개념질문·표명시·회원 액션 제외). dual sync with nodeAgent.mjs. //
    //   non-fabrication: executeShowPointManagement success=false면 short-circuit X → 일반 loop fall-through. //
    let _pointManageOrchestrated = false;
    if (!_deicticEmptyBlocked && !_conversionCohort0 && !_autoCrossRefHandled && !_autoTermHandled && !_ltvOrchestrated && !_rankingOrchestrated && !_memberStatusOrchestrated && !_membershipPrefOrchestrated && !_periodSalesOrchestrated) {
      try {
        if (sessionRef.current._detectPointManagementDataIntent(userInput)) {
          if (TOOL_CALLING_LOG) console.log('[POINT-MANAGE-ORCHESTRATION] detected → executeShowPointManagement (deterministic)');
          // 날짜 범위 — 결정적 한국어 기간 파서 우선 → LLM SQL 파싱 → 올해(1/1~오늘) default //
          const _pmNow = new Date();
          const _pmYear = _pmNow.getFullYear();
          const _pmToday = `${_pmYear}-${String(_pmNow.getMonth() + 1).padStart(2, '0')}-${String(_pmNow.getDate()).padStart(2, '0')}`;
          let _pmStart = `${_pmYear}-01-01`;
          let _pmEnd = _pmToday;
          const _pmRange = parsePeriodRange(userInput, { year: _pmYear, month: _pmNow.getMonth() + 1, day: _pmNow.getDate() }) || _sqlPeriodRange;
          if (_pmRange) { _pmStart = _pmRange.start; _pmEnd = _pmRange.end; }
          const _pmArgs = { startDate: _pmStart, endDate: _pmEnd };
          const _pm = await sessionRef.current.executeShowPointManagement(_pmArgs);
          if (_pm?.success && _pm.pointResult) {
            const _pmr = _pm.pointResult;
            finalText = `${_pmStart.slice(0, 7)} ~ ${_pmEnd.slice(0, 7)} 포인트 관리 현황이에요. 누적 발행 ${(_pmr.totalIssuedPoint || 0).toLocaleString('ko-KR')}P, 누적 사용 ${(_pmr.totalUsedPoint || 0).toLocaleString('ko-KR')}P, 현재 보유 ${(_pmr.totalHeldPoint || 0).toLocaleString('ko-KR')}P이에요.`;
            _pointManageOrchestrated = true;
            vizResult = { type: 'pointResult', payload: _pmr };
            clearToolLoading(aiMessageId);
            updateAiMessage(aiMessageId, finalText, { vizData: vizResult, isNewResponse: true });
            log.push({ type: 'tool_call', tool: 'show_point_mange', args: _pmArgs, iteration: 0, orchestrated: true });
            log.push({ type: 'tool_result', tool: 'show_point_mange', success: true, iteration: 0, message: _pm.message || null });
            log.push({ type: 'final', responseType: 'pointResult', content: finalText, model: AGENT_MODEL, reason: 'point_manage_orchestration_deterministic' });
            if (TOOL_CALLING_LOG) console.log(`[POINT-MANAGE-ORCHESTRATION] deterministic finish: "${finalText.slice(0, 80)}"`);
          }
          else if (TOOL_CALLING_LOG) {
            console.log(`[POINT-MANAGE-ORCHESTRATION] executeShowPointManagement success=false → fall through (no fabrication): ${_pm?.message || 'n/a'}`);
          }
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[POINT-MANAGE-ORCHESTRATION] failed (fall through to normal loop):', e.message);
        _pointManageOrchestrated = false;
      }
    }

    // [MANAGEMENT-REPORT-ORCHESTRATION] 월간 경영 리포트 — 여러 섹션(체험전환율/월별매출 …)을 코드 결정 계산으로 조립해 //
    //   한 응답에 표 여러 개(displayItems + extraDisplays)로 표출. _detectReportDataIntent가 유일 gate(개념질문 제외). //
    //   non-fabrication: 섹션 데이터 부재 시 executeManagementReport success=false → 일반 loop fall-through. dual sync with nodeAgent.mjs //
    let _reportOrchestrated = false;
    if (!_deicticEmptyBlocked && !_conversionCohort0 && !_autoCrossRefHandled && !_autoTermHandled && !_ltvOrchestrated && !_rankingOrchestrated && !_memberStatusOrchestrated && !_membershipPrefOrchestrated && !_periodSalesOrchestrated && !_pointManageOrchestrated) {
      try {
        if (sessionRef.current._detectReportDataIntent(userInput)) {
          if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT-ORCHESTRATION] detected → executeManagementReport (deterministic)');
          // 기간 — LLM SQL 파싱 기간 우선, 없으면 executor 기본(올해 1/1~오늘) //
          const _repArgs = {};
          if (_sqlPeriodRange) { _repArgs.startDate = _sqlPeriodRange.start; _repArgs.endDate = _sqlPeriodRange.end; }
          const _rep = await sessionRef.current.executeManagementReport(_repArgs);
          if (_rep?.success && Array.isArray(_rep.reportDisplays) && _rep.reportDisplays.length > 0) {
            lastDisplayResult = _rep.reportDisplays[0];
            lastExtraDisplays = _rep.reportDisplays.slice(1);
            finalText = _rep.reportIntro;
            _reportOrchestrated = true;
            clearToolLoading(aiMessageId);
            const _repFlags = { displayItems: lastDisplayResult, isNewResponse: true };
            if (lastExtraDisplays.length > 0) _repFlags.extraDisplays = lastExtraDisplays;
            updateAiMessage(aiMessageId, finalText, _repFlags);
            log.push({ type: 'tool_call', tool: 'management_report', args: _repArgs, iteration: 0, orchestrated: true });
            log.push({ type: 'tool_result', tool: 'management_report', success: true, iteration: 0, message: `${_rep.sectionCount} sections` });
            log.push({ type: 'final', responseType: 'managementReport', content: finalText, model: AGENT_MODEL, reason: 'management_report_orchestration_deterministic' });
            if (TOOL_CALLING_LOG) console.log(`[MANAGEMENT-REPORT-ORCHESTRATION] deterministic finish: ${_rep.sectionCount} sections`);
          }
          else if (TOOL_CALLING_LOG) {
            console.log(`[MANAGEMENT-REPORT-ORCHESTRATION] executeManagementReport success=false → fall through (no fabrication): ${_rep?.message || 'n/a'}`);
          }
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[MANAGEMENT-REPORT-ORCHESTRATION] failed (fall through to normal loop):', e.message);
        _reportOrchestrated = false;
      }
    }

    // [MEMBER-TREND-ORCHESTRATION] "(유효/활성)회원 증가/추이" — HISTORY_LIST 로드 + executeShowMemberTrend 결정성 short-circuit (ALIVE_LIST 오유인·vizTools 래치 체크에그·ALIVE_SCOPE 가드 데드락 차단, dual sync nodeAgent.mjs) //
    let _memberTrendOrchestrated = false;
    if (!_deicticEmptyBlocked && !_conversionCohort0 && !_autoCrossRefHandled && !_autoTermHandled && !_ltvOrchestrated && !_rankingOrchestrated && !_memberStatusOrchestrated && !_membershipPrefOrchestrated && !_periodSalesOrchestrated && !_pointManageOrchestrated && !_reportOrchestrated) {
      try {
        const _mt = await sessionRef.current.autoMemberTrendIfMatched(userInput);
        if (_mt?.success) {
          const _mtRes = sessionRef.current.executeShowMemberTrend({ series: _mt.series });
          if (_mtRes?.success && _mtRes.memberTrendResult) {
            const _mtr = _mtRes.memberTrendResult;
            const _series = _mtr.thisYear || [];
            finalText = buildMemberTrendFinalText(_mtr);
            _mtr.intro = finalText;
            _memberTrendOrchestrated = true;
            vizResult = { type: 'memberTrendResult', payload: _mtr };
            clearToolLoading(aiMessageId);
            updateAiMessage(aiMessageId, finalText, { vizData: vizResult, isNewResponse: true });
            log.push({ type: 'tool_call', tool: 'show_member_trend', args: {}, iteration: 0, orchestrated: true });
            log.push({ type: 'tool_result', tool: 'show_member_trend', success: true, iteration: 0, message: _mtRes.message || null });
            log.push({ type: 'final', responseType: 'memberTrendResult', content: finalText, model: AGENT_MODEL, reason: 'member_trend_orchestration_deterministic' });
            if (TOOL_CALLING_LOG) console.log(`[MEMBER-TREND-ORCHESTRATION] deterministic finish: ${_series.length}개월`);
          }
          else if (TOOL_CALLING_LOG) {
            console.log(`[MEMBER-TREND-ORCHESTRATION] executeShowMemberTrend success=false → fall through (no fabrication): ${_mtRes?.message || 'n/a'}`);
          }
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[MEMBER-TREND-ORCHESTRATION] failed (fall through to normal loop):', e.message);
        _memberTrendOrchestrated = false;
      }
    }

    // [MEMBER-REVENUE-TREND-ORCHESTRATION] "(신규/재등록) + 매출 + 추이/월별/증감" — HISTORY_LIST 로드 + executeShowMemberRevenueTrend 결정성 short-circuit (RevenueChart 막대=신규매출/점선=재등록매출, flow-trend 인원증감과 배타, dual sync nodeAgent.mjs) //
    let _memberRevenueTrendOrchestrated = false;
    if (!_deicticEmptyBlocked && !_conversionCohort0 && !_autoCrossRefHandled && !_autoTermHandled && !_ltvOrchestrated && !_rankingOrchestrated && !_memberStatusOrchestrated && !_membershipPrefOrchestrated && !_periodSalesOrchestrated && !_pointManageOrchestrated && !_reportOrchestrated && !_memberTrendOrchestrated) {
      try {
        const _mr = await sessionRef.current.autoMemberRevenueTrendIfMatched(userInput);
        if (_mr?.success) {
          const _mrRes = sessionRef.current.executeShowMemberRevenueTrend({});
          if (_mrRes?.success && _mrRes.memberTrendResult) {
            const _mrr = _mrRes.memberTrendResult;
            finalText = buildMemberRevenueTrendFinalText(_mrr);
            _mrr.intro = finalText;
            _memberRevenueTrendOrchestrated = true;
            vizResult = { type: 'memberTrendResult', payload: _mrr };
            clearToolLoading(aiMessageId);
            updateAiMessage(aiMessageId, finalText, { vizData: vizResult, isNewResponse: true });
            log.push({ type: 'tool_call', tool: 'show_member_revenue_trend', args: {}, iteration: 0, orchestrated: true });
            log.push({ type: 'tool_result', tool: 'show_member_revenue_trend', success: true, iteration: 0, message: _mrRes.message || null });
            log.push({ type: 'final', responseType: 'memberTrendResult', content: finalText, model: AGENT_MODEL, reason: 'member_revenue_trend_orchestration_deterministic' });
            if (TOOL_CALLING_LOG) console.log(`[MEMBER-REVENUE-TREND-ORCHESTRATION] deterministic finish: ${(_mrr.thisYear || []).length}개월`);
          }
          else if (TOOL_CALLING_LOG) {
            console.log(`[MEMBER-REVENUE-TREND-ORCHESTRATION] executeShowMemberRevenueTrend success=false → fall through (no fabrication): ${_mrRes?.message || 'n/a'}`);
          }
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[MEMBER-REVENUE-TREND-ORCHESTRATION] failed (fall through to normal loop):', e.message);
        _memberRevenueTrendOrchestrated = false;
      }
    }

    // [MEMBER-FLOW-TREND-ORCHESTRATION] "(신규/재등록/이탈) + 추이/증감/월별" — HISTORY_LIST 로드 + executeShowMemberFlowTrend 결정성 short-circuit (show_member_status 목록 오유인·vizTools 래치 체크에그 차단, A(유효회원 스톡, _memberTrendOrchestrated)와 배타 게이트, dual sync nodeAgent.mjs) //
    let _memberFlowTrendOrchestrated = false;
    if (!_deicticEmptyBlocked && !_conversionCohort0 && !_autoCrossRefHandled && !_autoTermHandled && !_ltvOrchestrated && !_rankingOrchestrated && !_memberStatusOrchestrated && !_membershipPrefOrchestrated && !_periodSalesOrchestrated && !_pointManageOrchestrated && !_reportOrchestrated && !_memberTrendOrchestrated && !_memberRevenueTrendOrchestrated) {
      try {
        const _mf = await sessionRef.current.autoMemberFlowTrendIfMatched(userInput);
        if (_mf?.success) {
          const _mfRes = sessionRef.current.executeShowMemberFlowTrend({});
          if (_mfRes?.success && _mfRes.memberFlowTrendResult) {
            const _mfr = _mfRes.memberFlowTrendResult;
            const _stats = _mfr.monthlyStats || [];
            const _last = _stats[_stats.length - 1] || {};
            finalText = `우리 센터 월별 회원 증감 추이야 (최근 ${_stats.length}개월). 막대 위쪽은 신규+재등록, 아래쪽은 이탈을 나타내. 가장 최근 달은 신규 ${(_last.new ?? 0)}명, 재등록 ${(_last.renewal ?? 0)}명, 이탈 ${(_last.churn ?? 0)}명이야.`;
            _mfr.intro = finalText;
            _memberFlowTrendOrchestrated = true;
            vizResult = { type: 'memberFlowTrendResult', payload: _mfr };
            clearToolLoading(aiMessageId);
            updateAiMessage(aiMessageId, finalText, { vizData: vizResult, isNewResponse: true });
            log.push({ type: 'tool_call', tool: 'show_member_flow_trend', args: {}, iteration: 0, orchestrated: true });
            log.push({ type: 'tool_result', tool: 'show_member_flow_trend', success: true, iteration: 0, message: _mfRes.message || null });
            log.push({ type: 'final', responseType: 'memberFlowTrendResult', content: finalText, model: AGENT_MODEL, reason: 'member_flow_trend_orchestration_deterministic' });
            if (TOOL_CALLING_LOG) console.log(`[MEMBER-FLOW-TREND-ORCHESTRATION] deterministic finish: ${_stats.length}개월`);
          }
          else if (TOOL_CALLING_LOG) {
            console.log(`[MEMBER-FLOW-TREND-ORCHESTRATION] executeShowMemberFlowTrend success=false → fall through (no fabrication): ${_mfRes?.message || 'n/a'}`);
          }
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[MEMBER-FLOW-TREND-ORCHESTRATION] failed (fall through to normal loop):', e.message);
        _memberFlowTrendOrchestrated = false;
      }
    }

    // [MEMBER-STATUS-TYPE-ORCHESTRATION] "(신규/재등록/이탈) 회원 현황/목록/매출" — HISTORY_LIST(+SALES 매출조인) 로드 + executeShowMemberStatus 결정성 short-circuit (show_display 범용표 오유인 차단, 추이 게이트와 배타, dual sync nodeAgent.mjs) //
    let _memberStatusTypeOrchestrated = false;
    if (!_deicticEmptyBlocked && !_conversionCohort0 && !_autoCrossRefHandled && !_autoTermHandled && !_ltvOrchestrated && !_rankingOrchestrated && !_memberStatusOrchestrated && !_membershipPrefOrchestrated && !_periodSalesOrchestrated && !_pointManageOrchestrated && !_reportOrchestrated && !_memberTrendOrchestrated && !_memberRevenueTrendOrchestrated && !_memberFlowTrendOrchestrated) {
      try {
        const _ms = await sessionRef.current.autoMemberStatusIfMatched(userInput);
        if (_ms?.success) {
          const _msRes = await sessionRef.current.executeShowMemberStatus({ memberType: _ms.memberType, startDate: _ms.startDate, endDate: _ms.endDate });
          if (_msRes?.success && _msRes.memberStatusResult) {
            const _msr = _msRes.memberStatusResult;
            finalText = buildMemberStatusFinalText(_msr);
            _msr.intro = finalText;
            _memberStatusTypeOrchestrated = true;
            vizResult = { type: 'memberStatusResult', payload: _msr };
            clearToolLoading(aiMessageId);
            updateAiMessage(aiMessageId, finalText, { vizData: vizResult, isNewResponse: true });
            log.push({ type: 'tool_call', tool: 'show_member_status', args: { memberType: _ms.memberType }, iteration: 0, orchestrated: true });
            log.push({ type: 'tool_result', tool: 'show_member_status', success: true, iteration: 0, message: _msRes.message || null });
            log.push({ type: 'final', responseType: 'memberStatusResult', content: finalText, model: AGENT_MODEL, reason: 'member_status_type_orchestration_deterministic' });
            if (TOOL_CALLING_LOG) console.log(`[MEMBER-STATUS-TYPE-ORCHESTRATION] deterministic finish: ${_ms.memberType} ${(_msr.members || []).length}명`);
          }
          else if (TOOL_CALLING_LOG) {
            console.log(`[MEMBER-STATUS-TYPE-ORCHESTRATION] executeShowMemberStatus success=false → fall through (no fabrication): ${_msRes?.message || 'n/a'}`);
          }
        }
      } catch (e) {
        if (TOOL_CALLING_LOG) console.log('[MEMBER-STATUS-TYPE-ORCHESTRATION] failed (fall through to normal loop):', e.message);
        _memberStatusTypeOrchestrated = false;
      }
    }

    while (!_deicticEmptyBlocked && !_conversionCohort0 && !_ltvOrchestrated && !_rankingOrchestrated && !_memberStatusOrchestrated && !_membershipPrefOrchestrated && !_periodSalesOrchestrated && !_pointManageOrchestrated && !_reportOrchestrated && !_memberTrendOrchestrated && !_memberRevenueTrendOrchestrated && !_memberFlowTrendOrchestrated && !_memberStatusTypeOrchestrated && iteration < MAX_TOOL_ITERATIONS) {
      iteration++;

      if (isCancelledRef.current) {
        finalText = CANCELLED_RESPONSE;
        updateAiMessage(aiMessageId, finalText);
        log.push({ type: 'cancelled' });
        break;
      }

      try {
        // 상태 기반 도구 가용성 제한 //
        const compStatus = sessionRef.current.getComparisonStatus();
        const isComparisonIncomplete = compStatus.active && !compStatus.hasMerged && compStatus.compareSlots.length > 0;

        // [LAZY TOOL-SET D1] mid-turn 래치 재평가 — visualizationOnly 인텐션 buffer 적재 감지 (래치 ON 후엔 재검사 skip) //
        if (!vizToolsNeeded && sessionRef.current?.buffers) {
          vizToolsNeeded = [...sessionRef.current.buffers.values()].some(b => b?.visualizationOnly === true);
        }

        let currentTools;
        if (sqlDataReady) {
          currentTools = TOOL_DEFINITIONS.filter(t => ['show_display', 'finish'].includes(t.function.name));
        }
        else if (isComparisonIncomplete) {
          currentTools = TOOL_DEFINITIONS.filter(t =>
            ['get_intentions', 'call_api', 'merge_slots', 'switch_slot', 'get_guide'].includes(t.function.name)
          );
        }
        else {
          // [LAZY TOOL-SET D1] viz show_*(show_display 제외)·recommend_sms는 래치 ON 시만 포함 — 나머지는 항상 //
          currentTools = TOOL_DEFINITIONS.filter(t => {
            const _tn = t.function.name;
            if (_tn === 'recommend_sms') return smsToolNeeded;
            if (_tn.startsWith('show_') && _tn !== 'show_display') return vizToolsNeeded;
            return true;
          });
        }

        const response = await loggedCompletion('tool_loop', {
          model: AGENT_MODEL,
          messages,
          tools: currentTools,
          tool_choice: "auto",
          ...buildTemperatureParam(AGENT_MODEL, 0),
          max_completion_tokens: MAX_OUTPUT_TOKENS,
        });

        const choice = response.choices[0];
        const assistantMessage = choice.message;

        // 로그
        // 토큰/raw는 시임1 콜렉터가 기록 → 턴 종료 /turn 일괄 적재 — 구 ai-log 개별 POST 폐기 (P2c) //

        // LLM 응답 로그
        log.push({
          type: 'llm_response',
          hasContent: !!assistantMessage.content,
          hasToolCalls: !!(assistantMessage.tool_calls?.length),
          toolCallCount: assistantMessage.tool_calls?.length || 0,
          contentPreview: assistantMessage.content?.slice(0, 100) || null,
          finishReason: choice.finish_reason,
          iteration,
        });

        // content가 있으면 처리
        if (assistantMessage.content) {
          if (TOOL_CALLING_LOG) {
            console.log('[Agent] 💬 GPT content:', assistantMessage.content.slice(0, 200));
          }

          if (!assistantMessage.tool_calls || assistantMessage.tool_calls.length === 0) {
            // 텍스트만 반환 (tool_calls 없음) //
            messages.push({ role: "assistant", content: assistantMessage.content });

            // SMS 키워드가 있는데 tool call 없이 응답한 경우 → 재시도 유도 (HOWTO 질문은 제외) //
            const smsKeywords = /문자|sms|SMS|추천.*보내|보내.*추천|뭐라고.*보내/i;
            const _isHowtoInput = /어떻게|방법|하는\s*법|사용법|설정하는\s*법/.test(userInput);

            if (iteration === 1 && calledTools.size === 0 && smsKeywords.test(userInput) && !_isHowtoInput) {
              if (TOOL_CALLING_LOG) console.log('[Agent] ⚠️ SMS request but no tool calls — forcing retry');
              messages.push({ role: "system", content: prompts.toolGuideSmsRetry });
              continue;
            }

            // show_revenue_chart가 호출됐으면 차트 렌더링 //
            if (revenueChartResult) {
              let chartText = _resolveVizText(assistantMessage.content || revenueChartResult.intro || '');
              // [FINANCE STATEMENT CAPTION] 재무제표 요청 + MONTHLY+TREND → 결정적 매출 요약으로 캡션 교체 (dual sync with nodeAgent.mjs)
              const _financeSession = sessionRef.current;
              if (/재무|제무|손익|재정/.test(userInput) && _financeSession.getBuffer?.()?.intention === 'CHECK_SALES+MONTHLY+TREND_STATISTICS_BY+PERIOD') {
                const _fc = buildFinanceSummaryIntro(_financeSession.getBuffer().summary, _financeSession.getBuffer().dateRange, _financeSession.getBuffer().items);
                if (_fc) chartText = _fc;
              }
              revenueChartResult.intro = chartText;
              clearToolLoading(aiMessageId);
              if (chartText) {
                await typeMessage(aiMessageId, chartText);
              }
              updateAiMessage(aiMessageId, chartText, { revenueChartData: revenueChartResult, isNewResponse: true });
              finalText = chartText;
              log.push({ type: 'final', responseType: 'revenueChart', year: revenueChartResult.year, model: AGENT_MODEL });
              break;
            }

            // show_sales_view가 호출됐으면 캘린더 렌더링 //
            if (salesViewResult) {
              const salesText = _resolveVizText(assistantMessage.content || salesViewResult.intro || '');
              salesViewResult.intro = salesText;
              clearToolLoading(aiMessageId);
              if (salesText) {
                await typeMessage(aiMessageId, salesText);
              }
              updateAiMessage(aiMessageId, salesText, { salesViewData: salesViewResult, isNewResponse: true });
              finalText = salesText;
              log.push({ type: 'final', responseType: 'salesView', weekCount: salesViewResult.weeksData?.length, model: AGENT_MODEL });
              break;
            }

            // [VIZ 6종 연결] 출석/랭킹/수업/연령/LTV/회원상태 차트 렌더 (단일 vizData) //
            if (vizResult) {
              const vizText = _resolveVizText(assistantMessage.content || vizResult.payload?.intro || '');
              if (vizResult.payload) vizResult.payload.intro = vizText;
              clearToolLoading(aiMessageId);
              if (vizText) {
                await typeMessage(aiMessageId, vizText);
              }
              updateAiMessage(aiMessageId, vizText, { vizData: vizResult, isNewResponse: true });
              finalText = vizText;
              log.push({ type: 'final', responseType: vizResult.type, model: AGENT_MODEL });
              break;
            }

            // [1-N P26 ext] GUARD 11 path 확장 — show_display 후 plain text outro 시 period count mismatch 검사 (finish() 우회 차단, 1회 한정)
            //   본질: LLM이 N월 enumeration 입력에 대해 부분 period만 call_api 호출 후 show_display + text-only stop으로 종료 시 finishPolicy 우회 → missing period 못 잡음.
            const _session = sessionRef.current;
            if (
              showDisplayResult &&
              assistantMessage.content &&
              !_session?._periodCountMismatchRetryAttempted &&
              _session?._periodCountMismatchMeta?.missingMonths?.length > 0
            ) {
              if (_session) _session._periodCountMismatchRetryAttempted = true;
              const _meta = _session._periodCountMismatchMeta;
              const _intentionHint = _meta.sourceIntention ? `intention="${_meta.sourceIntention}"` : 'the SAME intention used for the other periods';
              const _extraClause = _meta.extraMonths && _meta.extraMonths.length > 0
                ? ` Extra months loaded but NOT requested: [${_meta.extraMonths.join(', ')}] — these MUST be excluded from merge_slots slots and show_display columns.`
                : '';
              messages.push({ role: "system", content: `[PERIOD MONTH MISMATCH] User requested months [${_meta.inputMonths.join(', ')}] but call_api loaded [${_meta.calledMonths.join(', ')}]. Missing: [${_meta.missingMonths.join(', ')}].${_extraClause} (1) Call call_api(${_intentionHint}, startDate=<missing month start>, endDate=<missing month end>) for each missing month. (2) re-merge_slots using ONLY slots whose dateRange matches the requested months [${_meta.inputMonths.join(', ')}] — DROP any slot whose month is in the Extra list. (3) show_display columns must reference ONLY the requested months. Do NOT substitute adjacent calendar months for the user-named months.` });
              log.push({ type: 'finish_period_count_mismatch_retry', iteration, missingMonths: _meta.missingMonths, extraMonths: _meta.extraMonths || [], inputMonths: _meta.inputMonths, calledMonths: _meta.calledMonths, path: 'show_display_outro' });
              if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 PERIOD MONTH MISMATCH (show_display outro path): missing=${_meta.missingMonths.join(',')} extra=${(_meta.extraMonths||[]).join(',')} → retry nudge`);
              continue;
            }

            // [1-I α] GUARD 9 path 확장 — show_display 후 plain text outro 시 period count missing 검사 (finish() 우회 차단, 1회 한정)
            if (
              showDisplayResult &&
              assistantMessage.content &&
              !_session?._periodCountsMissingRetryAttempted &&
              _session?._periodCountHintMeta?.perPeriod?.length >= 2
            ) {
              const _text = assistantMessage.content;
              const _perPeriod = _session._periodCountHintMeta.perPeriod;
              const _missing = _perPeriod.filter(p => {
                const v = p?.count;
                if (typeof v !== 'number' || !Number.isFinite(v)) return false;
                return !_text.includes(String(v));
              });
              if (_missing.length > 0) {
                if (_session) _session._periodCountsMissingRetryAttempted = true;
                const _missingHint = _missing.map(p => `${p.periodLabel}=${p.count}${p.source ? ` (from ${p.source})` : ''}`).join(', ');
                const _allHint = _perPeriod.map(p => `${p.periodLabel}=${p.count}`).join(', ');
                messages.push({ role: "system", content: `[PERIOD COUNTS MISSING] Your outro mentioned only some period counts. Missing: ${_missingHint}. All periods: ${_allHint}. Rewrite the outro to mention EACH period count using direct numeric values (NOT placeholder like {{SUMMARY:...}}).` });
                log.push({ type: 'finish_period_counts_missing_retry', iteration, missing: _missing.map(p => ({ periodLabel: p.periodLabel, count: p.count, source: p.source })), path: 'show_display_outro' });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 PERIOD COUNTS MISSING (show_display outro path): missing=${_missing.length} → retry nudge`);
                continue;
              }
            }

            // show_display가 호출됐으면 display 렌더링 (기존 호환) //
            if (showDisplayResult) {
              await renderDisplay(showDisplayResult, assistantMessage.content);
              break;
            }

            // SMS 재추천 (같은 데이터, 새 문구) — 이전 display에 SMS 패널 교체 //
            if (calledTools.has('recommend_sms') && !calledTools.has('call_api') && pendingSmsRef.current) {
              const prevDisplayMsgId = chatMessagesRef.current.findLast(m => m.displayItems)?.id;

              if (prevDisplayMsgId) {
                updateAiMessage(prevDisplayMsgId, undefined, {
                  smsRecommendation: pendingSmsRef.current,
                });
                if (TOOL_CALLING_LOG) console.log('[Agent] 📱 SMS panel replaced on previous display:', prevDisplayMsgId);
              }

              pendingSmsRef.current = null;
            }

            // [STALE PERIOD RELABEL — text-only stop] 운영 5차 rid63 (zero-tool finish 경로, dual sync with nodeAgent.mjs)
            //   본질: LLM이 finish()/show_display 없이 text-only stop으로 직접 응답 시 stale period 버퍼를 새 기간으로 거짓 라벨
            //         (rid63 "2026년 기준" → 직전 다른 기간 총액 그대로 재라벨). show_display 가드 우회(tool 0회).
            //   해소: stop 직전 detectStalePeriodRelabel → block 시 system nudge로 call_api 재조회 유도 (1회 한정, show_display 가드와 _periodChangeRefetchBlocked 공유).
            if (sessionRef.current && sessionRef.current._periodChangeFinishBlockCount < 3 && !sessionRef.current._calledApiThisTurn) {
              const _relabelStop = detectStalePeriodRelabel({
                userInput: sessionRef.current.getUserInput?.() || '',
                buffer: sessionRef.current.getBuffer?.(),
                calledApiThisTurn: sessionRef.current._calledApiThisTurn,
              });
              if (_relabelStop?.block) {
                sessionRef.current._periodChangeFinishBlockCount++;
                const _rpS = _relabelStop.requestedPeriod, _bpS = _relabelStop.bufferPeriod;
                messages.push({ role: 'system', content: `[STALE PERIOD RELABEL — REFETCH REQUIRED] User requested a NEW period (resolved ${_rpS.startDate}~${_rpS.endDate}) but the active buffer (intention="${_relabelStop.intention}") holds data for a DIFFERENT period (${_bpS.startDate}~${_bpS.endDate}) and no call_api ran this turn. Answering now reuses the OLD-period numbers under a NEW-period label — a false relabel (the user notices and the contradiction surfaces). Re-call call_api(intention="${_relabelStop.intention}", startDate="${_rpS.startDate}", endDate="${_rpS.endDate}") to fetch the requested period BEFORE answering. Do NOT cite the existing buffer's totals for a different period.` });
                log.push({ type: 'finish_stale_period_relabel_retry', iteration, requested: `${_rpS.startDate}~${_rpS.endDate}`, buffer: `${_bpS.startDate}~${_bpS.endDate}`, path: 'text_only_stop' });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 STALE PERIOD RELABEL [text-only-stop]: requested ${_rpS.startDate}~${_rpS.endDate} vs buffer ${_bpS.startDate}~${_bpS.endDate}`);
                continue;
              }
            }

            // [1-K GUARD 10 ext] text-only stop path — finishPolicy.ENTITY_DETAIL_MISSING 발동 (finish 도구 호출 안 한 LLM도 catch, dual sync with nodeAgent.mjs)
            //   본질: LLM이 finish() 안 부르고 text-only stop으로 종료 시 finishPolicy 우회 → entity detail 누락 못 잡음
            //   해소: stop 직전 evaluateFinish 호출 → ENTITY_DETAIL_MISSING 발동 시 system nudge로 retry 유도 (1회 한정)
            //   언어 무관: GUARD 10 내부에서 response NAME value 검사로 entity 응답 mode 판정 — userInputClassification 의존 X
            if (!sessionRef.current?._entityDetailMissingRetryAttempted) {
              const _buf10 = sessionRef.current?.getBuffer?.() || null;
              const _decision10 = evaluateFinish({
                buffer: _buf10,
                rawAssistantText: stripPoliteReproposal(assistantMessage.content || ''),
                retryCount: { entityDetailMissingRetryAttempted: false },
                sessionFlags: { finishAllowedByGuard: false },
                userInputClassification: {},  // GUARD 10은 userInputClassification 의존 X
                detectors: { detectRawNumericLeak, detectIntroOutroNumericHallucination, detectUnfulfilledSpecifier },
                toolsCalledThisTurn: Array.from(calledTools),
              });
              if (_decision10.action === 'retry' && _decision10.reason === FINISH_REASONS.ENTITY_DETAIL_MISSING) {
                if (sessionRef.current) sessionRef.current._entityDetailMissingRetryAttempted = true;
                const _retryMsg10 = buildRetryMessage(_decision10);
                messages.push({ role: 'system', content: _retryMsg10 });
                log.push({ type: 'finish_entity_detail_missing_retry', iteration, mentioned: _decision10.retryGuide?.mentioned, total: _decision10.retryGuide?.total, path: 'text_only_stop' });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 GUARD 10 [text-only-stop]: mentioned=${_decision10.retryGuide?.mentioned}/${_decision10.retryGuide?.total}`);
                continue;
              }
            }

            // [1-L] 단일 row detail intention의 자연어 finish 허용 — nudge 강제 표 우회 차단 (dual sync with nodeAgent.mjs)
            //   본질: 단일 entity detail (count=1 + NAME field 보유)는 자연어 카드 응답이 user-facing 정답
            //   기존: nudge → finish_display_blocked → show_display 강제 → 표 1줄로 raw value/URL 노출 + UX degrade
            //   조건: count=1 + NAME-like field 보유 + 응답에 availableFields 한국어 label 3+ matching (자연어 detail 충분)
            //   효과: _finishAllowedByGuard set → consecutiveTextOnly++ 후 finish 처리 path (line ~1190) 즉시 통과
            if (!sessionRef.current?._finishAllowedByGuard) {
              const _bufL = sessionRef.current?.getBuffer?.();
              if (_bufL?.items?.length === 1 && _bufL.items[0] && typeof _bufL.items[0] === 'object') {
                const _NAME_FIELDS_L = ['storeName', 'name', 'memberName', 'memberShipName', 'membershipName', 'productName', 'itemName', 'lessonName', 'className', 'title'];
                const _NAME_LIKE_L = /name|title|label/i;
                const _sampleL = _bufL.items[0];
                const _hasName = Object.keys(_sampleL).some(k =>
                  (_NAME_FIELDS_L.includes(k) || _NAME_LIKE_L.test(k)) && _sampleL[k] && typeof _sampleL[k] === 'string'
                );
                if (_hasName && _bufL.availableFields && typeof _bufL.availableFields === 'object') {
                  const _labelsL = Object.values(_bufL.availableFields).filter(l => l && typeof l === 'string' && l.length >= 2);
                  const _textL = stripPoliteReproposal(assistantMessage.content || '');
                  const _labelMentionedL = _labelsL.filter(l => _textL.includes(l + ':') || _textL.includes(l + ' :')).length;
                  if (_labelMentionedL >= 3) {
                    if (sessionRef.current) sessionRef.current._finishAllowedByGuard = true;
                    log.push({ type: 'single_entity_natural_finish_allowed', iteration, items: 1, labelMentioned: _labelMentionedL, totalLabels: _labelsL.length, path: 'text_only_stop' });
                    if (TOOL_CALLING_LOG) console.log(`[Agent] ✓ [1-L] SINGLE-ENTITY natural finish allowed: count=1, labels matched=${_labelMentionedL}/${_labelsL.length}`);
                  }
                }
              }
            }

            // [L9b-CROSS-009 cycle 2] ENTITY NOT FOUND TEXT-ONLY FINISH ALLOW — dual sync with nodeAgent.mjs
            if (!sessionRef.current?._finishAllowedByGuard && sessionRef.current?._entityNotFoundContext) {
              const { missingName: _mNameLn } = sessionRef.current._entityNotFoundContext;
              let _textLn = (assistantMessage.content || '').trim();
              const _finishWrapperMatch = _textLn.match(/^\s*finish\s*\(\s*(\{[^]*\})\s*\)\s*$/);
              if (_finishWrapperMatch) {
                try {
                  const _parsed = JSON.parse(_finishWrapperMatch[1]);
                  if (_parsed?.response && typeof _parsed.response === 'string') {
                    _textLn = _parsed.response.trim();
                    if (TOOL_CALLING_LOG) console.log(`[Agent] 🧹 ENTITY NOT FOUND text-only: stripped finish() wrapper`);
                  }
                } catch (_e) { /* keep _textLn as-is */ }
              }
              const _hasNameAck = _textLn.includes(_mNameLn) &&
                /등록(?:되어\s*있지\s*않|되지\s*않|이?\s*없|이?\s*안\s*돼)|찾을\s*수\s*없|미등록|존재하지\s*않/.test(_textLn);
              if (_hasNameAck && sessionRef.current) {
                sessionRef.current._finishAllowedByGuard = true;
                sessionRef.current._finishGuardSuggestedText = _textLn;
                log.push({ type: 'entity_not_found_text_finish_allowed', iteration, missingName: _mNameLn, path: 'text_only_stop' });
                if (TOOL_CALLING_LOG) console.log(`[Agent] ✓ ENTITY NOT FOUND text-only finish allowed: "${_mNameLn}" ack detected in assistant content`);
              }
            }

            // [1-L-RAW] Raw datetime in single-entity text → override natural finish, nudge show_display
            if (sessionRef.current?._finishAllowedByGuard && !sessionRef.current?._rawDatetimeOverrideAttempted) {
              const _textRawDt = (assistantMessage.content || '').trim();
              const _hasRawDatetime = /\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:\.\d+)?/.test(_textRawDt);
              if (_hasRawDatetime) {
                if (sessionRef.current) {
                  sessionRef.current._finishAllowedByGuard = false;
                  sessionRef.current._rawDatetimeOverrideAttempted = true;
                }
                messages.push({ role: 'system', content: '[RAW VALUE DETECTED] Your text contains unformatted datetime values (ISO format). Use show_display to present this data — the display system formats dates and values automatically. Call show_display now with appropriate columns from availableFields.' });
                log.push({ type: 'single_entity_raw_datetime_override', iteration, path: 'text_only_stop' });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 [1-L-RAW] raw datetime detected in single-entity text → override natural finish, nudge show_display`);
                continue;
              }
            }

            // [1-L-LEAK] Payment/subscription/refund internal field leak in detail text → override natural finish, nudge show_display
            //   본질: [1-L]이 자연어 finish 허용했어도 응답에 결제/구독/환불/홀딩 internal field label N+ 등장 시 raw dump suspect → show_display 강제
            //   조건: _finishAllowedByGuard=true + INTERNAL_LEAK_LABELS registry 2+ matching (bullet/dash list mirroring 23-field dump 차단)
            //   효과: aiPrompts CHECK_MEMBER+MEMBERSHIP_DETAIL OUTRO RULE 보강 — prompt-level stochastic LLM 우회 시 deterministic enforcement
            //   참고: registry 패턴 (enum/keyword set), case-ID hardcode X. DETAIL category 무관 일반화.
            //   dual sync with nodeAgent.mjs
            if (sessionRef.current?._finishAllowedByGuard && !sessionRef.current?._paymentLeakOverrideAttempted) {
              const _textLk = (assistantMessage.content || '').trim();
              const INTERNAL_LEAK_LABELS = [
                '결제 식별값', '결제식별값', '거래번호',
                '구독 회원권 여부', '구독 상태', '구독 삭제 여부', '구독회원권', '구독상태', '구독삭제',
                '예상 회원권 여부', '예상회원권',
                '환불 여부', '환불여부', '환불 카드', '환불현금', '환불 현금', '환불카드',
                '판매 카드 금액', '판매카드', '판매 현금 금액', '판매현금', '판매 미수금', '판매미수금', '적립 포인트', '적립포인트',
                '홀딩 가능 횟수', '홀딩가능', '홀딩 횟수', '홀딩횟수',
                '선택된 결제수단', '결제수단 항목',
                '카드 결제액', '카드결제액', '현금 결제액', '현금결제액',
                'merchantUid', 'cardPrice', 'cashPrice', 'isSubscriptionMembership', 'isExpectedMemberShip', 'isRefund', 'salesCardPrice', 'salesCashPrice', 'notReceivedPrice', 'availableHold', 'holdCount',
              ];
              let _leakHits = 0;
              const _matchedLabels = [];
              for (const _lab of INTERNAL_LEAK_LABELS) {
                if (_textLk.includes(_lab)) {
                  _leakHits += 1;
                  _matchedLabels.push(_lab);
                  if (_leakHits >= 2) break;
                }
              }
              if (_leakHits >= 2) {
                if (sessionRef.current) {
                  sessionRef.current._finishAllowedByGuard = false;
                  sessionRef.current._paymentLeakOverrideAttempted = true;
                }
                messages.push({ role: 'system', content: `[INTERNAL FIELD LEAK DETECTED] Your text enumerates ${_leakHits}+ backend internal fields (matched: ${_matchedLabels.join(', ')}). These are payment/subscription/refund/hold internals — NEVER user-facing. MUST call show_display now to present membership detail as a table — table auto-filters internal fields. Outro may state ONE high-level fact only (active membership name + 남은일수). Do NOT enumerate raw field values as a bullet/dash list.` });
                log.push({ type: 'single_entity_internal_field_leak_override', iteration, leakHits: _leakHits, matchedLabels: _matchedLabels, path: 'text_only_stop' });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 [1-L-LEAK] internal field leak detected (hits=${_leakHits}, labels=[${_matchedLabels.join(', ')}]) → override natural finish, nudge show_display`);
                continue;
              }
            }

            // [1-L-LIST] Structured listing form in single-entity text → override natural finish, nudge show_display (dual sync with nodeAgent.mjs)
            //   본질: [1-L]이 자연어 finish 허용했어도 응답이 "Label1: Value1\nLabel2: Value2\nLabel3: Value3" 다줄 listing form
            //         이면 표 데이터를 텍스트로 표현한 것 → 표(show_display)가 가독성 더 높음. F2 directive.
            //   조건: _finishAllowedByGuard=true + buffer.items.length===1 (single entity)
            //         + 응답에 newline 구분 "label: value" 줄 3+ (structured tabular listing form)
            //   효과: 단일 detail intention의 show_display 미호출 stochastic (~50%) → deterministic show_display
            //   generic — case-ID/intention name hardcode X. label은 generic regex (한글/영문 word + colon + 비공백) — registry 의존 X.
            //   per-turn 1x cap via _listingFormOverrideAttempted (다른 [1-L-*] override와 동일 패턴)
            if (sessionRef.current?._finishAllowedByGuard && !sessionRef.current?._listingFormOverrideAttempted) {
              const _bufLF = sessionRef.current?.getBuffer?.();
              const _isSingleEntity = _bufLF?.items?.length === 1;
              // [B-4 과적합 수술] 사용자가 명시적으로 말/텍스트 설명을 요청한 턴은 표 강제 제외 (dual sync with nodeAgent.mjs)
              const _prosePreferredLF = /말로|글로|텍스트로|표\s*(말고|없이|빼고)|풀어서/.test(sessionRef.current?.getUserInput?.() || '');
              if (_isSingleEntity && !_prosePreferredLF) {
                const _textLF = (assistantMessage.content || '').trim();
                const _LISTING_LINE_RE = /(^|\n)\s*[가-힣A-Za-z][가-힣A-Za-z0-9 ]*\s*:\s*\S/g;
                const _listingHits = (_textLF.match(_LISTING_LINE_RE) || []).length;
                if (_listingHits >= 3) {
                  if (sessionRef.current) {
                    sessionRef.current._finishAllowedByGuard = false;
                    sessionRef.current._listingFormOverrideAttempted = true;
                  }
                  messages.push({ role: 'system', content: `[STRUCTURED LISTING FORM DETECTED] Your text presents single-entity detail as ${_listingHits}+ "Label: Value" lines — that is tabular data rendered as text and reduces readability. MUST call show_display now to present the single record as a single-row table — the table system formats values cleanly. Outro after the table may state ONE concise summary line; do NOT enumerate fields again as a vertical listing.` });
                  log.push({ type: 'single_entity_listing_form_override', iteration, listingHits: _listingHits, path: 'text_only_stop' });
                  if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 [1-L-LIST] structured listing form detected (lines=${_listingHits}) → override natural finish, nudge show_display`);
                  continue;
                }
              }
            }

            // [HOWTO GUARD] HOWTO intent detection — require get_guide before allowing finish
            // If HOWTO keywords detected and get_guide was already called, allow immediate finish.
            // If HOWTO keywords detected but get_guide NOT called, nudge to call get_guide first (retrieve actual guide content).
            // EXCEPTION: context explanation about a displayed metric (buffer has data + user asks "X 어떻게 계산됨?") → FOLLOW-UP, not HOWTO.
            if (!sessionRef.current?._finishAllowedByGuard) {
              const _howtoKw = /어떻게|방법|하는\s*법|사용법|설정하는\s*법|처리|계산법/;
              const _inputText = userInput || '';
              const _responseText = (assistantMessage.content || '').trim();

              // Context explanation detection: HOWTO keyword + buffer has data → metric explanation, not platform HOWTO //
              // Pattern: "출석률 어떻게 계산됨?" / "매출 어떻게 산출?" / "이거 뭐 기준?" — user asks about a metric in current data context
              // L34-CONTEXT-001 cycle 1 (2026-05-31, dual sync with nodeAgent.mjs): expanded to cover metadata FOLLOW-UP about loaded buffer's period/scope/criteria.
              //   Added patterns: (a) period/scope metadata nouns (산정기간/집계 기간/기준 기간/통계 기간/조회 기간/기간이/범위가/날짜가/조건이)
              //                   (b) lenient "어떻게" forms — "어떻게돼/어떻게 돼/어떻게됨/어떻게 됨/어떻게 되는/어떻게 되" (covers "산정기간이 어떻게됨?" without space)
              //                   (c) "기준" used as standalone query about loaded data
              //   Trigger condition unchanged: requires buffer data present → only re-classifies HOWTO→FOLLOW-UP when there IS prior data context.
              const _contextExplainKw = /어떻게\s*계산|어떻게\s*산출|어떻게\s*산정|계산\s*방식|산출\s*방식|뭐\s*기준|기준이?\s*뭐|무슨\s*뜻|뭘\s*의미|계산됨|산출됨|산정됨|어떻게\s*돼|어떻게돼|어떻게\s*됨|어떻게됨|어떻게\s*되는|어떻게\s*되\?|산정\s*기간|산정기간|집계\s*기간|기준\s*기간|통계\s*기간|조회\s*기간|기간이?\s*어떻|범위가?\s*어떻|날짜가?\s*어떻|조건이?\s*어떻/;
              const _hasBufferDataForContext = [...(sessionRef.current?.buffers || new Map()).entries()].some(([, b]) => (b.items?.length || 0) >= 1);
              const _isContextExplanation = _contextExplainKw.test(_inputText) && _hasBufferDataForContext;

              // [ADVICE FOLLOW-UP] 전략/조언 자문(rid100/101)은 platform HOWTO 가이드가 아니라 LLM 자체 자문이 정답.
              //   get_guide nudge가 자문을 "공식 가이드 먼저 확인해야 해" 디플렉션으로 유도 → 자문 후속이면 nudge skip + 직접 finish 허용 (dual sync with nodeAgent.mjs).
              const _isAdviceFollowupHG = sessionRef.current?._isAdviceFollowupIntent?.(_inputText);
              if (_howtoKw.test(_inputText) && _responseText.length >= 30 && !_isContextExplanation && !_isAdviceFollowupHG) {
                if (calledTools.has('get_guide')) {
                  if (sessionRef.current) sessionRef.current._finishAllowedByGuard = true;
                  log.push({ type: 'howto_guide_finish_allowed', iteration, inputLength: _inputText.length, responseLength: _responseText.length, getGuideCalled: true });
                  if (TOOL_CALLING_LOG) console.log(`[Agent] [HOWTO GUARD] HOWTO input + get_guide already called + substantial text (${_responseText.length} chars) → finish allowed`);
                }
                else {
                  log.push({ type: 'howto_guide_nudge', iteration, inputLength: _inputText.length, responseLength: _responseText.length });
                  if (TOOL_CALLING_LOG) console.log(`[Agent] [HOWTO GUARD] HOWTO input detected but get_guide NOT called → nudge to call get_guide`);
                  showToolLoading(aiMessageId, userInput);
                  messages.push({ role: 'system', content: `[HOWTO — get_guide REQUIRED] Your response is a HOWTO answer but you did not call get_guide first. The platform has specific step-by-step guides. MUST call get_guide(query: "${_inputText.slice(0, 60)}") to retrieve the official guide content, then finish with that content. Do NOT skip get_guide — your vague answer lacks the concrete steps the user needs.` });
                  continue;
                }
              }
              else if (_isContextExplanation || _isAdviceFollowupHG) {
                if (sessionRef.current) sessionRef.current._finishAllowedByGuard = true;
                log.push({ type: _isAdviceFollowupHG ? 'advice_followup_finish_allowed' : 'context_explanation_finish_allowed', iteration, inputLength: _inputText.length, responseLength: _responseText.length });
                if (TOOL_CALLING_LOG) console.log(`[Agent] [HOWTO GUARD] ${_isAdviceFollowupHG ? 'Advice follow-up' : 'Context explanation'} detected → finish allowed, skip get_guide nudge`);
              }
            }

            // [TEXT-ONLY SUMMARY PLACEHOLDER] text-only 응답에 {{SUMMARY:...}} 포함 시 자동 치환 후 finish 처리
            // LLM이 finish 도구 대신 text-only stop으로 응답했지만 placeholder 사용한 경우 — substitute path 적용
            const _textContent = (assistantMessage.content || '').trim();
            // [COMPARISON FOLLOW-UP — PLACEHOLDER PATH BLOCK] #585-FOLLOWUP-C L9c-COMPARE-006 cycle 1 (2026-05-29 dual sync with nodeAgent.mjs)
            //   본질: 비교 입력(비교/대비/대조/차이)인데 LLM이 {{SUMMARY:...}} placeholder text로 finish 시도 → 실제 비교 표 미렌더링
            //   해소: placeholder 치환 path 진입 전에 차단 — show_display 강제 nudge 1회 (1회 한정)
            const _COMPARE_KW_PH = /비교|대비|대조|비슷|차이/;
            const _inputRawForPh = (userInput || '').trim();
            const _sessionPh = sessionRef.current;
            const _showDisplayInTurnForPh = calledTools.has('show_display');
            const _activeBufForPh = _sessionPh?.getActiveBuf?.() || (_sessionPh?.buffers ? [..._sessionPh.buffers.values()].find(b => (b.items?.length || 0) >= 1) : null);
            const _bufHasItemsForPh = !!(_activeBufForPh && (_activeBufForPh.items?.length || 0) >= 1);
            if (_sessionPh && !_sessionPh._comparisonShowDisplayNudged
                && _COMPARE_KW_PH.test(_inputRawForPh)
                && _bufHasItemsForPh
                && !_showDisplayInTurnForPh) {
              const _bufCountPh = _activeBufForPh.items?.length || 0;
              const _calledListPh = Array.from(calledTools).join(', ') || '(none)';
              messages.push({ role: 'assistant', content: assistantMessage.content });
              messages.push({ role: 'system', content: `[COMPARISON FOLLOW-UP — SHOW-DISPLAY REQUIRED] User said "${_inputRawForPh}" (contains comparison keyword 비교/대비/대조/차이). Buffer has loaded items (count=${_bufCountPh}). Tools called so far this turn: [${_calledListPh}] — show_display was NOT called. Your text-only summary placeholder is INSUFFICIENT for a comparison request — the user wants the comparison rendered as a multi-column table, not a one-line summary. MUST call show_display() now to render the merged/sorted buffer as a comparison table. Do NOT respond with "비교했어"/"정리했어"/{{SUMMARY:...}}/etc — that is a meta acknowledgment, not the answer.` });
              _sessionPh._comparisonShowDisplayNudged = true;
              log.push({ type: 'comparison_show_display_nudge', iteration, bufCount: _bufCountPh, calledTools: Array.from(calledTools), path: 'placeholder_block' });
              if (TOOL_CALLING_LOG) console.log(`[Agent] 🔁 [COMPARISON FOLLOW-UP — PLACEHOLDER BLOCK] bufCount=${_bufCountPh}, calledTools=[${_calledListPh}]`);
              continue;
            }

            // [LIST INTENT — TEXT-ONLY STOP BLOCK] list 의도 + 이번 turn 데이터 적재(multi-row) + show_display 미호출인데
            // LLM이 tool 없이 text-only stop으로 종료 — finishPolicy(GUARD 1)가 finish 도구 경로만 검사해 우회되는 구멍.
            // (DEMO-37: "정리해줘"에 표 없이 "수업별로 정리돼 있고..." 빈말 종료). 비교 가드와 동형 — nudge 1회 한정.
            // dual sync with nodeAgent.mjs //
            {
              const _LIST_KW_TS = /보여줘|보여주세요|알려줘|알려주세요|확인해|확인 ?해줘|정리 ?해|목록|리스트|명단|현황|내역/;
              const _multiRowBufTS = _sessionPh?.buffers ? [..._sessionPh.buffers.values()].find(b => (b.items?.length || 0) >= 2) : null;
              if (_sessionPh && !_sessionPh._listIntentShowDisplayNudged
                  && _LIST_KW_TS.test(_inputRawForPh)
                  && calledTools.has('call_api')
                  && !_showDisplayInTurnForPh
                  && _multiRowBufTS) {
                messages.push({ role: 'assistant', content: assistantMessage.content });
                messages.push({ role: 'system', content: `[LIST INTENT — SHOW-DISPLAY REQUIRED] User asked for a list/table (Korean list keyword detected in "${_inputRawForPh.slice(0, 40)}"). This turn loaded data via call_api (a slot holds ${_multiRowBufTS.items.length} rows) but show_display was NEVER called — your text-only response leaves the user without the table they asked for. MUST call show_display() now on the slot holding the multi-row data (switch_slot first if it is not active). Do NOT end with a prose-only summary like "정리돼 있고/정리했어" — that is a meta acknowledgment, not the answer.` });
                _sessionPh._listIntentShowDisplayNudged = true;
                log.push({ type: 'list_intent_show_display_nudge', iteration, bufCount: _multiRowBufTS.items.length, calledTools: Array.from(calledTools) });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🔁 [LIST INTENT — TEXT-ONLY STOP BLOCK] bufCount=${_multiRowBufTS.items.length}`);
                continue;
              }
            }
            // [PLACEHOLDER LEAK — TEXT-ONLY FINISH] LLM이 text-only 종료 시 placeholder({{COUNT}}/{{SUM}}/{{SUMMARY:...}} 등) 사용 →
            //   show_display/finish() 경로처럼 buffer 정답으로 치환 (구 SUMMARY-only 게이트가 {{COUNT}} 등을 누출시키던 구멍 차단).
            //   가드레일: (1) topN/cap wrapping (finish() 경로 미러) (2) 미해결/빈 결과 시 guard suggested → strip → RESPONSE_FAILED_RETRY.
            //   raw placeholder·빈 응답 절대 미노출. dual sync with nodeAgent.mjs //
            if (/\{\{[^}]+\}\}/.test(_textContent)) {
              const _bufRawPh = sessionRef.current?.getActiveBuf?.() || [...(sessionRef.current?.buffers?.values() || [])].find(b => b.summary || b.items?.length);
              const _userInputPh = sessionRef.current?.getUserInput?.() || '';
              const _topNPh = detectExplicitTopNIntent(_userInputPh);
              let _bufForPh = _bufRawPh;

              if (_bufRawPh?.items?.length && _topNPh) {
                const _dispPh = Math.min(_bufRawPh.items.length, _topNPh.limit);

                if (_bufRawPh.items.length > _dispPh) {
                  _bufForPh = { ..._bufRawPh, _displayedCount: _dispPh, _userExplicitTopNSatisfied: true, _scopeTotal: _bufRawPh.items.length };
                }
              }

              // [BARE-COUNT AMBIGUITY backstop] 멀티슬롯 divergent + text-only turn의 bare {{COUNT}}는 marker 보존 → 하단 fallback이 처리 (top-N turn skip)
              const _bcMetaTO = _topNPh ? null : (sessionRef.current?.getBareCountAmbiguity?.() || null);
              let _resolvedPh = _bufForPh
                ? (substituteOutroPlaceholders(_textContent, _bufForPh, sessionRef.current?.getSlotSummaryMap?.(), _bcMetaTO ? { bareCountAmbiguous: true } : undefined).text ?? _textContent)
                : _textContent;

              // 미해결 placeholder 잔존 or 빈 결과 → 결정적 fallback (raw placeholder·빈 응답 금지) //
              if (/\{\{[^}]+\}\}/.test(_resolvedPh) || !_resolvedPh.trim()) {
                const _suggestedPh = sessionRef.current?._finishGuardSuggestedText;

                if (_suggestedPh && _suggestedPh.trim()) _resolvedPh = _suggestedPh;
                else {
                  const _strippedPh = stripPlaceholderArtifacts(_resolvedPh);
                  _resolvedPh = _strippedPh.trim() ? _strippedPh : RESPONSE_FAILED_RETRY;
                }
              }

              finalText = stripAssistantHarnessLeak(stripRawIsoDatetime(_resolvedPh));
              if (!finalText.trim()) finalText = RESPONSE_FAILED_RETRY;
              await typeMessage(aiMessageId, finalText);
              log.push({ type: 'finish', response: finalText, hasDisplay: false, displayCount: null, iteration, placeholderUsed: true, bufferHadChangeFields: false });
              log.push({ type: 'final', responseType: 'text', content: finalText, model: AGENT_MODEL, reason: 'text_only_placeholder_resolved' });
              if (TOOL_CALLING_LOG) console.log(`[Agent] TEXT-ONLY PLACEHOLDER resolved: "${finalText.slice(0, 60)}"`);
              break;
            }

            // finish 없이 텍스트만 → 넛지 후 continue (안전장치: N회 연속이면 강제 종료) //
            // 가드가 finish 허용한 상태면(예: COUNT QUESTION, ALL EMPTY) 즉시 finish로 인정 //
            consecutiveTextOnly++;
            const finishAllowedByGuard = !!sessionRef.current?._finishAllowedByGuard;

            if (consecutiveTextOnly >= MAX_TEXT_ONLY || finishAllowedByGuard) {
              if (TOOL_CALLING_LOG) console.log(`[Agent] ⚠️ 텍스트 응답 finish 처리 (textCount=${consecutiveTextOnly}, guardAllowed=${finishAllowedByGuard})`);
              // [SHORT PLACEHOLDER GUARD] 가드 허용 없이 MAX 도달 + 짧은 placeholder ("확인해볼게요!" 등) → RESPONSE_FAILED_RETRY로 대체
              const trimmed = (assistantMessage.content || '').trim();
              const isShortPlaceholder = !finishAllowedByGuard && trimmed.length < 30 && /^(확인해볼게요|잠시만요|알려드릴게요|확인할게요|확인 해볼게요|네)[\s!.…\n]*$/.test(trimmed);
              if (isShortPlaceholder) {
                finalText = RESPONSE_FAILED_RETRY;
                await typeMessage(aiMessageId, finalText);
                log.push({ type: 'final', responseType: 'text', content: finalText, model: AGENT_MODEL, reason: 'short_placeholder_guard' });
                if (TOOL_CALLING_LOG) console.log(`[Agent] ⚠️ SHORT PLACEHOLDER GUARD: "${trimmed}" → RESPONSE_FAILED_RETRY`);
                break;
              }
              // [F9 LLM_HEDGE] LLM이 viz 후 substantive 응답을 직접 생성했으면 그것이 답 — suggestedText(viz intro
              //   pre-viz framing)는 LLM 응답 미생성(empty) 시에만 안전망. 구조적 판정(empty 여부)만 — KPI 키워드/숫자 regex X.
              //   dual sync with nodeAgent.mjs.
              if (finishAllowedByGuard && sessionRef.current?._finishGuardSuggestedText && !trimmed) {
                finalText = stripRawIsoDatetime(sessionRef.current._finishGuardSuggestedText);
                await typeMessage(aiMessageId, finalText);
                log.push({ type: 'final', responseType: 'text', content: finalText, model: AGENT_MODEL, reason: 'guard_suggested_text' });
              }
              else {
                finalText = stripAssistantHarnessLeak(stripRawIsoDatetime(assistantMessage.content));
                await typeMessage(aiMessageId, finalText);
                log.push({ type: 'final', responseType: 'text', content: finalText, model: AGENT_MODEL });
              }
              break;
            }

            if (TOOL_CALLING_LOG) console.log(`[Agent] 💭 텍스트만 반환 (${consecutiveTextOnly}/${MAX_TEXT_ONLY}) — 넛지 후 continue`);
            log.push({ type: 'nudge', consecutiveTextOnly, maxTextOnly: MAX_TEXT_ONLY, iteration });
            showToolLoading(aiMessageId, userInput);

            // 직전 도구 실패 여부 확인 — 실패 후에는 포기 방지 넛지 //
            const lastToolFailed = messages.slice(-5).some(m => m.role === 'tool' && /"success"\s*:\s*false/.test(m.content));

            if (lastToolFailed) {
              messages.push({ role: "system", content: "A tool just failed. Do NOT finish or give up — fix the issue and retry. If show_display failed, call get_intentions → call_api to load data first. If call_api failed, check the error and retry with correct params." });
              if (TOOL_CALLING_LOG) console.log('[Agent] 💭 Tool failure detected — retry nudge injected');
            }
            else {
              // [1-E γ ext] text-only path는 finishPolicy 우회 — zeroCountAlternative 안내 nudge에 inject
              let nudgeMsg = resolvePrompt('TEXT_ONLY_NUDGE', '');
              if (_session?._zeroCountAlternativeMeta?.alternatives?.length > 0 && !_session?._zeroCountAlternativeRetryAttempted) {
                const meta = _session._zeroCountAlternativeMeta;
                nudgeMsg += `\n\n[ZERO COUNT ALTERNATIVE UNTRIED] Intention "${meta.intention}" returned 0 items in ${meta.count} consecutive calls. Same/cross-category alternatives [${meta.alternatives.join(', ')}] not tried. User's request may match a different scope. Call_api one of the alternatives FIRST to verify data exists.`;
                if (_session) _session._zeroCountAlternativeRetryAttempted = true;
              }
              // [SHORT AGREEMENT NUDGE] dual sync with nodeAgent.mjs — 짧은 동의 + buffer items 있을 때 강제 sort/filter directive
              const _shortAgreementRe = /^(네|응|좋아|좋아요|부탁|부탁해|부탁해요|해줘|해주세요|고고|진행|그래|오케이|ok|okay)[\s!.…?\n]*$/i;
              const _inputTextRaw = (userInput || '').trim();
              const _hasBufferItems = _session?.buffers ? [..._session.buffers.entries()].some(([, b]) => (b.items?.length || 0) >= 1) : false;
              if (_shortAgreementRe.test(_inputTextRaw) && _hasBufferItems) {
                const _activeBuf = _session.getActiveBuf?.() || [..._session.buffers.values()].find(b => b.items?.length > 0);
                const _availableFieldKeys = _activeBuf?.availableFields ? Object.keys(_activeBuf.availableFields) : [];
                const _fieldHint = _availableFieldKeys.length > 0 ? `available fields: [${_availableFieldKeys.slice(0, 20).join(', ')}]` : '';
                nudgeMsg += `\n\n[SHORT AGREEMENT — ACTION REQUIRED] User said "${_inputTextRaw}" (short agreement). Buffer already has loaded items. TEXT-ONLY REPLY IS FORBIDDEN. You MUST call a tool to refine the buffer NOW: sort/filter/derive/recommend_sms. ${_fieldHint}. Pick the most useful refinement (e.g., sort by expiry-approaching date asc for membership; sort by attendance rate asc; filter by gender/status; recommend SMS to expiring members). Do NOT respond with "알겠어"/"네!"/etc — call a tool.`;
                smsToolNeeded = true;  // [LAZY TOOL-SET D1] nudge가 recommend_sms를 권함 — 도구 부재 데드락 차단
              }
              // [SHORT AGREEMENT ON EMPTY PREV NUDGE] L17-AGREE-GOGO-001 cycle 1 (2026-05-30 dual sync with nodeAgent.mjs)
              //   본질: T0 결과 0건 + T1 짧은 동의 + LLM이 text-only로 "어떤 정보 원하시는지 확인이 어려워요" 등 generic 응답 → 사용자 입장 의미 없음
              //   해소: previous turn이 0건 결과였다는 컨텍스트를 nudge로 inject → finish() 적절한 안내 유도
              const _bufHasIntentionButNoItems_TX = !_hasBufferItems && (_session?.buffers
                ? [..._session.buffers.values()].some(b => !!b.intention && (b.items?.length || 0) === 0)
                : false);
              if (_shortAgreementRe.test(_inputTextRaw) && _bufHasIntentionButNoItems_TX) {
                const _prevBuf_TX = [..._session.buffers.values()].find(b => !!b.intention && (b.items?.length || 0) === 0);
                const _prevIntention_TX = _prevBuf_TX?.intention || 'previous';
                nudgeMsg += `\n\n[SHORT AGREEMENT — EMPTY PREVIOUS RESULT] User said "${_inputTextRaw}" (short agreement) but the previous query (intention=${_prevIntention_TX}) returned 0 items. There is no data to refine, sort, or recommend SMS for. Call finish() with a concise Korean message that explains there is no data and suggests trying a different category/period/condition. Example: finish("이전 검색 결과가 0건이라 추천할 데이터가 없어요. 다른 기간이나 조건으로 다시 시도해 주세요."). Do NOT give a generic "어떤 정보를 원하시는지 모르겠어요" reply — the user agreed to a previous suggestion that produced 0 results.`;
              }
              // [PERIOD-RESTRICTION FOLLOW-UP — RE-DISPLAY REQUIRED] L9a-MEMBER-003 cycle 1 (2026-05-29 dual sync with nodeAgent.mjs)
              //   본질: 사용자가 시간 키워드(이번달/지난주/N월 등) + 한정 입력으로 follow-up하는데 LLM이 text-only로 "이미 X 기준으로 보고 있어요" / "다시 볼게요" / "어떤 항목 보고 싶으신가요" 같은 meta acknowledgment 또는 역질문만 반환 → list view 누락 + 컨텍스트 단절 → T2 cascading 실패 (intention drift to MEMBER+ALIVE/EXPIRED)
              //   해소: 직전 turn buffer dateRange 존재 + (items 보유 OR items 0건이지만 intention 존재) + user input에 TIME_KW 포함 + (T0가 아닌) text-only stop → show_display(active buffer) 또는 call_api(updated dateRange) 강제 nudge
              //   패턴 기반 (TIME_KW = AgentSession.js line 700와 동일). 단일 case hardcode X — 모든 period-restriction follow-up에 범용 적용
              //   2026-05-30 L9a-MEMBERSHIP-002 cycle 1 확장: items=0 (prev 0건) 케이스도 catch — buffer의 intention+dateRange만으로 컨텍스트 유지 가능. EMPTY PREV BUFFER variant 추가.
              const _PERIOD_TIME_KW = /오늘|어제|내일|지난주|이번주|다음주|지난달|이번달|다음달|\d+\s*월|\d+\s*일|\d{4}-\d{2}-\d{2}|최근|지난\s*\d+|\d+일\s*전|작년|올해|금주|금월/;
              const _activeBufForPeriod = _session?.getActiveBuf?.()
                || (_session?.buffers ? [..._session.buffers.values()].find(b => (b.items?.length || 0) >= 1) : null)
                || (_session?.buffers ? [..._session.buffers.values()].find(b => !!b.intention && !!b.dateRange?.startDate) : null);
              const _bufHasItemsForPeriod = !!(_activeBufForPeriod && (_activeBufForPeriod.items?.length || 0) >= 1 && _activeBufForPeriod.dateRange?.startDate);
              const _bufEmptyButHasContext = !!(_activeBufForPeriod && (_activeBufForPeriod.items?.length || 0) === 0 && !!_activeBufForPeriod.intention && _activeBufForPeriod.dateRange?.startDate);
              const _hasBufferWithDateRange = _bufHasItemsForPeriod || _bufEmptyButHasContext;
              if (_session && !_session._periodRestrictionRedisplayNudged
                  && !_shortAgreementRe.test(_inputTextRaw)
                  && _PERIOD_TIME_KW.test(_inputTextRaw)
                  && _hasBufferWithDateRange) {
                const _dr = _activeBufForPeriod.dateRange;
                const _bufIntention = _activeBufForPeriod.intention || 'active';
                const _bufCount = _activeBufForPeriod.items?.length || 0;
                if (_bufEmptyButHasContext) {
                  nudgeMsg += `\n\n[PERIOD-RESTRICTION FOLLOW-UP — EMPTY PREV BUFFER] User said "${_inputTextRaw}" (contains time keyword). Previous query (intention=${_bufIntention}, dateRange=${_dr.startDate}~${_dr.endDate}) returned 0 items. CONTEXT MUST BE MAINTAINED across turns. TEXT-ONLY REPLY IS FORBIDDEN. NEVER say "이번달 기준으로 다시 볼게요"/"이번달 기준으로 확인해볼게요"/"어떤 항목 보고 싶으신가요"/"이미 X 기준으로 보고 있어요" — those are vague meta-acks or reverse questions that LOSE CONTEXT for the next turn. MANDATORY ACTION: call_api with EXACTLY intention="${_bufIntention}" and startDate/endDate matching the user's requested period (resolve "${_inputTextRaw}" to concrete start/end dates). This RE-VERIFIES the data for the new period and KEEPS the intention context active so subsequent turns ("문자 보내줘", "몇 명이야?", etc.) can reference the SAME list/intent. Do NOT switch intention. Do NOT ask the user to clarify — the prev intention + new period is unambiguous. EXCEPTION: if the current input explicitly names a DIFFERENT target than the previous query (새 대상 명사 — 예: 직전이 구독 해지인데 "만료 회원은?"), treat it as a NEW request and route via get_intentions to the intention matching the newly named target instead.`;
                }
                else {
                  nudgeMsg += `\n\n[PERIOD-RESTRICTION FOLLOW-UP — RE-DISPLAY REQUIRED] User said "${_inputTextRaw}" (contains time keyword). Buffer has loaded items (intention=${_bufIntention}, count=${_bufCount}, dateRange=${_dr.startDate}~${_dr.endDate}). TEXT-ONLY REPLY IS FORBIDDEN for list intents. Decision: (a) If buffer's dateRange already covers user's requested period (e.g., user says "이번달만"/"이번 달 기준" and buffer dateRange already spans the current calendar month), call show_display on the active buffer to RE-RENDER the existing list (no new call_api needed). (b) If user's period is different from buffer's dateRange (e.g., user says "4월만" but buffer covers May), call_api with updated startDate/endDate. Do NOT respond with "이미 X 기준으로 보고 있어요"/"X 기준으로 확인 중이에요"/etc — that is a meta acknowledgment, not an answer. The user wants the list displayed.`;
                }
                _session._periodRestrictionRedisplayNudged = true;
              }
              // [COMPARISON FOLLOW-UP — SHOW-DISPLAY REQUIRED] #585-FOLLOWUP-C L9c-COMPARE-006 cycle 1 (2026-05-29 dual sync with nodeAgent.mjs)
              //   본질: 사용자가 비교 키워드(비교/대비/대조) 포함 입력으로 (재)요청하는데 LLM이 switch_slot/sort 같은 buffer-관리 도구만 호출하고 show_display 없이 text-only stop으로 종료
              //         → "1월 기준으로 다시 비교했어." 같은 메타 응답만 남고 실제 표 미렌더링 → 사용자 입장 답변 미충족
              //   해소: 본 turn에 show_display 미호출 + buffer 데이터 보유 + 입력에 COMPARE_KW 포함 + text-only stop → show_display 강제 nudge (1회 한정)
              //   패턴 기반: structural Korean keyword regex (단일 case "비교해줘" hardcode X). PERIOD guard와 독립 발동 — 비교 입력은 시간 키워드 없어도 catch
              const _COMPARE_KW = /비교|대비|대조|비슷|차이/;
              const _showDisplayCalledThisTurn = calledTools.has('show_display');
              const _activeBufForCompare = _session?.getActiveBuf?.() || (_session?.buffers ? [..._session.buffers.values()].find(b => (b.items?.length || 0) >= 1) : null);
              const _bufHasItemsForCompare = !!(_activeBufForCompare && (_activeBufForCompare.items?.length || 0) >= 1);
              if (_session && !_session._comparisonShowDisplayNudged
                  && !_shortAgreementRe.test(_inputTextRaw)
                  && _COMPARE_KW.test(_inputTextRaw)
                  && _bufHasItemsForCompare
                  && !_showDisplayCalledThisTurn) {
                const _bufIntention = _activeBufForCompare.intention || 'active';
                const _bufCount = _activeBufForCompare.items?.length || 0;
                const _calledList = Array.from(calledTools).join(', ') || '(none)';
                nudgeMsg += `\n\n[COMPARISON FOLLOW-UP — SHOW-DISPLAY REQUIRED] User said "${_inputTextRaw}" (contains comparison keyword 비교/대비/대조/차이). Buffer has loaded items (intention=${_bufIntention}, count=${_bufCount}). Tools called so far this turn: [${_calledList}] — show_display was NOT called. TEXT-ONLY REPLY IS FORBIDDEN for comparison intents. The user wants the comparison rendered as a table, not a one-line summary. MUST call show_display() now to render the merged/sorted buffer as a comparison table. Do NOT respond with "비교했어"/"정리했어"/etc — that is a meta acknowledgment, not the answer. The user wants the comparison table displayed.`;
                _session._comparisonShowDisplayNudged = true;
                log.push({ type: 'comparison_show_display_nudge', iteration, bufCount: _bufCount, calledTools: Array.from(calledTools) });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🔁 [COMPARISON FOLLOW-UP] show_display nudge: bufCount=${_bufCount}, calledTools=[${_calledList}]`);
              }
              messages.push({ role: "system", content: nudgeMsg });
            }
            continue;
          }
          else {
            // 로딩 문구 (tool call 진행 중) → shimmer 애니메이션
            showToolLoading(aiMessageId, userInput);
            log.push({ type: 'loading', content: assistantMessage.content });
          }
        }

        // tool_calls가 없으면 종료
        if (!assistantMessage.tool_calls || assistantMessage.tool_calls.length === 0) {
          if (!assistantMessage.content) {
            if (revenueChartResult) {
              clearToolLoading(aiMessageId);
              let _chartText = _resolveVizText(revenueChartResult.intro || '');
              // [FINANCE STATEMENT CAPTION] 재무제표 요청 + MONTHLY+TREND → 결정적 매출 요약 캡션 (dual sync with nodeAgent.mjs)
              const _financeSession2 = sessionRef.current;
              if (/재무|제무|손익|재정/.test(userInput) && _financeSession2.getBuffer?.()?.intention === 'CHECK_SALES+MONTHLY+TREND_STATISTICS_BY+PERIOD') {
                const _fc = buildFinanceSummaryIntro(_financeSession2.getBuffer().summary, _financeSession2.getBuffer().dateRange, _financeSession2.getBuffer().items);
                if (_fc) _chartText = _fc;
              }
              revenueChartResult.intro = _chartText;
              updateAiMessage(aiMessageId, _chartText, { revenueChartData: revenueChartResult, isNewResponse: true });
              finalText = _chartText;
              log.push({ type: 'final', responseType: 'revenueChart', year: revenueChartResult.year, model: AGENT_MODEL });
              break;
            }
            if (salesViewResult) {
              clearToolLoading(aiMessageId);
              const _salesText = _resolveVizText(salesViewResult.intro || '');
              salesViewResult.intro = _salesText;
              updateAiMessage(aiMessageId, _salesText, { salesViewData: salesViewResult, isNewResponse: true });
              finalText = _salesText;
              log.push({ type: 'final', responseType: 'salesView', weekCount: salesViewResult.weeksData?.length, model: AGENT_MODEL });
              break;
            }
            if (vizResult) {
              clearToolLoading(aiMessageId);
              const _vizText = _resolveVizText(vizResult.payload?.intro || '');
              if (vizResult.payload) vizResult.payload.intro = _vizText;
              updateAiMessage(aiMessageId, _vizText, { vizData: vizResult, isNewResponse: true });
              finalText = _vizText;
              log.push({ type: 'final', responseType: vizResult.type, model: AGENT_MODEL });
              break;
            }
            if (showDisplayResult) {
              await renderDisplay(showDisplayResult, null);
              break;
            }
            // [EMPTY STOP RETRY] LLM이 content/tool_calls 모두 비운 채 stop — 직전 tool 성공 시 1회 retry nudge로 finish 강제
            // 패턴 차단: count=0/1 single-record 응답에서 LLM이 "표/finish 결정 못함" 상태로 empty stop → fallback "지금은 답변이 어려워요" 회피
            const emptyRetryCount = log.filter(l => l.type === 'empty_stop_retry').length;
            const lastToolResult = log.slice().reverse().find(l => l.type === 'tool_result');
            if (emptyRetryCount === 0 && lastToolResult?.success) {
              const c = lastToolResult.count;
              const guide = (c === null || c === 0)
                ? `Use finish() with a brief "해당 데이터가 없어요" message. Do NOT call show_display (empty table blocked).`
                : c === 1
                  ? `Use finish() to answer in natural language with the single record's key fields, OR show_display() if a multi-column table is meaningful.`
                  : `Use show_display() to render the table.`;
              messages.push({ role: 'system', content:
                `Your last response was empty (no content, no tool_calls). The previous tool ${lastToolResult.tool}() succeeded with count=${c}. ` +
                `You MUST respond to the user now. ${guide} Do NOT return empty again.`
              });
              log.push({ type: 'empty_stop_retry', iteration, lastTool: lastToolResult.tool, lastCount: c });
              if (TOOL_CALLING_LOG) console.log(`[Agent] 🔁 EMPTY STOP RETRY: ${lastToolResult.tool} count=${c} → forced finish/show_display nudge`);
              continue;
            }
            // [EMPTY STOP AFTER BLOCK RETRY] dual sync with nodeAgent.mjs — finish 블록 직후 LLM이 빈 stop으로 도망 → 1회 retry로 실제 도구 호출 유도
            // 패턴 차단: finish_no_action_blocked / finish_blocked / finish_empty_summary_blocked / finish_parallel_blocked 등 finish 블록 후 LLM 포기 → fallback "지금은 답변이 어려워요" 회피
            const emptyAfterBlockCount = log.filter(l => l.type === 'empty_stop_after_block_retry').length;
            const finishBlockedRecent = log.slice().reverse().slice(0, 6).some(l =>
              l.type === 'finish_no_action_blocked' ||
              l.type === 'finish_blocked' ||
              l.type === 'finish_empty_summary_blocked' ||
              l.type === 'finish_parallel_blocked'
            );
            const _sessionEmpty = sessionRef.current;
            const _bufHasData = _sessionEmpty?.buffers ? [..._sessionEmpty.buffers.entries()].some(([, b]) => (b.items?.length || 0) >= 1) : false;
            if (emptyAfterBlockCount === 0 && finishBlockedRecent && _bufHasData) {
              const _activeBuf = _sessionEmpty.getActiveBuf?.() || [..._sessionEmpty.buffers.values()].find(b => b.items?.length > 0);
              const _availableFieldKeys = _activeBuf?.availableFields ? Object.keys(_activeBuf.availableFields) : [];
              const _fieldHint = _availableFieldKeys.length > 0 ? ` available fields: [${_availableFieldKeys.slice(0, 12).join(', ')}]` : '';
              const _bufCount = _activeBuf?.items?.length || 0;
              messages.push({ role: 'system', content:
                `Your last response was empty (no content, no tool_calls) after a finish attempt was blocked. The buffer already has ${_bufCount} items.${_fieldHint} ` +
                `You MUST call a real tool now to fulfill the user's request: show_display() to render the table, OR filter()/sort()/derive() to refine the data first. ` +
                `Do NOT return empty again. Do NOT call finish() without first executing a data tool.`
              });
              log.push({ type: 'empty_stop_after_block_retry', iteration, bufCount: _bufCount });
              if (TOOL_CALLING_LOG) console.log(`[Agent] 🔁 EMPTY STOP AFTER BLOCK RETRY: bufCount=${_bufCount} → forced data tool nudge`);
              continue;
            }
            // [EMPTY STOP — SHORT AGREEMENT FOLLOW-UP] L17-AGREE-GOGO-001 cycle 1 (2026-05-30 dual sync with nodeAgent.mjs)
            //   본질: 짧은 동의 입력(고고/네/응/좋아/부탁/진행 등) 시 LLM이 어떤 도구도 호출하지 못하고 empty stop으로 도망 → RESPONSE_FAILED_RETRY generic fallback
            //         (a) 이전 turn 0건 (buffer에 intention 있지만 items=0) → "추천할 데이터가 없어요" finish() 강제
            //         (b) 이전 turn 데이터 있음 (buffer items≥1) → sort/filter/show_display 호출 강제
            //   해소: short agreement + 이번 turn no tool 호출 + empty stop → 1회 nudge로 적절한 다음 단계 안내
            //   패턴 기반: structural Korean short-agreement regex + buffer 상태 분기 (한 case hardcode X — 모든 짧은 동의 follow-up에 범용 적용)
            const _emptyAgreementRetryCount = log.filter(l => l.type === 'empty_agreement_retry').length;
            const _shortAgreementReEmpty = /^(네|응|좋아|좋아요|부탁|부탁해|부탁해요|해줘|해주세요|고고|진행|그래|오케이|ok|okay)[\s!.…?\n]*$/i;
            const _inputTextRawEmpty = (userInput || '').trim();
            const _bufHasIntentionButNoItems = _sessionEmpty?.buffers
              ? [..._sessionEmpty.buffers.values()].some(b => !!b.intention && (b.items?.length || 0) === 0)
              : false;
            const _noToolCalledThisTurn = !log.some(l => l.type === 'tool_result' || l.type === 'tool_call');
            const _isShortAgreement = _shortAgreementReEmpty.test(_inputTextRawEmpty);
            if (_emptyAgreementRetryCount === 0
                && _isShortAgreement
                && _noToolCalledThisTurn
                && (_bufHasData || _bufHasIntentionButNoItems)) {
              if (_bufHasData) {
                const _activeBuf = _sessionEmpty.getActiveBuf?.() || [..._sessionEmpty.buffers.values()].find(b => b.items?.length > 0);
                const _availableFieldKeys = _activeBuf?.availableFields ? Object.keys(_activeBuf.availableFields) : [];
                const _fieldHint = _availableFieldKeys.length > 0 ? `available fields: [${_availableFieldKeys.slice(0, 12).join(', ')}]` : '';
                const _bufCount = _activeBuf?.items?.length || 0;
                messages.push({ role: 'system', content:
                  `User said "${_inputTextRawEmpty}" (short agreement). The buffer has ${_bufCount} loaded items. ${_fieldHint}. ` +
                  `Your last response was empty (no content, no tool_calls). EMPTY REPLY IS FORBIDDEN. ` +
                  `You MUST call a real tool now: show_display() to render the loaded list as a table, OR sort()/filter()/derive()/recommend_sms() to refine first. ` +
                  `Pick the most useful refinement based on the data (e.g. sort by endDate asc + show_display for expiring members, sort by attendance desc for active members, recommend_sms for retention). ` +
                  `Do NOT return empty. Do NOT call finish() without first executing a data tool.`
                });
                log.push({ type: 'empty_agreement_retry', iteration, mode: 'has_data', bufCount: _bufCount });
                smsToolNeeded = true;  // [LAZY TOOL-SET D1] nudge가 recommend_sms를 권함 — 도구 부재 데드락 차단
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🔁 EMPTY AGREEMENT RETRY (has_data): bufCount=${_bufCount} → forced data tool nudge`);
              }
              else {
                const _prevBuf = [..._sessionEmpty.buffers.values()].find(b => !!b.intention && (b.items?.length || 0) === 0);
                const _prevIntention = _prevBuf?.intention || 'previous';
                messages.push({ role: 'system', content:
                  `User said "${_inputTextRawEmpty}" (short agreement). However, the previous query (intention=${_prevIntention}) returned 0 items — there is no data to refine, recommend SMS for, or display. ` +
                  `Your last response was empty (no content, no tool_calls). You MUST call finish() now with a concise Korean response explaining that there is no data to act on, and suggest the user try a different category, period, or condition. ` +
                  `Example: finish("이전 검색 결과가 0건이라 추천할 데이터가 없어요. 다른 기간이나 조건으로 다시 시도해 주세요.") ` +
                  `Do NOT return empty again. Do NOT call sort/filter/recommend_sms on empty buffer.`
                });
                log.push({ type: 'empty_agreement_retry', iteration, mode: 'empty_prev', prevIntention: _prevIntention });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🔁 EMPTY AGREEMENT RETRY (empty_prev): prev intention=${_prevIntention}, 0 items → forced finish() guide`);
              }
              continue;
            }
            finalText = RESPONSE_FAILED_RETRY;
            updateAiMessage(aiMessageId, RESPONSE_FAILED_RETRY);
          }
          break;
        }

        // assistant 메시지 추가
        messages.push(assistantMessage);
        consecutiveTextOnly = 0; // tool call 있으면 카운터 리셋

        // 각 tool call 실행
        for (const toolCall of assistantMessage.tool_calls) {
          if (isCancelledRef.current) break;

          const toolName = toolCall.function.name;
          let toolArgs = {};
          try { toolArgs = JSON.parse(toolCall.function.arguments); }
          catch { toolArgs = {}; }

          // [POST-GUARD TOOL BLOCK] 가드(_finishAllowedByGuard) set 후 LLM이 finish 외 도구 호출 시 즉시 forced finish
          // ALL EMPTY/COUNT QUESTION/EMPTY DISPLAY/REPEATED FAILURE 등 가드가 finish 안내했는데 LLM 무시 시 백엔드가 결정한 텍스트로 강제 종료 (LLM 의존 X)
          // [B-1 과적합 수술 2026-07-03] viz 렌더 턴에 사용자가 명시적으로 표/리스트를 요구(_vizListRequested)한 경우
          //   show_display(+준비 도구 sort/switch_slot)는 통과 — "리스트로 보여달라구 왜자꾸끊겨" 봉쇄 해소 (dual sync with nodeAgent.mjs)
          // [CENTER-INFO PREP PASS 2026-07-06] CENTER-INFO STALE RENDER 가드 발동 턴은 준비 도구 통과 (L9a-INFRA-CENTERINFO-001 R5:
          //   가드 차단 직후 LLM이 text 대신 switch_slot 등 준비 도구를 고르면 사살 → 93건 적재 상태에서 "찾을 수 없어요" 거짓 no-data).
          //   merge_slots/show_display 제외 — merge가 intention을 'MERGED'로 세탁해 /^CHECK_MEMBER/ 가드 우회 거짓 표 렌더 가능(적대검증).
          //   cap은 iteration당 1 증가 5회(병렬 tool_calls 다중 소진 방지). (dual sync with nodeAgent.mjs)
          const _ciPrepPass = sessionRef.current?._finishAllowedByGuard && toolName !== 'finish'
              && sessionRef.current?._showDisplayCenterInfoBlocked
              && sessionRef.current._centerInfoPrepPassCount < 5
              && ['get_intentions', 'call_api', 'switch_slot', 'compute', 'sort', 'filter'].includes(toolName);

          if (_ciPrepPass && sessionRef.current._centerInfoPrepLastIter !== iteration) {
            sessionRef.current._centerInfoPrepPassCount++;
            sessionRef.current._centerInfoPrepLastIter = iteration;
          }

          if (sessionRef.current?._finishAllowedByGuard && toolName !== 'finish' && !_ciPrepPass
              && !(sessionRef.current?._vizListRequested && ['show_display', 'sort', 'switch_slot', 'call_api', 'get_intentions'].includes(toolName))) {
            finishCalled = true;

            // [1b 폴백 정직화 2026-07-06] suggestedText 부재 강제 종료 시 활성 버퍼에 데이터 실재하면 "찾을 수 없어요" 거짓 no-data 금지 (dual sync with nodeAgent.mjs) //
            const _pgSuggested = sessionRef.current?._finishGuardSuggestedText;
            const _pgActiveItems = sessionRef.current?.getActiveBuf?.()?.items?.length || 0;
            let fallbackKind;

            if (_pgSuggested) {
              finishResponse = _pgSuggested;
              fallbackKind = 'suggested';
            }
            else if (_pgActiveItems > 0) {
              finishResponse = '요청 처리를 마무리하지 못했어요. 다시 한번 질문해 주세요.';
              fallbackKind = 'neutral';
            }
            else {
              finishResponse = '요청하신 데이터를 찾을 수 없어요. 다른 조건으로 다시 시도해 주세요.';
              fallbackKind = 'nodata';
            }

            messages.push({ role: 'tool', tool_call_id: toolCall.id, content: 'ok' });
            log.push({ type: 'post_guard_forced_finish', iteration, blockedTool: toolName, fallbackKind, finalText: finishResponse });
            if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 POST-GUARD FORCED FINISH: ${toolName} → "${finishResponse.slice(0,40)}..."`);
            break;
          }

          // [SWITCH_SLOT POST-MERGE BLOCK] merge_slots와 같은 응답에서 switch_slot 호출 시 차단
          // merge_slots가 이미 activeSlot을 outputSlot으로 설정 — switch_slot이 덮어쓰면 wrong buffer 참조
          if (toolName === 'switch_slot') {
            const hasMergeInSameResponse = assistantMessage.tool_calls.some(
              tc => tc.function.name === 'merge_slots'
            );
            if (hasMergeInSameResponse) {
              const blockMessage = `[SWITCH_SLOT BLOCKED — POST-MERGE] merge_slots가 같은 호출에 포함되어 있어 switch_slot을 차단했습니다. merge_slots가 이미 활성 슬롯을 병합 결과("${sessionRef.current?.activeSlot}")로 설정했습니다. 바로 filter/sort/show_display를 진행하세요.`;
              messages.push({ role: 'tool', tool_call_id: toolCall.id, content: blockMessage });
              log.push({ type: 'switch_slot_post_merge_blocked', iteration });
              if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 SWITCH_SLOT BLOCKED (parallel with merge_slots)`);
              continue;
            }
          }

          // finish 도구 처리 — 루프 종료 //
          if (toolName === 'finish') {
            // [STALE PERIOD RELABEL — FINISH BLOCK] 운영 5차 rid63/65/68 (finish-tool 경로, dual sync with nodeAgent.mjs)
            //   본질: 후속 턴 새 절대기간 요청에 LLM이 신규 call_api 없이 stale period 버퍼(또는 switch_slot로 전환한 다른 기간 슬롯)를
            //         finish()로 그대로 답변 → 새 기간 거짓 라벨(rid63 "6월 총매출 38,440,000" = 직전 2026 전체 총액). show_display 가드 우회.
            //   해소: finish 진입부 detectStalePeriodRelabel → block 시 call_api 재조회 nudge (cap 3 — LLM이 nudge 무시하고 finish 반복 가능, 3회 후 graceful 허용).
            if (sessionRef.current && sessionRef.current._periodChangeFinishBlockCount < 3 && !sessionRef.current._calledApiThisTurn) {
              const _relabelFin = detectStalePeriodRelabel({
                userInput: sessionRef.current.getUserInput?.() || '',
                buffer: sessionRef.current.getBuffer?.(),
                calledApiThisTurn: sessionRef.current._calledApiThisTurn,
              });
              if (_relabelFin?.block) {
                sessionRef.current._periodChangeFinishBlockCount++;
                const _rpF = _relabelFin.requestedPeriod, _bpF = _relabelFin.bufferPeriod;
                const blockMessage = `[STALE PERIOD RELABEL — REFETCH REQUIRED] User requested a NEW period (resolved ${_rpF.startDate}~${_rpF.endDate}) but the active buffer (intention="${_relabelFin.intention}") holds data for a DIFFERENT period (${_bpF.startDate}~${_bpF.endDate}) and no call_api ran this turn. Finishing now cites the OLD-period totals under a NEW-period label — a false relabel (e.g. citing a full-year total as a single month). Re-call call_api(intention="${_relabelFin.intention}", startDate="${_rpF.startDate}", endDate="${_rpF.endDate}") to fetch the requested period BEFORE finishing. Do NOT switch_slot to a different-period slot and do NOT cite the existing buffer's totals for a different period. (block ${sessionRef.current._periodChangeFinishBlockCount}/3)`;
                messages.push({ role: 'tool', tool_call_id: toolCall.id, content: blockMessage });
                log.push({ type: 'finish_stale_period_relabel_blocked', iteration, requested: `${_rpF.startDate}~${_rpF.endDate}`, buffer: `${_bpF.startDate}~${_bpF.endDate}`, count: sessionRef.current._periodChangeFinishBlockCount });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 STALE PERIOD RELABEL [finish-tool]: requested ${_rpF.startDate}~${_rpF.endDate} vs buffer ${_bpF.startDate}~${_bpF.endDate} (${sessionRef.current._periodChangeFinishBlockCount}/3)`);
                continue;
              }
            }

            // [B-1 과적합 수술] [FINISH BLOCKED — LIST REQUESTED] viz 렌더 턴 + 사용자 명시 표/리스트 요구 + show_display 미성공 → 1회 차단
            //   probe L40-OPS-VIZ-RANK-LIST-001 잔존 1/5: LLM이 [FINISH NOW + LIST REQUESTED] 지시 무시하고 즉시 finish (dual sync with nodeAgent.mjs)
            if (sessionRef.current?._vizListRequested && sessionRef.current?._finishAllowedByGuard
                && !sessionRef.current?._showDisplaySucceededThisTurn && !sessionRef.current?._listRequestFinishBlockAttempted) {
              sessionRef.current._listRequestFinishBlockAttempted = true;
              const _listBlockMsg = `[FINISH BLOCKED — LIST REQUESTED] The user explicitly asked for a table/list this turn, the visualization was rendered, but NO table was displayed. Call show_display now on a tabular (non-visualizationOnly) buffer — if none is loaded, call_api the relevant non-visualizationOnly LIST intention (예: CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD for attendance) first — then finish() with a brief summary. (1회 한정 차단)`;
              messages.push({ role: 'tool', tool_call_id: toolCall.id, content: _listBlockMsg });
              log.push({ type: 'finish_list_requested_blocked', iteration });
              if (TOOL_CALLING_LOG) console.log('[Agent] 🚫 FINISH BLOCKED — LIST REQUESTED (viz rendered, no table, user asked list)');
              continue;
            }

            // [B-2 과적합 수술] [FINISH BLOCKED — DATA-GROUNDED ADVICE] "우리 데이터 보고 …추천/조언" 명시 입력 —
            //   call_api 0회 + 제안/거절형 텍스트 finish 차단 1회. NO ACTION 게이트는 버퍼 존재 전제라 첫 턴 미커버 (dual sync with nodeAgent.mjs).
            const _dgAdviceRe = /(데이터|현황|수치|실적|지표|기록|통계)\s*(를|을|도)?\s*(보고|보면서|기반|참고|토대|가지고|활용|분석)/;
            const _dgAdviceNounRe = /추천|조언|제안|방법|전략|팁|아이디어|컨설/;
            if (_dgAdviceRe.test(userInput || '') && _dgAdviceNounRe.test(userInput || '')
                && log.filter(l => l.type === 'finish_data_grounded_advice_blocked').length === 0
                && log.filter(l => l.type === 'tool_call' && l.tool === 'call_api').length === 0) {
              const _dgBlockMsg = `[FINISH BLOCKED — DATA-GROUNDED ADVICE] The user explicitly asked for advice BASED ON the gym's actual data ("데이터/현황 보고 … 추천/조언"). A generic offer or refusal does not satisfy this. REQUIRED: (1) get_intentions(SALES or MEMBER) → call_api the relevant statistics (예: 매출 추이/재등록률/만료 예정/출석 현황), (2) then finish() with 3-5 concrete suggestions, each citing the loaded numbers. Do NOT fabricate numbers — only cite what call_api returned. (1회 한정 차단)`;
              messages.push({ role: 'tool', tool_call_id: toolCall.id, content: _dgBlockMsg });
              log.push({ type: 'finish_data_grounded_advice_blocked', iteration });
              if (TOOL_CALLING_LOG) console.log('[Agent] 🚫 FINISH BLOCKED — DATA-GROUNDED ADVICE (no call_api, user asked data-based advice)');
              continue;
            }

            // [FINISH NO ACTION BLOCKED] 본 turn 데이터 처리 도구 호출 0회 + buffer 데이터 존재 + finish 시도 → 차단
            // (사용자 input 데이터 요청에 대해 LLM이 약속만 하고 미수행 패턴 차단. 기본 1회 한정, 무한루프 방지)
            // EXCEPTION: HOWTO/context-explanation/get_guide inputs — finish without data tools is valid
            //
            // [SHORT_AGREEMENT_EXTENDED_BLOCK] L17-AGREE-BUTAK-001 cycle 1 (2026-05-31 dual sync with nodeAgent.mjs)
            //   본질: 사용자가 짧은 동의(네/응/좋아/부탁해/고고/진행/...) 입력 시 LLM이 [FINISH BLOCKED — NO ACTION] 1회 차단 후
            //         data tool 호출 없이 finish() 재시도 → intro/summary placeholder만 재기술 → 사용자 동의에 대한 실제 후속 조치 누락
            //   해소: short-agreement 입력 + buffer 데이터 존재 시 차단 cap을 3회로 확장. 3회 안에 LLM이 sort/filter/show_display 등 실제 도구 호출 강제.
            //         3회 도달 후에는 무한루프 방지 위해 finish 허용 (graceful degradation).
            //   패턴 기반: structural Korean short-agreement regex (한 case "부탁해" hardcode X — 모든 짧은 동의 follow-up에 범용 적용)
            const _shortAgreementReFinish = /^(네|응|좋아|좋아요|부탁|부탁해|부탁해요|해줘|해주세요|고고|진행|그래|오케이|ok|okay)[\s!.…?\n]*$/i;
            const _inputTextRawFinish = (userInput || '').trim();
            const _isShortAgreementFinish = _shortAgreementReFinish.test(_inputTextRawFinish);
            const finishNoActionBlockedCount = log.filter(l => l.type === 'finish_no_action_blocked').length;
            const _maxFinishNoActionBlocks = _isShortAgreementFinish ? 3 : 1;
            if (finishNoActionBlockedCount < _maxFinishNoActionBlocks) {
              const _dataTools = log.filter(l => l.type === 'tool_call' &&
                ['call_api','merge_slots','filter','sort','aggregate','derive','compute','switch_slot','customize_display','show_display','show_sales_view','show_revenue_chart','show_attendance_chart','show_attendance_ranking','show_class_attendance','show_engagement_chart','show_ltv','show_member_status','recommend_sms','get_guide'].includes(l.tool));
              const _hasBufferData = [...(sessionRef.current?.buffers || new Map()).entries()].some(([, b]) => (b.items?.length || 0) >= 1);
              // Context explanation bypass: "어떻게 계산됨" + buffer data = answering from context, not a data action //
              const _contextExplainBypass = /어떻게\s*계산|어떻게\s*산출|어떻게\s*산정|계산\s*방식|산출\s*방식|뭐\s*기준|기준이?\s*뭐|무슨\s*뜻|뭘\s*의미|계산됨|산출됨|산정됨/.test(userInput || '');
              // [ADVICE FOLLOW-UP] 자문 의도 입력은 텍스트-only finish가 정답(데이터 액션 강제 X) — ADVICE FOLLOW-UP GATE GUARD가 유도한 자문 finish 통과 (dual sync with nodeAgent.mjs)
              const _adviceFollowupBypass = sessionRef.current?._isAdviceFollowupIntent?.(userInput);
              if (_dataTools.length === 0 && _hasBufferData && !_contextExplainBypass && !_adviceFollowupBypass) {
                // [SHORT_AGREEMENT_EXTENDED_BLOCK] 동의 입력 시 더 직접적인 directive 메시지 추가
                const _shortAgreementHint = _isShortAgreementFinish
                  ? `\n  [SHORT AGREEMENT — REAL ACTION REQUIRED] 사용자가 "${_inputTextRawFinish}" (짧은 동의)로 응답했습니다. 이는 직전 turn에서 제시한 추천 또는 후속 작업에 대한 OK 신호입니다. intro/summary를 재기술하는 finish는 무효입니다 — 사용자는 이미 직전 turn의 결과를 봤습니다. MUST 실제 데이터 처리 도구 호출: sort()/filter()/derive()/recommend_sms()/show_display() 등으로 buffer를 refine하거나 새 view를 렌더링하세요. 현재 누적 차단: ${finishNoActionBlockedCount + 1}/${_maxFinishNoActionBlocks}회.`
                  : '';
                const blockMessage = `[FINISH BLOCKED — NO ACTION] 본 turn에 데이터 처리 도구(call_api/merge_slots/filter/sort/show_display 등)를 한 번도 호출하지 않고 finish 시도했습니다. 직전 buffer에 데이터가 있고 사용자가 처리/조회/cross 요청한 것으로 보입니다.\n  (1) 직전 buffer의 raw slots 활용해 chain 시도 (merge_slots/filter/show_display)\n  (2) 데이터 의도가 아니면(HOWTO/인사) 즉시 finish 가능 — 단 "X해드릴게요/추려드릴게요/불러와서…드릴게요" 같은 약속형 표현 금지. 약속 대신 행동 직접 실행 후 finish.${_shortAgreementHint}`;
                messages.push({ role: 'tool', tool_call_id: toolCall.id, content: blockMessage });
                log.push({ type: 'finish_no_action_blocked', iteration, shortAgreement: _isShortAgreementFinish });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 FINISH BLOCKED (no data action, buffer has data)${_isShortAgreementFinish ? ` [short_agreement ${finishNoActionBlockedCount + 1}/${_maxFinishNoActionBlocks}]` : ''}`);
                continue;
              }
            }

            // [FINISH EMPTY + SUMMARY] finish response가 빈 문자열이고 buffer에 summary가 존재하면 차단 (1회 한정)
            // filter 0건 후 items는 비었지만 summary에 aggregate 값(totalRefundAmount 등)이 있어 답변 가능한데 빈 finish 시도 패턴
            const finishEmptySummaryBlockedCount = log.filter(l => l.type === 'finish_empty_summary_blocked').length;
            if (finishEmptySummaryBlockedCount === 0) {
              const _finishResponse = (toolArgs.response || '').trim();
              if (_finishResponse.length === 0) {
                const _summaryBuf = [...sessionRef.current.buffers.entries()].find(([, b]) => b.summary && Object.keys(b.summary).length > 0);
                if (_summaryBuf) {
                  const [_sName, _sBuf] = _summaryBuf;
                  const _summaryEntries = Object.entries(_sBuf.summary)
                    .filter(([k, v]) => v != null && typeof v !== 'object')
                    .map(([k, v]) => `{{SUMMARY:${k}}}=${v}`)
                    .slice(0, 10)
                    .join(', ');
                  const blockMessage = `[FINISH BLOCKED — EMPTY RESPONSE + SUMMARY AVAILABLE] finish response is empty, but buffer "${_sName}" has summary data: ${_summaryEntries}. Use {{SUMMARY:<key>}} placeholders to answer the user's question. For example, total refund amount = {{SUMMARY:totalRefundAmount}}, refund count = {{SUMMARY:refundCount}}. Write a natural Korean response using these values, then call finish again.`;
                  messages.push({ role: 'tool', tool_call_id: toolCall.id, content: blockMessage });
                  log.push({ type: 'finish_empty_summary_blocked', iteration });
                  if (TOOL_CALLING_LOG) console.log(`[Agent] FINISH BLOCKED (empty response + summary available in "${_sName}")`);
                  continue;
                }
              }
            }

            // [FINISH PARALLEL BLOCKED] finish가 다른 도구와 같은 응답에서 호출 시 차단 — buffer 변화 확인 후 재결정
            // (1회 한정, 무한루프 방지. parallel tool_call 시 다른 도구 결과 받기 전에 finish 발화하는 LLM 패턴 차단)
            const finishParallelBlockedCount = log.filter(l => l.type === 'finish_parallel_blocked').length;
            if (assistantMessage.tool_calls.length > 1 && finishParallelBlockedCount === 0) {
              const otherTools = assistantMessage.tool_calls
                .filter(tc => tc.function.name !== 'finish')
                .map(tc => tc.function.name);
              const blockMessage = `[FINISH BLOCKED — PARALLEL TOOLS] finish가 다른 도구(${otherTools.join(', ')})와 같은 응답에서 호출됐습니다. 다른 도구의 결과(성공/실패 여부 + 메시지의 chain hint)를 먼저 확인하고 buffer/state 변화를 반영한 뒤 다시 finish 결정하세요. finish는 모든 데이터 처리 도구 호출 후 단독으로 발화해야 합니다. 다른 도구 실패 시에는 메시지의 hint대로 chain 재시도하세요.`;
              messages.push({ role: 'tool', tool_call_id: toolCall.id, content: blockMessage });
              log.push({ type: 'finish_parallel_blocked', iteration, otherTools });
              if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 FINISH BLOCKED (parallel with: ${otherTools.join(',')})`);
              continue;
            }

            // [B] cross-merge pending — fieldMissing 자동 무효화 발생 후 mergeSlots(intersect) 미호출 상태에서 finish 시 차단 (1회 한정)
            // [L22-DATERANGE-001 cycle 1] narrow_period_chain_loop_blocked 발동 시 cross-merge guard skip — narrow period inform이 우선 (사용자 의도: 시간 좁히기 ≠ 카테고리 intersect)
            const finishCrossMergeBlockedCount = log.filter(l => l.type === 'finish_cross_merge_blocked').length;
            const _narrowPeriodAlreadyBlocked = log.some(l => l.type === 'narrow_period_chain_loop_blocked');
            // inner/intersect 모두 cross-merge 의미 충족 — AgentSession.mergeSlots 내부 동일 처리. 가드만 'intersect' 단일 인식이라 inner 케이스 misfire 차단 → 정규화 (아래 mergeIntersectOk)
            if (finishCrossMergeBlockedCount === 0 && !_narrowPeriodAlreadyBlocked) {
              const _toolResults = log.filter(l => l.type === 'tool_result');
              const fieldMissingResult = _toolResults.slice().reverse().find(r => r.tool === 'filter' && r.autoRestored);
              const mergeIntersectOk = _toolResults.some(r => r.tool === 'merge_slots' && r.success && (r.mergeMode === 'intersect' || r.mergeMode === 'inner'));
              // [B+++] fieldMissing 이후 다른 카테고리에서 동일 필드로 성공적인 filter가 있으면 자체 해결된 것으로 판단, 차단 해제
              const _fieldMissingIdx = fieldMissingResult ? _toolResults.indexOf(fieldMissingResult) : -1;
              const _fieldResolvedByNewData = _fieldMissingIdx >= 0
                ? _toolResults.slice(_fieldMissingIdx + 1).some(r =>
                    r.tool === 'filter' && r.success && !r.autoRestored && r.filterField === fieldMissingResult.filterField && (r.filterAfter > 0 || r.after > 0))
                : false;
              if (fieldMissingResult && !mergeIntersectOk && !_fieldResolvedByNewData) {
                const fieldName = fieldMissingResult.filterField || 'the missing field';
                const blockMessage = `[FINISH BLOCKED — CROSS-MERGE REQUIRED] filter("${fieldName}") returned fieldMissing. The user's request requires INTERSECTION across categories. You MUST execute these steps before finish:\n  1) call_api(<intention containing "${fieldName}">, slot:"<new>")\n  2) merge_slots([{slotName:"default"}, {slotName:"<new>"}], mode:"intersect", matchKey:"<memberId|userId|name>")\n  3) filter(field:"${fieldName}", ...) on merged slot\n  4) show_display\nDo NOT respond with "${fieldName} 정보 없어요" / "다시 불러오면" — the field IS reachable via cross-merge. Retry now.`;
                messages.push({ role: 'tool', tool_call_id: toolCall.id, content: blockMessage });
                log.push({ type: 'finish_cross_merge_blocked', iteration, field: fieldName });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 FINISH BLOCKED: cross-merge pending for "${fieldName}"`);
                continue;
              }
            }

            // [POLITE RETRY GUARD] 사용자에게 동의/재입력 요구하는 finish 응답 차단 (1회 한정)
            const politeRetryBlockedCount = log.filter(l => l.type === 'finish_polite_retry_blocked').length;
            const politeRetryResponse = toolArgs.response || '';
            if (politeRetryBlockedCount === 0 && politeRetryResponse) {
              const ASKING_PERMISSION = /(원하시면|원하신다면)[^.!?]{0,40}(드릴게요|드릴까요|드릴까\?|해\s*드릴|보여\s*드릴|정리\s*해|확인\s*해|조회\s*해|추려\s*드릴)/;
              const RE_QUERY_REQUEST = /(다시\s*조회|다시\s*정리|다시\s*불러|다시\s*확인|새로\s*조회|새로\s*불러)[가-힣\s]*?(해야|필요|드려야|드릴게요|와야\s*합니다|와야\s*돼요|와야\s*해요|야\s*합니다|야\s*해요)/;
              const hasPoliteRetry = ASKING_PERMISSION.test(politeRetryResponse) || RE_QUERY_REQUEST.test(politeRetryResponse);
              if (hasPoliteRetry) {
                const turnTools = log.filter(l => l.type === 'tool_call');
                const turnResults = log.filter(l => l.type === 'tool_result');
                const dataLoadedThisTurn = turnResults.some(r => r.tool === 'call_api' && r.success);
                const displayShownThisTurn = turnTools.some(t => t.tool === 'show_display');
                if (!dataLoadedThisTurn && !displayShownThisTurn) {
                  const blockMessage = `[FINISH BLOCKED — POLITE RETRY] 사용자에게 동의/재입력 요구 패턴 감지("${politeRetryResponse.slice(0,60)}..."). 사용자 입력 "${userInput.slice(0,50)}"의 차원에 맞는 새 call_api 호출하세요 — 직전/동일 카테고리 intention의 enum/params 옵션(groupBy 등)을 검토하여 적합한 옵션으로 즉시 호출. "원하시면 X해드릴게요" / "다시 X해야 합니다" 금지. 백엔드 옵션이 정말 없으면 "현재 백엔드는 Y만 지원" 형태로 안내.`;
                  messages.push({ role: 'tool', tool_call_id: toolCall.id, content: blockMessage });
                  log.push({ type: 'finish_polite_retry_blocked', iteration, response: politeRetryResponse.slice(0, 100) });
                  if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 FINISH BLOCKED: polite retry pattern`);
                  continue;
                }
              }
            }

            // [FALSE EXCUSE GUARD] LLM이 buffers의 활용 가능 슬롯을 무시하고 "비교 불가/형태가 아니라/다시 불러" 회피 finish 시 차단 (1회 한정)
            // 트리거: 회피 표현 + 이번 turn 도구 0회 + buffers에 활용 가능 슬롯(MERGED 또는 dateRange) 존재 + input이 비교/기간 의도
            const falseExcuseBlockedCount = log.filter(l => l.type === 'finish_false_excuse_blocked').length;
            const falseExcuseResponse = toolArgs.response || '';
            if (falseExcuseBlockedCount === 0 && falseExcuseResponse) {
              const FALSE_EXCUSE_RE = /(형태가\s*아니|비교할\s*수\s*(없|어렵)|직접\s*비교\s*(불가|어려)|불러[와야오]+\s*(합니다|돼요|해요|있어요)|다시\s*(불러|조회|정리|확인)[가-힣\s]*?(합니다|돼요|해요|입니다))/;
              const COMPARE_INTENT_RE = /비교|대비|vs|\d+\s*월(?:.*?\d+\s*월)?|전월|전년|이번\s*[달월]|지난\s*[달월]/;
              if (FALSE_EXCUSE_RE.test(falseExcuseResponse) && COMPARE_INTENT_RE.test(userInput)) {
                const turnTools = log.filter(l => l.type === 'tool_call');
                const turnResults = log.filter(l => l.type === 'tool_result');
                const dataLoadedThisTurn = turnResults.some(r => r.tool === 'call_api' && r.success);
                const displayShownThisTurn = turnTools.some(t => t.tool === 'show_display');
                const switchedThisTurn = turnTools.some(t => t.tool === 'switch_slot');
                const mergedThisTurn = turnTools.some(t => t.tool === 'merge_slots');
                if (!dataLoadedThisTurn && !displayShownThisTurn && !switchedThisTurn && !mergedThisTurn) {
                  const _session = sessionRef.current;
                  const activeName = _session?.activeSlot;
                  const candidates = [];
                  if (_session?.buffers) {
                    for (const [name, buf] of _session.buffers) {
                      if (name === activeName) continue;
                      const count = buf?.items?.length || 0;
                      if (count === 0) continue;
                      const isMerged = buf.intention === 'MERGED' && count >= 2;
                      const hasDateRange = buf.dateRange?.startDate && buf.dateRange?.endDate;
                      if (isMerged || hasDateRange) {
                        const tag = isMerged ? 'merged' : `${buf.dateRange.startDate}~${buf.dateRange.endDate}`;
                        candidates.push({ name, count, intention: buf.intention, tag });
                      }
                    }
                  }
                  if (candidates.length > 0) {
                    const top = candidates.slice(0, 3);
                    const list = top.map(c => `  - ${c.name}(${c.count}건, intention:${c.intention}, ${c.tag})`).join('\n');
                    const preferred = top[0].name;
                    const blockMessage = `[FINISH BLOCKED — FALSE EXCUSE] Buffers에 활용 가능 슬롯이 이미 존재합니다:\n${list}\n사용자 입력 "${userInput.slice(0,50)}"의 비교/기간 의도와 매칭됩니다.\n"비교 불가/형태가 아니라/다시 불러와야 합니다" 등 회피 응답 금지.\n다음 중 하나를 즉시 호출:\n  (1) switch_slot({slot:"${preferred}"}) → show_display\n  (2) show_display({columns:[...]}) — buffers 매칭 슬롯의 fields 사용\n새 call_api 금지. 1회 한정 — 다음 호출에서도 같은 응답이면 통과.`;
                    messages.push({ role: 'tool', tool_call_id: toolCall.id, content: blockMessage });
                    log.push({ type: 'finish_false_excuse_blocked', iteration, candidates: top.map(c => c.name), response: falseExcuseResponse.slice(0, 100) });
                    if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 FINISH BLOCKED: false excuse — buffers [${top.map(c => c.name).join(', ')}] available`);
                    continue;
                  }
                }
              }
            }

            // [Finish Sanity] response에 raw camelCase key 노출 시 차단 (최대 3회). entity hallucination은 show_display에서만 검증 (일반 카테고리 단어 false positive 회피)
            // [TOKEN-ANCHORED 예외] placeholder({{CHANGE:fieldName}}/{{CHANGE_RATE:fieldName}}) 내부 fieldName은 의도된 토큰이므로 sanity check에서 제외
            // [P1-D3xx] 검사 대상 확장: items[0] ∪ summary ∪ availableFields ∪ resolvedColumns — _merged buffer 의 summary scalar key (totalMembers/activeMembers/activeRate 등) 도 차단 (items[0] 만으로는 우회됨)
            const finishSanityBlockedCount = log.filter(l => l.type === 'finish_sanity_blocked').length;
            const responseText = toolArgs.response || '';
            if (responseText && finishSanityBlockedCount < 3) {
              const _session = sessionRef.current;
              let buf = null;
              try { buf = _session.getBuffer?.(); } catch {}
              if (buf) {
                const sample = buf.items?.[0];
                const summary = buf.summary;
                const availableFields = buf.availableFields;
                const resolvedColumns = buf.resolvedColumns;
                const resolvedColumnLabels = buf.resolvedColumnLabels;
                const allKeys = new Set();
                const labelAcc = {};
                if (sample && typeof sample === 'object') Object.keys(sample).forEach(k => allKeys.add(k));
                if (summary && typeof summary === 'object') Object.keys(summary).forEach(k => allKeys.add(k));
                if (availableFields && typeof availableFields === 'object') {
                  Object.entries(availableFields).forEach(([k, v]) => { allKeys.add(k); if (v && typeof v === 'string') labelAcc[k] = v; });
                }
                if (Array.isArray(resolvedColumns)) resolvedColumns.forEach(k => allKeys.add(k));
                // [P1-D3xx] 모든 slot의 summary scalar key + availableFields key 도 검사 대상 — _merged slot 에는 entity-level fields 만, summary scalar (totalMembers/activeMembers/activeRate 등) 는 개별 slot (base/compare_1) 의 buf.summary 에만 존재
                const _slotSummaries = _session.getSlotSummaries?.() || {};
                for (const _sn of Object.keys(_slotSummaries)) {
                  const _s = _slotSummaries[_sn]?.summary;
                  const _af = _slotSummaries[_sn]?.availableFields;
                  if (_s && typeof _s === 'object') Object.keys(_s).forEach(k => allKeys.add(k));
                  if (_af && typeof _af === 'object') Object.entries(_af).forEach(([k, v]) => { allKeys.add(k); if (v && typeof v === 'string' && !labelAcc[k]) labelAcc[k] = v; });
                }
                if (allKeys.size > 0) {
                  const responseForSanity = responseText.replace(/\{\{[^}]+\}\}/g, '');
                  const camelCaseRe = /^[a-z][a-zA-Z]*[A-Z][a-zA-Z]*$/;
                  const rawKeys = [...allKeys].filter(k => typeof k === 'string' && camelCaseRe.test(k));
                  const leaked = rawKeys.filter(k => responseForSanity.includes(k));
                  if (leaked.length > 0) {
                    const labelMap = { ...labelAcc, ...(resolvedColumnLabels || {}) };
                    const labelHints = leaked
                      .map(k => (labelMap[k] && typeof labelMap[k] === 'string' && labelMap[k] !== k) ? `${k}="${labelMap[k]}"` : null)
                      .filter(Boolean);
                    const labelLine = labelHints.length > 0 ? `\n한국어 라벨 매핑: ${labelHints.join(', ')}` : '';
                    const msg = `[FINISH BLOCKED — RAW KEY LEAK] response에 영문 raw 필드명 [${leaked.join(', ')}] 노출.${labelLine}\n한국어 라벨이 없는 필드는 응답에서 생략하거나 placeholder({{CHANGE:fieldName}}/{{CHANGE_RATE:fieldName}}/{{SUM:fieldName}} 등) 안에서만 사용. 한국어 라벨/placeholder로 재작성 후 다시 finish 호출하세요.`;
                    messages.push({ role: 'tool', tool_call_id: toolCall.id, content: msg });
                    log.push({ type: 'finish_sanity_blocked', iteration, reason: 'raw_key_leak', leaked });
                    if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 FINISH SANITY BLOCKED: raw keys [${leaked.join(', ')}]`);
                    continue;
                  }
                }
              }
            }

            // [COMPARISON FINISH BLOCK — SHOW-DISPLAY REQUIRED] #585-FOLLOWUP-C L9c-COMPARE-006 cycle 1 (2026-05-29 dual sync with nodeAgent.mjs)
            //   본질: 비교 입력(비교/대비/대조/차이/비슷)인데 LLM이 finish() 도구로 직행하면서 show_display 호출 없이 placeholder/text outro만으로 답변
            //         → 사용자가 본 "비교"는 표 응답 의도 — placeholder summary 한 줄로 비교 표 대체 불가
            //   해소: finish 직전에 차단 — 본 turn에 show_display 미호출 + buffer items ≥1 + 입력 COMPARE_KW → show_display 강제 nudge (1회 한정)
            //   패턴 기반: structural Korean keyword (단일 case hardcode X)
            {
              const _sessionCmp = sessionRef.current;
              const finishComparisonBlockedCount = log.filter(l => l.type === 'finish_comparison_display_blocked').length;
              const _COMPARE_KW_FIN = /비교|대비|대조|비슷|차이/;
              const _userInputForCompare = _sessionCmp?.getUserInput?.() || userInput || '';
              // [rid49 2차 Fix B+C — COMPARISON MERGE REQUIRED, this-turn scoped, show_display 전/후 무관] (dual sync with nodeAgent.mjs)
              //   기존(Fix C 4차 #9): !showDisplayResult 게이트 안 → LLM이 단일기간 show_display 먼저 호출 후 finish 도피하면 미발동(rid49 2/5).
              //   정정 ① 게이트 밖으로 분리 → show_display 후에도 발동. ② this-turn 로드 기간버퍼만(_turnCallApiSlots) → 체인 후속턴 stale 기간버퍼 오발동 차단(적대검증 Agent2).
              //   ③ _hasMergedSlot category-aware(_mergedSourcePrefix, 304e0186 태그) → stale cross-category _merged 무시(Fix B). once-cap+동일카테고리 merged 생성 시 자기억제 → 루프 안전.
              const _mergeBlockedCount = log.filter(l => l.type === 'finish_comparison_merge_blocked').length;
              if (_mergeBlockedCount === 0 && _COMPARE_KW_FIN.test(_userInputForCompare)) {
                const _slotEntries = _sessionCmp?.buffers ? [..._sessionCmp.buffers.entries()] : [];
                const _turnLoaded = _sessionCmp?._turnCallApiSlots || new Set();
                const _periodBufs = _slotEntries.filter(([n, b]) =>
                  n !== '_merged' && b?.intention !== 'MERGED'
                  && _turnLoaded.has(n)
                  && (b?.items?.length || 0) >= 1
                  && b?.dateRange?.startDate && b?.dateRange?.endDate
                );
                let _periodCatPrefix = null;
                for (const [, b] of _periodBufs) { const _m = String(b?.intention || '').match(/^(CHECK_[A-Z]+)/); if (_m) { _periodCatPrefix = _m[1]; break; } }
                const _hasMergedSlot = _slotEntries.some(([n, b]) => {
                  const _isMerged = n === '_merged' || b?.intention === 'MERGED';
                  if (!_isMerged) return false;
                  if (!_periodCatPrefix) return true;  // 카테고리 미상 → 보수적으로 병합 있음 처리
                  return b?._mergedSourcePrefix === _periodCatPrefix;  // 동일 카테고리 merged만 "병합완료"
                });
                const _distinctRanges = new Set(_periodBufs.map(([, b]) => `${b.dateRange.startDate}~${b.dateRange.endDate}`));
                if (!_hasMergedSlot && _periodBufs.length >= 2 && _distinctRanges.size >= 2) {
                  const _slotList = _periodBufs.map(([n, b]) => `{slotName:"${n}",label:"${b.dateRange.startDate.slice(0, 7)}"}`).join(', ');
                  const _rangeDesc = _periodBufs.map(([n, b]) => `"${n}"=${b.dateRange.startDate}~${b.dateRange.endDate}`).join(', ');
                  const _msgMerge = `[FINISH BLOCKED — COMPARISON MERGE REQUIRED] User input "${_userInputForCompare.slice(0, 60)}" is a comparison, but ${_periodBufs.length} separate period buffers were loaded this turn (${_rangeDesc}) and NOT yet merged. A single-period table is NOT a comparison. You MUST call merge_slots FIRST to combine the periods into one row-per-entity table with _change_/_changeRate_ fields, THEN show_display.\n\nCall: merge_slots({ slots:[${_slotList}], mode:"inner" }) then show_display({ columns:["period", "<metric>", "_change_<metric>", "_changeRate_<metric>"] }).`;
                  messages.push({ role: 'tool', tool_call_id: toolCall.id, content: _msgMerge });
                  log.push({ type: 'finish_comparison_merge_blocked', iteration, periodSlots: _periodBufs.map(([n]) => n), calledTools: Array.from(calledTools) });
                  if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 FINISH BLOCKED (comparison merge, this-turn): merge_slots required (${_periodBufs.length} period bufs, ${_distinctRanges.size} distinct ranges)`);
                  continue;
                }
              }
              if (finishComparisonBlockedCount === 0 && !showDisplayResult && _COMPARE_KW_FIN.test(_userInputForCompare)) {
                let _bufForCmp = null;
                try { _bufForCmp = _sessionCmp?.getBuffer?.(); } catch {}
                if (!_bufForCmp && _sessionCmp?.buffers) {
                  _bufForCmp = [..._sessionCmp.buffers.values()].find(b => (b.items?.length || 0) >= 1);
                }
                const _itemsCmp = _bufForCmp?.items || [];
                if (_itemsCmp.length >= 1) {
                  const _calledListCmp = Array.from(calledTools).join(', ') || '(none)';
                  const _keyHint = _itemsCmp[0] ? Object.keys(_itemsCmp[0]).filter(k => !k.startsWith('_')).slice(0, 10).join(', ') : '';
                  const msg = `[FINISH BLOCKED — COMPARISON DISPLAY REQUIRED] User input "${_userInputForCompare.slice(0, 60)}" contains comparison keyword (비교/대비/대조/차이/비슷). Buffer has ${_itemsCmp.length} merged items. Tools called this turn: [${_calledListCmp}] — show_display was NOT called. A comparison request demands a multi-column table, not a one-line text/placeholder summary. MUST call show_display first.\n\nCall: show_display({ intro: "<짧은 intro>", columns: [<key>, <기간1>_totalPrice or 매출 field, <기간2>_totalPrice, _change_*, _changeRate_*] })\nBuffer keys: ${_keyHint || '(no keys)'}`;
                  messages.push({ role: 'tool', tool_call_id: toolCall.id, content: msg });
                  log.push({ type: 'finish_comparison_display_blocked', iteration, itemCount: _itemsCmp.length, calledTools: Array.from(calledTools) });
                  if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 FINISH BLOCKED (comparison): show_display required (${_itemsCmp.length} items, calledTools=[${_calledListCmp}])`);
                  continue;
                }
              }
            }

            // [1-D-1] FL-2 finishPolicy.evaluateFinish 위임 — 3 guard 통합 (DISPLAY/OUTRO/NUMERIC)
            //   GUARD 1 DISPLAY_REQUIRED — !finishAllowedByGuard + items + list intent + !count + !meta
            //   GUARD 2 OUTRO_RAW_LEAK — !_outroRetryAttempted + items + detectRawNumericLeak
            //   GUARD 3 NUMERIC_HALLUCINATION — _finishNumericRetryCount===0 + items + detectIntroOutroNumericHallucination
            //   1회 cap 보존: DISPLAY는 log count로 cap (override를 effective flag로), OUTRO/NUMERIC은 retryCount input으로 자체 cap.
            // [GUARD 15 BARE-COUNT AMBIGUITY] meta 1회 산정 — 정책 input + 치환 backstop opts 공용.
            //   top-N 명시 turn은 {{COUNT}}가 user-anchored cap 정답이라 skip (적대검증 FP-5). dual sync with nodeAgent.mjs //
            const _bcAmbiguityMeta = detectExplicitTopNIntent(sessionRef.current?.getUserInput?.() || '')
              ? null
              : (sessionRef.current?.getBareCountAmbiguity?.() || null);
            {
              const _session = sessionRef.current;
              let buf = null;
              try { buf = _session?.getBuffer?.(); } catch {}
              const LIST_INTENT_KW = /보여줘|보여주세요|알려줘|알려주세요|확인해|확인 ?해줘|정리 ?해|목록|리스트|명단|현황|내역|들 ?(은|을|이|의|을)/;
              const COUNT_KW = /몇\s*[명건개]|얼마|얼만큼|총\s*\d|\b수가\b|\b수는\b|갯수|개수/;
              const META_KW = /어떻게|방법|하는\s*법|왜|언제|어디|기준|산정/;
              const displayBlockedCount = log.filter(l => l.type === 'finish_display_blocked').length;
              const effectiveFinishAllowed = !!_session?._finishAllowedByGuard || displayBlockedCount >= 1 || !!showDisplayResult;

              const decision = evaluateFinish({
                buffer: buf,
                rawAssistantText: stripPoliteReproposal(toolArgs.response || ''),
                retryCount: {
                  outroRetryAttempted: !!_session?._outroRetryAttempted,
                  numericHallucinationRetryCount: _session?._finishNumericRetryCount ?? 0,
                  noDataFetchRetryAttempted: !!_session?._noDataFetchRetryAttempted,
                  detailFallbackRetryAttempted: !!_session?._detailFallbackRetryAttempted,  // [anchor 16]
                  emptyResponseAfterVizRetryAttempted: !!_session?._emptyResponseAfterVizRetryAttempted,  // [1-F]
                  kpiValueMissingRetryAttempted: !!_session?._kpiValueMissingRetryAttempted,  // [1-A γ]
                  zeroCountAlternativeRetryAttempted: !!_session?._zeroCountAlternativeRetryAttempted,  // [1-E γ]
                  periodCountsMissingRetryAttempted: !!_session?._periodCountsMissingRetryAttempted,  // [1-F γ]
                  periodCountMismatchRetryAttempted: !!_session?._periodCountMismatchRetryAttempted,  // [1-N P26]
                  entityDetailMissingRetryAttempted: !!_session?._entityDetailMissingRetryAttempted,  // [1-K GUARD 10]
                  columnEnumRetryAttempted: !!_session?._columnEnumRetryAttempted,
                  bufferMismatchRetryAttempted: !!_session?._bufferMismatchFinishRetryAttempted,  // [C-#6]
                  bareCountRetryAttempted: !!_session?._bareCountFinishRetryAttempted,  // [GUARD 15]
                },
                sessionFlags: {
                  finishAllowedByGuard: effectiveFinishAllowed,
                  detailNotFoundContext: _session?._detailNotFoundContext,  // [anchor 16]
                  bufferMismatchUnresolved: !!_session?._bufferMismatchInfo && !effectiveFinishAllowed,  // [C-#6] show_display/viz buffer mismatch 발생 + 이번 turn 성공 표출 없음 (viz 성공은 _finishAllowedByGuard→effectiveFinishAllowed로 자연 skip)
                },
                bufferMismatchInfo: _session?._bufferMismatchInfo || null,  // [C-#6] GUARD 14 retryGuide용
                userInputClassification: {
                  isListIntent: LIST_INTENT_KW.test(userInput),
                  isCountIntent: COUNT_KW.test(userInput),
                  isMetaIntent: META_KW.test(userInput),
                },
                detectors: { detectRawNumericLeak, detectIntroOutroNumericHallucination, detectUnfulfilledSpecifier, detectColumnEnumLeak },
                toolsCalledThisTurn: Array.from(calledTools),
                kpiHintMeta: (_session?._ensureKpiHintMeta?.() || _session?._kpiHintMeta) || null,  // [1-A γ] show_display 미호출 path도 RATE buffer auto-detect
                zeroCountAlternativeMeta: _session?._zeroCountAlternativeMeta || null,  // [1-E γ] call_api 2+ count=0 동일 intention 감지 시 저장된 meta
                periodCountHintMeta: _session?._periodCountHintMeta || null,  // [1-F γ] merge_slots multi-period distinct dateRange 감지 시 저장된 meta
                periodCountMismatchMeta: _session?._periodCountMismatchMeta || null,  // [1-N P26] user input의 N월 enumeration vs call_api dateRange month set 비교
                bareCountAmbiguityMeta: _bcAmbiguityMeta,  // [GUARD 15] 멀티슬롯 divergent + text-only turn의 bare {{COUNT}} 참조 모호
              });

              if (decision.action === 'retry') {
                const retryMsg = buildRetryMessage(decision);
                messages.push({ role: 'tool', tool_call_id: toolCall.id, content: retryMsg });

                let logType;
                const logEntry = { iteration };
                if (decision.reason === FINISH_REASONS.DISPLAY_REQUIRED) {
                  logType = 'finish_display_blocked';
                  logEntry.itemCount = buf?.items?.length || 0;
                }
                else if (decision.reason === FINISH_REASONS.OUTRO_RAW_LEAK) {
                  logType = 'outro_raw_leak_retry';
                  if (_session) _session._outroRetryAttempted = true;
                  if (decision.retryGuide?.sample) logEntry.sample = decision.retryGuide.sample;
                }
                else if (decision.reason === FINISH_REASONS.NUMERIC_HALLUCINATION) {
                  logType = 'finish_numeric_hallucination_retry';
                  if (_session) _session._finishNumericRetryCount = 1;
                  if (decision.retryGuide?.sample) logEntry.sample = decision.retryGuide.sample;
                }
                else if (decision.reason === FINISH_REASONS.NO_DATA_FETCH) {
                  logType = 'finish_no_data_fetch_retry';
                  if (_session) _session._noDataFetchRetryAttempted = true;
                  if (decision.retryGuide?.toolsCalled) logEntry.toolsCalled = decision.retryGuide.toolsCalled;
                }
                else if (decision.reason === FINISH_REASONS.DETAIL_FALLBACK_MISLEADING) {
                  logType = 'finish_detail_fallback_retry';
                  if (_session) _session._detailFallbackRetryAttempted = true;
                  if (decision.retryGuide?.specifier) logEntry.specifier = decision.retryGuide.specifier;
                  if (decision.retryGuide?.sample) logEntry.sample = decision.retryGuide.sample;
                }
                else if (decision.reason === FINISH_REASONS.EMPTY_RESPONSE_AFTER_VIZ) {  // [1-F]
                  logType = 'finish_empty_response_after_viz';
                  if (_session) _session._emptyResponseAfterVizRetryAttempted = true;
                  if (decision.retryGuide?.vizTool) logEntry.vizTool = decision.retryGuide.vizTool;
                }
                else if (decision.reason === FINISH_REASONS.KPI_VALUE_MISSING) {  // [1-A γ]
                  logType = 'finish_kpi_value_missing_retry';
                  if (_session) _session._kpiValueMissingRetryAttempted = true;
                  if (decision.retryGuide?.key) logEntry.kpiKey = decision.retryGuide.key;
                  if (decision.retryGuide?.value !== undefined) logEntry.kpiValue = decision.retryGuide.value;
                }
                else if (decision.reason === FINISH_REASONS.ZERO_COUNT_ALTERNATIVE_UNTRIED) {  // [1-E γ]
                  logType = 'finish_zero_count_alternative_retry';
                  if (_session) _session._zeroCountAlternativeRetryAttempted = true;
                  if (decision.retryGuide?.intention) logEntry.zeroIntention = decision.retryGuide.intention;
                  if (decision.retryGuide?.alternatives) logEntry.alternatives = decision.retryGuide.alternatives;
                }
                else if (decision.reason === FINISH_REASONS.PERIOD_COUNTS_MISSING) {  // [1-F γ]
                  logType = 'finish_period_counts_missing_retry';
                  if (_session) _session._periodCountsMissingRetryAttempted = true;
                  if (decision.retryGuide?.missing) logEntry.missing = decision.retryGuide.missing;
                }
                else if (decision.reason === FINISH_REASONS.PERIOD_COUNT_MISMATCH) {  // [1-N P26]
                  logType = 'finish_period_count_mismatch_retry';
                  if (_session) _session._periodCountMismatchRetryAttempted = true;
                  if (decision.retryGuide?.missingMonths) logEntry.missingMonths = decision.retryGuide.missingMonths;
                  if (decision.retryGuide?.inputMonths) logEntry.inputMonths = decision.retryGuide.inputMonths;
                  if (decision.retryGuide?.calledMonths) logEntry.calledMonths = decision.retryGuide.calledMonths;
                }
                else if (decision.reason === FINISH_REASONS.ENTITY_DETAIL_MISSING) {  // [1-K GUARD 10]
                  logType = 'finish_entity_detail_missing_retry';
                  if (_session) _session._entityDetailMissingRetryAttempted = true;
                  if (decision.retryGuide?.mentioned !== undefined) logEntry.mentioned = decision.retryGuide.mentioned;
                  if (decision.retryGuide?.total !== undefined) logEntry.total = decision.retryGuide.total;
                }
                else if (decision.reason === FINISH_REASONS.COLUMN_ENUM_LEAK) {
                  logType = 'finish_column_enum_retry';
                  if (_session) _session._columnEnumRetryAttempted = true;
                  if (decision.retryGuide?.sample) logEntry.sample = decision.retryGuide.sample;
                }
                else if (decision.reason === FINISH_REASONS.EMPTY_BUFFER_COUNT_HALLUCINATION) {
                  logType = 'finish_empty_buffer_count_retry';
                  if (_session) _session._finishNumericRetryCount = 1;  // share retry counter with GUARD 3 NUMERIC_HALLUCINATION
                  if (decision.retryGuide?.sample) logEntry.sample = decision.retryGuide.sample;
                  if (decision.retryGuide?.claimedCount) logEntry.claimedCount = decision.retryGuide.claimedCount;
                  if (decision.retryGuide?.unit) logEntry.unit = decision.retryGuide.unit;
                }
                else if (decision.reason === FINISH_REASONS.BARE_COUNT_AMBIGUOUS) {  // [GUARD 15]
                  logType = 'finish_bare_count_ambiguous_retry';
                  if (_session) _session._bareCountFinishRetryAttempted = true;
                  if (decision.retryGuide?.slotCounts) logEntry.slotCounts = decision.retryGuide.slotCounts;
                }
                else if (decision.reason === FINISH_REASONS.BUFFER_MISMATCH_UNRESOLVED) {  // [C-#6]
                  logType = 'finish_buffer_mismatch_retry';
                  if (_session) _session._bufferMismatchFinishRetryAttempted = true;
                  if (decision.retryGuide?.requestedColumns) logEntry.requestedColumns = decision.retryGuide.requestedColumns;
                  if (decision.retryGuide?.activeSlot) logEntry.activeSlot = decision.retryGuide.activeSlot;
                }
                else {
                  logType = 'finish_blocked_by_policy';
                  logEntry.reason = decision.reason;
                }
                log.push({ type: logType, ...logEntry });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 FINISH BLOCKED [${decision.reason}]: ${decision.retryGuide?.sample || ''}`);
                continue;
              }
            }

            finishCalled = true;
            // [OUTRO REPROPOSAL SANITIZE] 표 정보 재제안 sentence 제거 (결정성)
            const _rawResponse = toolArgs.response || '';
            const _sanitizedResponse = stripPoliteReproposal(_rawResponse);
            if (TOOL_CALLING_LOG && _sanitizedResponse !== _rawResponse) {
              console.log('[Agent] 🧹 OUTRO 공손 재제안 sanitize');
            }
            // [OUTRO CHANGE GUARD] SUMMARY-OVERRIDE 비교 표 outro에 변화량 자연어 부재 시 자동 보강 (helper가 조건 검사)
            let _bufForOutro = null;
            try { _bufForOutro = sessionRef.current?.getBuffer?.(); } catch {}
            // [PLACEHOLDER-SUMMARY-SCOPE] finish path top-N awareness — 사용자가 "상위 5명만" 같이 명시적 limit 후 finish 응답에
            //   "{{SUMMARY:totalOutstandingPrice}}" 작성 시 full-scope value (83M) substitute 차단.
            //   detectExplicitTopNIntent helper 재사용 (show_display path와 동일 signal).
            //   dual sync: nodeAgent.mjs finish path 동일 처리.
            try {
              const _userInputForFinish = sessionRef.current?.getUserInput?.() || '';
              const _topNIntentForFinish = detectExplicitTopNIntent(_userInputForFinish);
              if (_bufForOutro?.items?.length && _topNIntentForFinish) {
                const _displayedCountFinish = Math.min(_bufForOutro.items.length, _topNIntentForFinish.limit);
                if (_bufForOutro.items.length > _displayedCountFinish) {
                  _bufForOutro = {
                    ..._bufForOutro,
                    _displayedCount: _displayedCountFinish,
                    _userExplicitTopNSatisfied: true,
                    _scopeTotal: _bufForOutro.items.length,
                  };
                }
              }
            } catch {}
            // [TOKEN-ANCHORED] LLM이 작성한 {{CHANGE:field}}/{{CHANGE_RATE:field}} placeholder를 buffer 정답으로 결정적 치환 (raw 수치 hallucination 차단)
            // [GUARD 15 backstop] retry 후에도 bare {{COUNT}} 잔존 시 marker 보존 → clause drop (거짓 수치 대신 정직한 생략)
            const _phResult = substituteOutroPlaceholders(_sanitizedResponse, _bufForOutro, sessionRef.current?.getSlotSummaryMap?.(), _bcAmbiguityMeta ? { bareCountAmbiguous: true } : undefined);
            if (TOOL_CALLING_LOG && _phResult.substituted) {
              console.log('[Agent] 🎯 OUTRO placeholder 치환');
            }
            // placeholder 치환 발동 시 augmentOutro skip (중복 보강 방지)
            finishResponse = _phResult.substituted
              ? _phResult.text
              : augmentOutroWithChange(_phResult.text, _bufForOutro);
            if (TOOL_CALLING_LOG && !_phResult.substituted && finishResponse !== _phResult.text) {
              console.log('[Agent] 📋 OUTRO 변화량 자동 보강');
            }
            // [B1 STRIP FALLBACK] 1회 retry 후에도 raw 수치 잔존 시 자동 strip
            if ((sessionRef.current?._finishNumericRetryCount || 0) >= 1) {
              const _stripped = stripRawNumberArtifacts(finishResponse);
              if (_stripped !== finishResponse) {
                if (TOOL_CALLING_LOG) console.log('[Agent] 🧹 finish raw number auto-strip after retry');
                finishResponse = _stripped;
              }
            }
            // [GUARD 12 STRIP FALLBACK] 1회 retry 후에도 컬럼명 나열 잔존 시 자동 strip (dual sync with nodeAgent.mjs)
            if (sessionRef.current?._columnEnumRetryAttempted) {
              const _bufForEnumStrip = sessionRef.current?.getBuffer?.() || null;
              const _enumStripped = stripColumnEnumLeak(finishResponse, _bufForEnumStrip);
              if (_enumStripped !== finishResponse) {
                if (TOOL_CALLING_LOG) console.log('[Agent] finish column enum leak auto-strip after retry');
                finishResponse = _enumStripped;
              }
            }

            // [FINISH NEVER-EMPTY] placeholder clause-drop/strip이 응답 전체를 비운 극단 — 중립 fallback.
            //   본질: 콤마 없는 단문 전체가 placeholder 절이면 drop 후 finalText=''로 최상위 generic 실패문 강등.
            //   text-only stop 경로의 never-empty fallback과 동형 (dual sync with nodeAgent.mjs) //
            if (_rawResponse.trim() && !String(finishResponse || '').trim()) {
              const _suggestedNE = sessionRef.current?._finishGuardSuggestedText;
              finishResponse = (_suggestedNE && _suggestedNE.trim()) ? _suggestedNE : RESPONSE_FAILED_RETRY;
              log.push({ type: 'finish_never_empty_fallback', iteration });
              if (TOOL_CALLING_LOG) console.log('[Agent] 🛟 finish never-empty fallback');
            }

            // [REREG-SCOPE GUARD finish path] finish 재등록 서술 교정 — 배타 모수 혼합·stale rate (dual sync nodeAgent.mjs) //
            if (finishResponse && sessionRef.current?._reRegMetaThisTurn) {
              const _rrCorrected = correctReRegScopeNarrative(finishResponse, sessionRef.current._reRegMetaThisTurn);
              if (_rrCorrected !== finishResponse) {
                if (TOOL_CALLING_LOG) console.log('[Agent] [REREG-SCOPE GUARD] finish response corrected');
                finishResponse = _rrCorrected;
              }
            }

            // [L9b-CROSS-009 cycle 1] ENTITY NOT FOUND — deterministic missing-entity acknowledgment auto-prepend (dual sync with nodeAgent.mjs)
            if (sessionRef.current?._entityNotFoundContext) {
              const { missingName, entityNames: _entityListArr } = sessionRef.current._entityNotFoundContext;
              const _alreadyAcked = (finishResponse || '').includes(missingName) &&
                /등록(?:되어\s*있지\s*않|되지\s*않|이?\s*없|이?\s*안\s*돼)|찾을\s*수\s*없|미등록|존재하지\s*않/.test(finishResponse || '');
              if (!_alreadyAcked) {
                const _lastCh = missingName.charCodeAt(missingName.length - 1);
                const _isKoreanSyllable = _lastCh >= 0xAC00 && _lastCh <= 0xD7A3;
                const _hasJongseong = _isKoreanSyllable && ((_lastCh - 0xAC00) % 28 !== 0);
                const _particle = _hasJongseong ? '은' : '는';
                const _listShort = _entityListArr.slice(0, 5).join(', ') + (_entityListArr.length > 5 ? ' 외' : '');
                const _ackLine = `${missingName}${_particle} 등록되어 있지 않아요. 현재 등록 목록은 ${_listShort}이에요.`;
                finishResponse = finishResponse && finishResponse.trim().length > 0
                  ? `${_ackLine}\n\n${finishResponse}`
                  : _ackLine;
                if (TOOL_CALLING_LOG) console.log(`[Agent] 📝 ENTITY NOT FOUND auto-prepend: "${missingName}"`);
              }
              sessionRef.current._entityNotFoundContext = null;
            }

            // [L11-DERIVE-FILTER-001 fix] PER-MEMBER REVENUE CAPABILITY DISCLOSURE — auto-inject (dual sync with nodeAgent.mjs)
            //   trigger: userInput contains per-member axis (1인당/회원별/회원당) + filter qualifier (평균 이상/이하/상위 N명 etc)
            //            AND buffer intention = CHECK_SALES+AVERAGE+REVENUE_STATISTICS_BY+PERIOD
            //            AND finishResponse does NOT already contain disclosure phrase
            //   action: append disclosure clause per aiPrompts.js:595 policy
            //   범용: 정책 line 595 keyword 그대로. case ID 하드코딩 없음
            {
              const _intentionForDisclosure = _bufForOutro?.intention || null;
              const _beforeDisclosure = finishResponse;
              finishResponse = injectPerMemberRevenueCapabilityDisclosure(
                finishResponse,
                userInput,
                _intentionForDisclosure,
              );
              if (finishResponse !== _beforeDisclosure) {
                if (TOOL_CALLING_LOG) console.log('[Agent] 📝 PER-MEMBER REVENUE CAPABILITY DISCLOSURE auto-append');
              }
            }

            // [1-D-2] OL-6 Momus reviewOutput — finish 직전 verdict check
            //   env-var USE_OUTPUT_REVIEWER off default → skipped='env-disabled' → verdict='OKAY' (effect 0)
            //   env on + risk-marker 있는 finalText 한정 gpt-4o-mini LLM-as-judge
            //   verdict='REJECT' + !_reviewerRetryAttempted → 1회 cap retry continue
            {
              const _reviewSession = sessionRef.current;
              const review = await reviewOutput({
                finalText: finishResponse,
                buffer: _bufForOutro,
                userInput,
                llmCall: async (msgs) => {
                  const r = await loggedCompletion('sub', {
                    model: TITLE_MODEL,
                    messages: msgs,
                    temperature: 0,
                    max_completion_tokens: 200,
                  });
                  return r.choices[0]?.message?.content || '';
                },
              });
              if (review.verdict === 'REJECT' && _reviewSession && !_reviewSession._reviewerRetryAttempted) {
                _reviewSession._reviewerRetryAttempted = true;
                finishCalled = false;
                const rejectMsg = `[REVIEWER REJECT] ${review.reason || ''}\nRetry hint: ${review.suggestedRetry || ''}`;
                messages.push({ role: 'tool', tool_call_id: toolCall.id, content: rejectMsg });
                log.push({ type: 'finish_reviewer_reject', iteration, reason: review.reason, suggestedRetry: review.suggestedRetry });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 REVIEWER REJECT: ${review.reason}`);
                continue;
              }
              if (TOOL_CALLING_LOG && review.skipped) console.log(`[Agent] 📝 reviewer skipped: ${review.skipped}`);
            }

            // OpenAI API 요구사항: tool_call_id에 대한 response 필수
            messages.push({
              role: "tool",
              tool_call_id: toolCall.id,
              content: "ok",
            });
            if (TOOL_CALLING_LOG) console.log('[Agent] 🏁 finish:', finishResponse.slice(0, 100));
            // [PLACEHOLDER METRICS] 정착률 측정용 메타 — variance turn(_change_* 보유) 분모, placeholder 정착 분자
            const _bufHasChange = !!(_bufForOutro?.items?.some(it => it && typeof it === 'object' && Object.keys(it).some(k => k.startsWith('_change_'))));
            log.push({ type: 'finish', response: finishResponse, hasDisplay: !!showDisplayResult, displayCount: showDisplayResult?.totalCount || null, iteration, placeholderUsed: !!_phResult.substituted, bufferHadChangeFields: _bufHasChange });
            break; // tool loop 탈출
          }

          // [CONTEXT REUSE REDIRECT] call_api 진입 시 buffers에 비교 데이터(_change_*) 존재 + 사용자 비교 의도 + 동일 카테고리 인텐션이면 차단 → switch_slot 안내 (1회 한정)
          // - 트리거: 본 turn 첫 call_api + redirect 미시도 + 비교 명령 input + buffers _change_* 보유 + 동일 카테고리
          // - 차단 후 LLM이 switch_slot 호출하면 정상. 두 번째 call_api는 통과 (false positive 안전망)
          {
            const _session = sessionRef.current;
            if (toolName === 'call_api' && toolArgs.intention
                && _session && !_session._contextReuseRedirectAttempted
                && !_session._calledApiThisTurn) {
              const _userInputCR = _session.getUserInput?.() || '';
              const COMPARE_CMD_RE = /비교(?:해(?:줘|봐|드려)?|했|하면|하니|해보)|(\d+)\s*월(?:.{0,10})(\d+)\s*월/;
              if (COMPARE_CMD_RE.test(_userInputCR)) {
                const m = String(toolArgs.intention).match(/^(CHECK_[A-Z]+)/);
                const intentionPrefix = m ? m[1] : null;
                const reuseSlot = _session._findContextReuseSlot?.(intentionPrefix);
                if (reuseSlot) {
                  _session._contextReuseRedirectAttempted = true;
                  const blockMsg = `[CALL_API BLOCKED — CONTEXT REUSE] buffers의 "${reuseSlot}" 슬롯에 비교 데이터(_change_*/_changeRate_* 파생 필드)가 이미 존재합니다. 사용자 입력 "${_userInputCR.slice(0, 50)}"의 비교 의도와 매칭되며, 새 call_api 호출은 기존 비교 데이터를 무시하고 다른 차원의 데이터를 로드하는 결과가 됩니다.\n\n즉시 다음 행동:\n  switch_slot({slot:"${reuseSlot}"}) → show_display({columns:[...]})\n\n1회 한정 차단 — 만약 의도가 정말 다른 데이터 로드라면 동일 call_api를 한 번 더 호출하면 통과합니다.`;
                  messages.push({ role: 'tool', tool_call_id: toolCall.id, content: blockMsg });
                  log.push({ type: 'call_api_redirect_blocked', iteration, reuseSlot, intention: toolArgs.intention });
                  if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 CALL_API BLOCKED (context reuse): "${reuseSlot}" available, intention=${toolArgs.intention}`);
                  continue;
                }
              }
            }
          }

          // [L9b-CROSS-009 cycle 2] ENTITY NOT FOUND CALL_API BLOCK — deterministic block to prevent silent name substitution (dual sync with nodeAgent.mjs)
          {
            const _sessionEnf = sessionRef.current;
            if (toolName === 'call_api' && _sessionEnf?._entityNotFoundContext && !_sessionEnf?._entityNotFoundCallApiBlocked) {
              const { missingName, entityNames: _eListBlock } = _sessionEnf._entityNotFoundContext;
              _sessionEnf._entityNotFoundCallApiBlocked = true;
              const _listShortBlock = _eListBlock.slice(0, 5).join(', ') + (_eListBlock.length > 5 ? ' 외' : '');
              const blockMsg = `[CALL_API BLOCKED — ENTITY NOT IN PREVIOUS BUFFER] "${missingName}" is NOT in the previously loaded entity list [${_listShortBlock}]. Calling call_api now risks (a) the entity not existing in the new dataset → wasteful failed query, or (b) the LLM silently substituting another entity from the result (e.g. retrieving 박대용's data when user asked about Jay) — the user would be misled.\n\nIMMEDIATE next action:\n  finish({response: "${missingName}는/은 등록되어 있지 않아요. 현재 등록 목록은 ${_listShortBlock}이에요. 다시 확인해 주세요."})\n\nUse the correct Korean particle (받침 검사 — '${missingName}' last char) — NEVER output dual-disclosure like "은(는)". Do NOT call another tool — ONLY finish.\n\n1-time block per turn — if the user REALLY wants to search for "${missingName}" in another category (broader search), call call_api again to bypass.`;
              messages.push({ role: 'tool', tool_call_id: toolCall.id, content: blockMsg });
              log.push({ type: 'entity_not_found_call_api_blocked', iteration, missingName, intention: toolArgs.intention });
              if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 CALL_API BLOCKED (entity not found): "${missingName}" not in prev buffer entity set`);
              continue;
            }
          }

          // [L10-SCHEDULE-WORKOUT-001 cycle 2 + L10-WORKOUT-SALES-001 baseline] WOD_EMPTY_CASCADE_GUARD — empty WOD result 후 cascade 차단 (deterministic, dual sync with nodeAgent.mjs)
          //   본질: 사용자가 "오늘 WOD 보여줘" (WOD 키워드 only, 참가자/공유/파트너 키워드 없음) 호출 시 WOD_LIST_BY+PERIOD가 0건 반환하면
          //         LLM이 fallback으로 (a) PARTICIPANT_LIST_BY+PERIOD ("운동 참가자도 없어") 또는 (b) PARTNER+GYM+WOD_LIST (파트너 체육관 공유 WOD 목록 표시)을 호출하고 intent drift
          //         사용자는 자기 체육관 오늘 WOD 컨텐츠를 물었는데 응답은 참가자(사람) 또는 외부 파트너 풀 — semantic mismatch
          //   해소: cascade 후보 intention 호출 진입 시 (a) 직전 WOD_LIST_BY+PERIOD 호출 + count=0 + (b) 사용자 input WOD/와드 키워드 + cascade 의도 키워드 부재 검사
          //         → 차단 + finish 강제 (1회 한정 — 사용자가 명시 의도 표현하면 같은 call 재호출로 통과)
          //   범용: 모든 "X" 조회 후 0건 → "Y" cascade 패턴 일반화 — case ID hardcode X (KEYWORD pattern + intention registry based, candidates 확장 가능).
          //   회귀 risk: 사용자가 "WOD랑 참가자 둘다" / "공유 WOD" 같이 명시 시 미발동 (각 의도 키워드 포함). single-intent WOD 만 trigger.
          {
            const _WOD_EMPTY_CASCADE_CANDIDATES = {
              'CHECK_WORKOUT+PARTICIPANT_LIST_BY+PERIOD': {
                permittedKwRegex: /참가|참여|명단/,
                label: 'PARTICIPANT',
                blockHint: 'PARTICIPANT_LIST_BY+PERIOD is a SEMANTICALLY DIFFERENT entity (people attending classes, not workout program content).',
                permittedKwLabel: '참가자/참여자',
              },
              'CHECK_WORKOUT+PARTNER+GYM+WOD_LIST': {
                permittedKwRegex: /공유|파트너|제휴/,
                label: 'PARTNER_GYM',
                blockHint: 'PARTNER+GYM+WOD_LIST is a SEMANTICALLY DIFFERENT scope (shared WODs from external partner gyms, not own gym today\'s WOD).',
                permittedKwLabel: '공유/파트너/제휴',
              },
            };
            const _sessionWOD = sessionRef.current;
            if (toolName === 'call_api' && _WOD_EMPTY_CASCADE_CANDIDATES[toolArgs.intention]
                && _sessionWOD && !_sessionWOD._wodEmptyParticipantBlocked) {
              const _cascadeRule = _WOD_EMPTY_CASCADE_CANDIDATES[toolArgs.intention];
              const _userInputWOD = _sessionWOD.getUserInput?.() || '';
              const _hasWODKw = /\bWOD\b|와드/i.test(_userInputWOD);
              const _hasPermittedKw = _cascadeRule.permittedKwRegex.test(_userInputWOD);
              if (_hasWODKw && !_hasPermittedKw) {
                const _priorWODResults = log.filter(l =>
                  l?.type === 'tool_result' &&
                  l.tool === 'call_api' &&
                  l.intention === 'CHECK_WORKOUT+WOD_LIST_BY+PERIOD' &&
                  l.success === true
                );
                const _lastWODEmpty = _priorWODResults.length > 0 &&
                  (_priorWODResults[_priorWODResults.length - 1].count === 0 ||
                   _priorWODResults[_priorWODResults.length - 1].count === null);
                if (_lastWODEmpty) {
                  _sessionWOD._wodEmptyParticipantBlocked = true;
                  _sessionWOD._finishAllowedByGuard = true;
                  const blockMsg = `[CALL_API BLOCKED — WOD EMPTY ${_cascadeRule.label} CASCADE] User asked for WOD content (Korean: "${_userInputWOD.slice(0, 60)}"), CHECK_WORKOUT+WOD_LIST_BY+PERIOD returned 0 items. ${_cascadeRule.blockHint} Cascading to ${_cascadeRule.label} as a fallback drifts the response away from the user's WOD-content ask.\n\nIMMEDIATE next action:\n  finish({response: "오늘 WOD는 등록된 내역이 없어요."}) — or appropriate period word (이번 주/이번 달) matching the user's input.\n\nDo NOT call any other workout-adjacent intention to "fill" the answer. The empty-WOD finding IS the correct answer.\n\n1-time block per turn — if the user REALLY wants ${_cascadeRule.permittedKwLabel} data, call call_api again to bypass (but the input did NOT mention ${_cascadeRule.permittedKwLabel}).`;
                  messages.push({ role: 'tool', tool_call_id: toolCall.id, content: blockMsg });
                  log.push({ type: 'wod_empty_participant_cascade_blocked', iteration, userInput: _userInputWOD.slice(0, 60), cascadeKind: _cascadeRule.label });
                  if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 CALL_API BLOCKED (WOD empty → ${_cascadeRule.label} cascade): user input "${_userInputWOD.slice(0, 50)}" lacks ${_cascadeRule.permittedKwLabel} keyword`);
                  continue;
                }
              }
            }
          }

          // [L1-WORKOUT-002 cycle 1] PARTNER_GYM_WOD_INTENT_REDIRECT — 제휴점/공유/파트너 + WOD 입력에서 own-gym workout intention 차단 (deterministic, dual sync with nodeAgent.mjs)
          //   본질: 사용자가 "제휴점 WOD 알려줘" 입력 시 LLM이 own-gym workout intention (WOD_LIST_BY+PERIOD / PARTICIPANT_LIST_BY+PERIOD)를 호출 → false-label intent drift
          //   해소: own-gym workout intention 호출 진입 시 (a) 제휴/공유/파트너 + (b) WOD/와드 동시 매칭 → 차단 + redirect hint
          //   범용: 한국어 partner-gym 명시 키워드 + WOD 명시 키워드 동시 패턴. case ID hardcode X.
          {
            const _sessionPGWod = sessionRef.current;
            const _OWN_GYM_WORKOUT_INTENTIONS_REDIRECT_M = new Set([
              'CHECK_WORKOUT+WOD_LIST_BY+PERIOD',
              'CHECK_WORKOUT+PARTICIPANT_LIST_BY+PERIOD',
            ]);
            if (toolName === 'call_api' && _OWN_GYM_WORKOUT_INTENTIONS_REDIRECT_M.has(toolArgs.intention)
                && _sessionPGWod && !_sessionPGWod._partnerGymWodIntentRedirectBlocked) {
              const _userInputPGWod = _sessionPGWod.getUserInput?.() || '';
              const _hasPartnerGymKw = /제휴|공유|파트너/.test(_userInputPGWod);
              const _hasWODKwP = /\bWOD\b|와드/i.test(_userInputPGWod);
              if (_hasPartnerGymKw && _hasWODKwP) {
                _sessionPGWod._partnerGymWodIntentRedirectBlocked = true;
                const _blockedIntentionM = toolArgs.intention;
                const blockMsg = `[CALL_API BLOCKED — PARTNER GYM WOD INTENT DRIFT] User asked for PARTNER gym WOD (Korean: "${_userInputPGWod.slice(0, 60)}" — contains 제휴/공유/파트너 + WOD/와드 keywords). ${_blockedIntentionM} returns OWN-GYM workout data, NOT external partner gyms' shared WOD pool — calling this with a "제휴점 WOD" framing yields false-label intent drift (intro reads 제휴점 but data is own gym).\n\nIMMEDIATE next action:\n  call_api({intention: "CHECK_WORKOUT+PARTNER+GYM+WOD_LIST"}) — partner gym shared WOD list (correct semantic match).\n\nThen show_display with columns=["storeName","title","lessonName","classDate"] so the source partner gym is identified for each WOD row.\n\n1-time block per turn — if you re-call any own-gym workout intention now, it will pass through.`;
                messages.push({ role: 'tool', tool_call_id: toolCall.id, content: blockMsg });
                log.push({ type: 'partner_gym_wod_intent_redirect_blocked', iteration, userInput: _userInputPGWod.slice(0, 60), blockedIntention: _blockedIntentionM });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 CALL_API BLOCKED (partner-gym WOD intent drift): ${_blockedIntentionM} on input "${_userInputPGWod.slice(0, 50)}" has 제휴/공유/파트너 + WOD — redirect to PARTNER+GYM+WOD_LIST`);
                continue;
              }
            }
          }

          // [P-층3] 멀티턴 args 자동 보존 — 같은 인텐션 직전 호출의 args를 base로 얕은 merge (사용자 명시 우선)
          if (toolName === 'call_api' && toolArgs.intention) {
            const previousCall = log
              .filter(l => l.type === 'tool_call' && l.tool === 'call_api')
              .reverse()
              .find(l => l.args?.intention === toolArgs.intention);
            if (previousCall?.args) {
              const beforeKeys = Object.keys(toolArgs);
              toolArgs = { ...previousCall.args, ...toolArgs };
              const preservedKeys = Object.keys(toolArgs).filter(k => !beforeKeys.includes(k));
              if (preservedKeys.length > 0 && TOOL_CALLING_LOG) {
                console.log(`[Agent] 🔄 Multi-turn args preserved (${toolArgs.intention}): ${preservedKeys.join(', ')}`);
              }
            }
          }

          // [L22-DATERANGE-001 cycle 2] NARROW PERIOD CHAIN — call_api dateRange auto-inject (2026-05-31, dual sync with nodeAgent.mjs)
          //   본질: 사용자가 narrowing 시간 키워드 + "만" 입력 시 LLM이 call_api 호출은 하나 startDate/endDate를 명시하지 않음
          //         → backend는 default(all-time) 반환 → 응답이 "이번달 X" framing이지만 실제 데이터는 lifetime aggregate (false label)
          //   해소: call_api 진입 시 (a) NARROW_PERIOD_RE 매칭 + (b) args에 startDate/endDate 누락 + (c) 직전 turn buffer items 보유
          //         → 사용자 narrow keyword를 명시 dateRange로 deterministic resolve 후 args 주입 (params + top-level dual sync)
          //   범용: 한국어 narrow particle "만" + time keyword 패턴. 어느 intention/category에도 적용. idempotent injection.
          //   회귀 risk: 사용자가 명시 startDate/endDate 지정 시 미발동.
          {
            const _sessionInject = sessionRef.current;
            if (toolName === 'call_api' && toolArgs.intention && _sessionInject) {
              const NARROW_PERIOD_INJECT_RE = /(오늘|어제|이번\s*주|이번\s*달|이달|금주|금월|지난\s*주|지난\s*달|\d+\s*월|\d+\s*주|\d+\s*일|최근\s*\d+\s*일)\s*만/;
              const _userInputInject = _sessionInject.getUserInput?.() || '';
              const _injectMatchObj = _userInputInject.match(NARROW_PERIOD_INJECT_RE);
              if (_injectMatchObj) {
                const _hasStartDate = !!(toolArgs.startDate || toolArgs.params?.startDate);
                const _hasEndDate = !!(toolArgs.endDate || toolArgs.params?.endDate);
                const _hasPrevBufItems = _sessionInject.buffers ? [..._sessionInject.buffers.values()].some(b => (b.items?.length || 0) >= 1) : false;
                if (!_hasStartDate && !_hasEndDate && _hasPrevBufItems) {
                  const _now = new Date();
                  const _pad2 = (n) => String(n).padStart(2, '0');
                  const _toDateOnly = (d) => `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}-${_pad2(d.getDate())}`;
                  const _kw = _injectMatchObj[1].replace(/\s+/g, '');
                  let _startDt = null, _endDt = null, _resolvedLabel = '';
                  const _monthMatch = _kw.match(/^(\d+)월$/);
                  const _recentDaysMatch = _kw.match(/^최근(\d+)일$/);
                  if (_kw === '오늘') {
                    _startDt = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate());
                    _endDt = new Date(_startDt);
                    _resolvedLabel = '오늘';
                  }
                  else if (_kw === '어제') {
                    _startDt = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() - 1);
                    _endDt = new Date(_startDt);
                    _resolvedLabel = '어제';
                  }
                  else if (_kw === '이번주' || _kw === '금주') {
                    const _dow = _now.getDay();
                    const _diffToMon = _dow === 0 ? 6 : _dow - 1;
                    _startDt = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() - _diffToMon);
                    _endDt = new Date(_startDt.getFullYear(), _startDt.getMonth(), _startDt.getDate() + 6);
                    _resolvedLabel = '이번주';
                  }
                  else if (_kw === '지난주') {
                    const _dow = _now.getDay();
                    const _diffToMon = _dow === 0 ? 6 : _dow - 1;
                    const _thisMonday = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() - _diffToMon);
                    _startDt = new Date(_thisMonday.getFullYear(), _thisMonday.getMonth(), _thisMonday.getDate() - 7);
                    _endDt = new Date(_thisMonday.getFullYear(), _thisMonday.getMonth(), _thisMonday.getDate() - 1);
                    _resolvedLabel = '지난주';
                  }
                  else if (_kw === '이번달' || _kw === '이달' || _kw === '금월') {
                    _startDt = new Date(_now.getFullYear(), _now.getMonth(), 1);
                    _endDt = new Date(_now.getFullYear(), _now.getMonth() + 1, 0);
                    _resolvedLabel = '이번달';
                  }
                  else if (_kw === '지난달') {
                    _startDt = new Date(_now.getFullYear(), _now.getMonth() - 1, 1);
                    _endDt = new Date(_now.getFullYear(), _now.getMonth(), 0);
                    _resolvedLabel = '지난달';
                  }
                  else if (_monthMatch) {
                    const _m = Number(_monthMatch[1]);
                    if (_m >= 1 && _m <= 12) {
                      _startDt = new Date(_now.getFullYear(), _m - 1, 1);
                      _endDt = new Date(_now.getFullYear(), _m, 0);
                      _resolvedLabel = `${_m}월`;
                    }
                  }
                  else if (_recentDaysMatch) {
                    const _n = Number(_recentDaysMatch[1]);
                    if (_n > 0) {
                      _endDt = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate());
                      _startDt = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() - _n + 1);
                      _resolvedLabel = `최근${_n}일`;
                    }
                  }
                  if (_startDt && _endDt) {
                    const _startDateOnly = _toDateOnly(_startDt);
                    const _endDateOnly = _toDateOnly(_endDt);
                    toolArgs = { ...toolArgs, startDate: _startDateOnly, endDate: _endDateOnly };
                    toolArgs.params = { ...(toolArgs.params || {}), startDate: _startDateOnly, endDate: _endDateOnly };
                    log.push({ type: 'narrow_period_daterange_injected', iteration, narrowKw: _kw, startDate: _startDateOnly, endDate: _endDateOnly, resolvedLabel: _resolvedLabel });
                    if (TOOL_CALLING_LOG) console.log(`[Agent] 📅 NARROW PERIOD dateRange injected: "${_kw}" → ${_startDateOnly}~${_endDateOnly}`);
                  }
                }
              }
            }
          }

          if (TOOL_CALLING_LOG) {
            console.log(`[Agent] 🔧 ${toolName}(${JSON.stringify(toolArgs)})`);
          }

          // [L22-DATERANGE-001 cycle 1] NARROW PERIOD CHAIN LOOP-LEVEL GUARD — show_display path (2026-05-31, dual sync with nodeAgent.mjs)
          //   본질: chain follow-up에서 사용자가 narrowing 시간 키워드 + 한정 어미("만") 입력 시
          //         buffer items가 date-segment field 없는 aggregate형이면 LLM이 filter(date) 시도 → fieldMissing autoRestored
          //         → 이어진 show_display가 cross_merge_blocked guard로 잘못 빠져 "여자/EXPIRING" 식 cross-merge 가이드 발화
          //         → 사용자는 단순 narrow period 요청인데 응답이 cross-merge 관련 혼란 메시지 또는 "5월 필드 없어서" 모호 회피
          //   해소: show_display 진입 시점에 (a) NARROW_PERIOD_RE + (b) 본 turn call_api 미호출 + (c) buffer items 보유
          //         → cross_merge guard보다 먼저 honest narrow-period inform 메시지로 LLM 유도 (1회 한정)
          //   범용 (case ID 무관): 한국어 "만" 한정 particle + 시간 키워드 패턴. 모든 intention/category에 적용.
          //   회귀 risk: 본 turn call_api 호출됐으면 통과 (정상 narrow + 재조회 흐름 보존). 1회 한정으로 무한 retry 차단.
          {
            const _sessionNarrow = sessionRef.current;
            if (toolName === 'show_display' && _sessionNarrow && !_sessionNarrow._calledApiThisTurn) {
              const _narrowLoopBlockedCount = log.filter(l => l.type === 'narrow_period_chain_loop_blocked').length;
              if (_narrowLoopBlockedCount === 0) {
                const NARROW_PERIOD_LOOP_RE = /(오늘|어제|이번\s*주|이번\s*달|이달|금주|금월|지난\s*주|지난\s*달|\d+\s*월|\d+\s*주|\d+\s*일|최근\s*\d+\s*일)\s*만/;
                const _userInputLoopNarrow = _sessionNarrow.getUserInput?.() || '';
                if (NARROW_PERIOD_LOOP_RE.test(_userInputLoopNarrow)) {
                  const _activeBufLoop = _sessionNarrow.getBuffer?.();
                  const _hasItemsLoopNarrow = !!(_activeBufLoop && (_activeBufLoop.items?.length || 0) >= 1);
                  if (_hasItemsLoopNarrow) {
                    const _narrowMatchLoop = (_userInputLoopNarrow.match(NARROW_PERIOD_LOOP_RE) || [])[0] || '';
                    const _bufIntentionLoop = _activeBufLoop.intention || 'active';
                    const _bufCountLoop = _activeBufLoop.items?.length || 0;
                    const _blockMessageNarrowLoop = `[NARROW PERIOD CHAIN — HONEST INFORM] User said "${_userInputLoopNarrow.slice(0, 50)}" (narrowing time keyword "${_narrowMatchLoop}" + 한정 particle "만"). Active buffer (intention=${_bufIntentionLoop}, ${_bufCountLoop} items) was loaded for a wider/different time period and is an aggregate without per-row date fields — show_display with the current buffer cannot honor the narrow-period request. Do NOT respond with confusing "${_narrowMatchLoop} 필드가 없어서" / "다시 불러오면 가능" / cross-merge guidance — these mislead the user. Instead, call finish IMMEDIATELY with text like: "${_narrowMatchLoop} 기준으로 좁히려면 새로 조회가 필요해요. 정확한 기간(예: 시작일~종료일)을 알려주시면 다시 가져올게요." This honestly tells the user the previous buffer cannot be re-used for a narrower period.`;
                    messages.push({ role: 'tool', tool_call_id: toolCall.id, content: _blockMessageNarrowLoop });
                    log.push({ type: 'narrow_period_chain_loop_blocked', iteration, tool: toolName, narrowMatch: _narrowMatchLoop, bufIntention: _bufIntentionLoop });
                    if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 SHOW_DISPLAY BLOCKED: narrow period chain inform (${_narrowMatchLoop})`);
                    continue;
                  }
                }
              }
            }
          }

          // [B+] show_display before cross-merge — fieldMissing 자동 무효화 후 mergeSlots(intersect) 미호출 상태에서 show_display 차단 (1회 한정)
          if (toolName === 'show_display') {
            const showDisplayBlockedCount = log.filter(l => l.type === 'show_display_cross_merge_blocked').length;
            if (showDisplayBlockedCount === 0) {
              const _toolResults = log.filter(l => l.type === 'tool_result');
              const fieldMissingResult = _toolResults.slice().reverse().find(r => r.tool === 'filter' && r.autoRestored);
              const mergeIntersectOk = _toolResults.some(r => r.tool === 'merge_slots' && r.success && (r.mergeMode === 'intersect' || r.mergeMode === 'inner'));
              // [B++] dateRange 변경 의도 감지 — fieldMissing 이후 동일 인텐션 + 새 슬롯 call_api 성공이 있으면 단순 데이터 재조회로 판단, 차단 해제
              const fieldMissingIdx = _toolResults.indexOf(fieldMissingResult);
              const priorCallApi = fieldMissingIdx > 0
                ? _toolResults.slice(0, fieldMissingIdx).reverse().find(r => r.tool === 'call_api' && r.success)
                : null;
              const sameIntentionNewSlotCallApi = fieldMissingIdx >= 0
                ? _toolResults.slice(fieldMissingIdx + 1).some(r =>
                    r.tool === 'call_api' && r.success && r.slot && r.slot !== 'default' && r.intention === priorCallApi?.intention)
                : false;
              // [B+++] fieldMissing 이후 다른 카테고리에서 동일 필드로 성공적인 filter가 있으면 자체 해결된 것으로 판단, 차단 해제
              const fieldResolvedByNewData = fieldMissingIdx >= 0
                ? _toolResults.slice(fieldMissingIdx + 1).some(r =>
                    r.tool === 'filter' && r.success && !r.autoRestored && r.filterField === fieldMissingResult.filterField && (r.filterAfter > 0 || r.after > 0))
                : false;
              if (fieldMissingResult && !mergeIntersectOk && !sameIntentionNewSlotCallApi && !fieldResolvedByNewData) {
                const fieldName = fieldMissingResult.filterField || 'the missing field';
                const blockMessage = `[SHOW_DISPLAY BLOCKED — CROSS-MERGE REQUIRED] filter("${fieldName}") earlier returned fieldMissing — original "default" data preserved. You have not called merge_slots(mode:"intersect") yet. show_display now would display the WRONG target (e.g., 33 women instead of EXPIRING ∩ 여자). Execute FIRST:\n  1) merge_slots([{slotName:"default"}, {slotName:"<new>"}], mode:"intersect", matchKey:"<memberId|userId|name>")\n  2) filter(field:"${fieldName}", ...) on the merged slot\n  3) show_display on the merged result\nRetry now.`;
                messages.push({ role: 'tool', tool_call_id: toolCall.id, content: blockMessage });
                log.push({ type: 'show_display_cross_merge_blocked', iteration, field: fieldName });
                if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 SHOW_DISPLAY BLOCKED: cross-merge pending for "${fieldName}"`);
                continue;
              }
            }
          }

          // [rid192 SMS-RECOMMEND-MISSING fix] SMS 추천 요청인데 recommend_sms 미호출 상태로 show_display 시도 → 차단, recommend_sms 선행 유도 (1회 한정, dual sync with nodeAgent.mjs)
          //   본질: "미수금 회원에게 문자 추천"에서 call_api+show_display(명단)로 끝내고 recommend_sms 미호출 → SMS 문구 누락(Cat3-SMS_MISSING, 운영 rid192).
          //   기존 SMS 가드(iteration===1 && calledTools.size===0)는 도구 선호출 케이스 미커버. detectSmsRecommendMissing이 유일 gate (이력조회/HOWTO/phone없음 제외).
          if (toolName === 'show_display') {
            const _smsRecMissingBlocked = log.filter(l => l.type === 'sms_recommend_missing_blocked').length;
            const _smsAlreadyCalled = log.some(l => l.type === 'tool_call' && l.tool === 'recommend_sms');
            if (_smsRecMissingBlocked === 0 && !_smsAlreadyCalled && sessionRef.current.detectSmsRecommendMissing(userInput)) {
              const _smsRecBuf = sessionRef.current.getBuffer?.();
              const _smsRecBlockMsg = `[SMS RECOMMEND REQUIRED — recommend_sms NOT CALLED] The user asked to recommend/compose an SMS for the loaded members (intention=${_smsRecBuf?.intention || 'active'}, ${_smsRecBuf?.items?.length || 0} members with phone numbers), but you have NOT called recommend_sms — rendering only the member table OMITS the SMS the user requested. MANDATORY: call recommend_sms NOW on the CURRENT buffer (mode="bulk" default — same text to all; use targetNames/targetIndex ONLY if the user named specific members or a row number). recommend_sms generates the SMS text — do NOT write it yourself. THEN show_display renders the list with the SMS panel attached. Do NOT finish with just the member table.`;
              messages.push({ role: 'tool', tool_call_id: toolCall.id, content: _smsRecBlockMsg });
              log.push({ type: 'sms_recommend_missing_blocked', iteration, bufIntention: _smsRecBuf?.intention });
              smsToolNeeded = true;  // [LAZY TOOL-SET D1] 가드가 recommend_sms 호출을 강제 — 도구 부재 데드락 차단
              if (TOOL_CALLING_LOG) console.log('[Agent] 🚫 SHOW_DISPLAY BLOCKED: SMS request but recommend_sms not called');
              continue;
            }
          }

          log.push({ type: 'tool_call', tool: toolName, args: toolArgs, iteration, timestamp: Date.now() });
          calledTools.add(toolName);

          if (toolName === 'call_api' && toolArgs.intention) {
            lastIntention = toolArgs.intention;
          }

          const toolResult = await sessionRef.current.executeTool(toolName, toolArgs);

          // [OB-1 가드 트레이스] 가드성 실패 구조화 기록 — message 선두 [마커] 추출, 계측 전용·행동 0변경 (dual sync with nodeAgent.mjs) //
          if (toolResult && toolResult.success === false) {
            sessionRef.current.recordGuardFiring?.(extractGuardMarker(toolResult.message) || 'UNMARKED_TOOL_FAIL', { tool: toolName, iteration });
          }

          // [REPEATED FAILURE GUARD] 동일 error 3회(직전 2 + 현재 1) 반복 시 무한 재시도 차단 (args는 살짝 변경되어도 발동)
          if (!toolResult.success && toolName === 'call_api') {
            const errMsg = toolResult.message || '';
            const priorSameFails = log.filter(l =>
              l?.type === 'tool_result' && l.tool === 'call_api' && l.success === false &&
              (l.message === errMsg || l.error === errMsg)
            ).length;
            if (priorSameFails >= 2) {
              toolResult.message = `[REPEATED FAILURE] ${errMsg}\n\nThis call_api has failed 3+ times with the same error. STOP retrying. Finish immediately with a brief user-facing message like "해당 정보를 불러오기 어려워요. 다른 조건으로 다시 시도해주세요." (텍스트만, 추가 도구 호출 금지).`;
              sessionRef.current._finishAllowedByGuard = true;
              if (TOOL_CALLING_LOG) console.log(`[Agent] 🚫 REPEATED FAILURE: call_api 3x same error → finish allowed`);
            }
          }

          // call_api 실패 시: 비-MEMBER_DETAIL 인텐션 실패 + 회원명 포함 입력 → MEMBER_DETAIL recovery
          if (toolName === 'call_api' && !toolResult.success) {
            const failedIntention = toolArgs.intention || '';
            const _userInput = sessionRef.current?.getUserInput?.() || '';
            // [MEMBER_DETAIL RECOVERY GUARD] 인프라 에러(슬롯 고갈 등)엔 미발동 — shouldFireMemberDetailRecovery (aiUtils 공유)
            if (shouldFireMemberDetailRecovery(_userInput, toolResult.message, failedIntention)) {
              toolResult.message = (toolResult.message || '') + `\n[RECOVERY] This intention failed. The user mentioned a specific member name — use MEMBER_DETAIL category instead. Call get_intentions(MEMBER_DETAIL) to find the correct CHECK_MEMBER+<TOPIC>_DETAIL intention, then call_api with the memberId from the earlier CHECK_MEMBER_SEARCH result. Do NOT fall back to a list intention (e.g. CHECK_LOCKER_LIST).`;
            }
            // CHECK_LOCKER_DETAIL fail with no lockerName → fallback to LIST overview //
            if (failedIntention === 'CHECK_LOCKER_DETAIL' && (!toolArgs.lockerName || String(toolArgs.lockerName).trim() === '')) {
              toolResult.message = (toolResult.message || '') + `\n[RECOVERY — LOCKER LIST FALLBACK] CHECK_LOCKER_DETAIL requires lockerName but none was provided. User input "${_userInput}" has no explicit locker number. Call call_api(intention="CHECK_LOCKER_LIST") instead to return overview of all lockers. In show_display.intro, use overview phrasing — '락커 현황' / '락커 목록' — NOT '락커 상세 정보'. NEVER refuse with "락커 번호 알려주세요" — LIST overview is available.`;
            }
          }

          if (TOOL_CALLING_LOG) {
            console.log(`[Agent] ✅ ${toolName} result:`, toolResult.success ? 'Success' : 'Failed');
            if (toolResult.data) console.log(`[Agent] 📦 API data count:`, Array.isArray(toolResult.data) ? toolResult.data.length : 1);
            if (toolResult.members) console.log(`[Agent] 👥 Members count:`, toolResult.members.length);
            if (toolResult.intentions) console.log(`[Agent] 🎯 Intentions:`, toolResult.intentions.length);
            if (!toolResult.success) console.log(`[Agent] ❌ Error:`, toolResult.message);
          }

          log.push({
            type: 'tool_result',
            tool: toolName,
            success: toolResult.success,
            iteration,
            // 공통
            count: toolResult.count || toolResult.after || toolResult.mergedCount || null,
            message: toolResult.message || null,
            error: toolResult.success ? null : toolResult.message,
            // get_intentions
            ...(toolName === 'get_intentions' && { category: toolArgs.category, intentionCount: toolResult.intentions?.length }),
            // call_api
            ...(toolName === 'call_api' && { intention: toolResult.intention, slot: toolResult.slot }),
            // filter
            ...(toolName === 'filter' && { filterBefore: toolResult.before, filterAfter: toolResult.after, filterField: toolArgs.field, filterCondition: toolArgs.condition?.type, fieldMissing: !!toolResult.fieldMissing, autoRestored: !!toolResult.autoRestored }),
            // sort
            ...(toolName === 'sort' && { sortField: toolArgs.field, sortDirection: toolArgs.direction }),
            // merge_slots
            ...(toolName === 'merge_slots' && { mergeMode: toolArgs.mode, mergeSlots: toolArgs.slots?.map(s => s.slotName), derivedFields: toolResult.derivedFields, unmatchedCounts: toolResult.unmatchedCounts, matchKey: toolResult.matchKey }),
            // compute
            ...(toolName === 'compute' && { computeOperation: toolArgs.operation, computeResult: toolResult.result }),
            // derive
            ...(toolName === 'derive' && { deriveName: toolArgs.name, deriveOperation: toolArgs.operation }),
            // show_display
            ...(toolName === 'show_display' && { displayCount: toolResult.displayResult?.totalCount, displayColumns: toolResult.displayResult?.columns }),
            // recommend_sms
            ...(toolName === 'recommend_sms' && { smsMode: toolArgs.mode, smsRecipientCount: toolResult.recipientCount }),
            // show_sales_view
            ...(toolName === 'show_sales_view' && { weekCount: toolResult.salesViewResult?.weeksData?.length }),
          });

          // recommend_sms 성공 시 → FAST_MODEL로 템플릿 생성 후 대기
          if (toolName === 'recommend_sms' && toolResult.success) {
            const smsData = await generateSmsTemplates?.(aiMessageId, toolResult);
            if (smsData) {
              pendingSmsRef.current = smsData;
              if (TOOL_CALLING_LOG) console.log('[SMS] 📱 Templates ready:', smsData.mode, smsData.templates?.length || Object.keys(smsData.individualTexts || {}).length);
            }

            // [L12-SMS-REGEN-001 fix] session에 SMS 상태 저장 — 다음 turn Buffer State에서 SMS 맥락 주입용 (dual sync with nodeAgent.mjs)
            sessionRef.current._lastSmsRecommendation = {
              mode: toolResult.mode,
              targetDescription: toolResult.targetDescription,
              recipientCount: toolResult.recipientCount,
            };
          }

          // show_display 성공 시 → displayResult 저장 (최종 응답에서 렌더링) //
          if (toolName === 'show_display' && toolResult.success && toolResult.displayResult) {
            showDisplayResult = toolResult.displayResult;
            if (TOOL_CALLING_LOG) console.log('[Agent] 🎨 show_display:', `${toolResult.count}건 준비`);
            // [L9b-CROSS-009 cycle 2] ENTITY NOT FOUND outro injection moved to renderDisplay() — see line ~885
          }

          // show_sales_view 성공 시 → salesViewResult 저장 (최종 응답에서 렌더링) //
          if (toolName === 'show_sales_view' && toolResult.success && toolResult.salesViewResult) {
            salesViewResult = toolResult.salesViewResult;
            if (TOOL_CALLING_LOG) console.log('[Agent] 📅 show_sales_view:', `${toolResult.count}주 준비`);
          }

          // show_revenue_chart 성공 시 → revenueChartResult 저장 //
          if (toolName === 'show_revenue_chart' && toolResult.success && toolResult.revenueChartResult) {
            revenueChartResult = toolResult.revenueChartResult;
            if (TOOL_CALLING_LOG) console.log('[Agent] 📊 show_revenue_chart:', `${toolResult.count}개월 준비`);
          }

          // [VIZ 6종 연결] 출석/랭킹/수업/연령/LTV/회원상태 차트 결과를 단일 vizResult로 캡처 → 메시지 vizData 렌더 (dispatch는 AiChattingView) //
          //   근본: 그동안 이 6개 도구는 호출·반환 정상이나 useAiChat 캡처 + AiChattingView 렌더 분기가 없어 화면에 안 떴음 (테스트는 도구/데이터만 검증)
          if (toolResult.success && !vizResult) {
            const _vizField = VIZ_RESULT_FIELDS[toolName];
            if (_vizField && toolResult[_vizField]) {
              vizResult = { type: _vizField, payload: toolResult[_vizField] };
              if (TOOL_CALLING_LOG) console.log(`[Agent] 📊 ${toolName}: viz 렌더 준비 (${_vizField})`);
            }
          }

          // tool 결과 구성 — LLM에 불필요한 데이터 제거 //
          let toolResultForMessage = stripBase64Values(toolResult);

          // recommend_sms 결과는 recipients 제거 (GPT에게 불필요, 토큰 절감) //
          if (toolName === 'recommend_sms' && toolResultForMessage.recipients) {
            const { recipients: _r, ...rest } = toolResultForMessage;
            toolResultForMessage = rest;
          }

          // show_display 결과는 displayResult 제거 (코드가 사용, LLM에 불필요) //
          if (toolName === 'show_display' && toolResultForMessage.displayResult) {
            const { displayResult: _d, ...rest } = toolResultForMessage;
            toolResultForMessage = rest;
          }

          // show_revenue_chart 결과는 revenueChartResult 제거 (LLM에 불필요) //
          if (toolName === 'show_revenue_chart' && toolResultForMessage.revenueChartResult) {
            const { revenueChartResult: _rc, ...rest } = toolResultForMessage;
            toolResultForMessage = rest;
          }

          const toolDataJson = JSON.stringify(toolResultForMessage, null, 0);
          let guideContent = '';

          // [2단계] get_intentions 결과에 INTENTION_GUIDE + WORKFLOW_GUIDE + FILTER_GUIDE 포함
          if (toolName === 'get_intentions' && !intentionGuideInjected) {
            guideContent += `[INSTRUCTION] ${prompts.intentionGuide}\n\n${prompts.workflowGuide}\n\n${prompts.filterGuide}`;

            // 비교/변화 질문이면 COMPARISON_GUIDE 추가 주입
            const isComparisonAtIntention = COMPARISON_REGEX.test(userInput); // [게이트 단일소스 ⑤] aiUtils.COMPARISON_REGEX (dual sync with nodeAgent.mjs)
            if (isComparisonAtIntention) {
              guideContent += `\n\n${prompts.comparisonGuide}`;
              if (TOOL_CALLING_LOG) console.log('[Agent] 📋 Comparison guide injected');
            }

            // [LOCKER LIST FORCE] INFRA category + "락커 상세/정보" + 번호 없음 → LIST 강제 hint (DETAIL 거절 차단) //
            if (toolArgs.category === 'INFRA' && /락커.{0,4}(?:상세|정보)/.test(userInput) && !/\d+\s*번|\d+\s*호|[A-Za-z]+-\d+|\d+\s*락커/.test(userInput)) {
              guideContent += `\n\n[LOCKER QUERY ROUTING — MANDATORY] User input "${userInput}" mentions 락커 상세/정보 but contains NO specific locker number. You MUST call call_api(intention="CHECK_LOCKER_LIST") to return overview of all lockers. PROHIBITED:\n- call_api(intention="CHECK_LOCKER_DETAIL") without lockerName (will fail — no number provided)\n- finish() with "락커 번호 알려주세요" / "번호를 알려주면" / "회원 이름이 필요해" — LIST overview is callable and the correct answer for ambiguous "락커 상세".\nAfter call_api(CHECK_LOCKER_LIST), in show_display.intro use overview phrasing — '락커 현황' / '락커 목록' — NOT '락커 상세 정보'.`;
            }

            // [MEMBER_DETAIL SEARCH FORCE — 은퇴 2026-07-05] mini 이름 재요청(5/15) 보상 게이트였음. gpt-5.4 off 5run+회귀 0으로 제거 — 재추가 전 은퇴 심사 기록 확인 (dual sync with nodeAgent.mjs) //

            intentionGuideInjected = true;
            if (TOOL_CALLING_LOG) console.log('[Agent] 📋 INTENTION + WORKFLOW + FILTER guide injected');
            log.push({ type: 'guide_injection', guides: isComparisonAtIntention ? ['INTENTION', 'WORKFLOW', 'FILTER', 'COMPARISON'] : ['INTENTION', 'WORKFLOW', 'FILTER'], iteration });
          }

          // [CATEGORY-SPLIT] get_intentions(category)별 BY_CATEGORY 라우팅 lazy 주입 (카테고리별 1회, dual sync nodeAgent) — 무관 카테고리 미로딩(토큰 절감), 활성 카테고리는 get_intentions 직후 동일 위치(proximity 유지)
          if (toolName === 'get_intentions') {
            const _cg = resolvePrompt('WORKFLOW_GUIDE_' + toolArgs.category, '');
            if (_cg && !_injectedCatGuides.has(toolArgs.category)) {
              guideContent += `${guideContent ? '\n\n' : ''}${_cg}`;
              _injectedCatGuides.add(toolArgs.category);
            }
          }

          // [MULTI-AXIS AUTO-RENDER] 본 turn call_api로 로드된 grouped(_groupBy) 축 슬롯 기록 → turn-end 미표출 축 자동 렌더 (dual sync with nodeAgent)
          if (toolName === 'call_api' && toolResult.success && toolResult.slot) {
            const _gb = sessionRef.current?.buffers?.get(toolResult.slot);
            // [MULTI-AXIS AUTO-RENDER — API-GROUPED] call_api가 params.groupBy로 적재한 슬롯(요일/시간대 등)은 compute(group)와
            //   달리 _currentAxisKey 미설정 → 기존 조건만으론 등록 누락 → auto-render 미발동(LLM이 한 축만 표출 시 다른 축 소실).
            //   groupBy param 구조 신호로도 등록(_currentAxisKey는 미설정 유지 → 광역 참조 메커니즘 영향 0 / slotName fallback 렌더).
            //   dual sync with nodeAgent.mjs.
            const _argGroupBy = (toolArgs && toolArgs.params && toolArgs.params.groupBy) || (toolArgs && toolArgs.groupBy);
            const _isGroupedAxisSlot = _gb && _gb.items && _gb.items.length && (
              (typeof _gb._currentAxisKey === 'string' && _gb._currentAxisKey.startsWith('_groupBy='))
              || (typeof _argGroupBy === 'string' && !!_argGroupBy)
            );
            if (_isGroupedAxisSlot) {
              groupedSlotsThisTurn.add(toolResult.slot);
            }
          }

          // 도구 후처리 가이드 주입 (DB 프롬프트 우선, fallback 상수)
          if (toolName === 'call_api' && toolResult.success) {
            const isSmsRequest = /문자|sms|SMS|추천.*보내|보내.*추천/i.test(userInput);
            const isComparisonRequest = COMPARISON_REGEX.test(userInput); // [게이트 단일소스 ⑤] aiUtils.COMPARISON_REGEX (dual sync with nodeAgent.mjs)
            const isVisualizationRequest = /그래프|차트|chart|graph|캘린더|달력|calendar|시각화|시각적|visual|그려|대시보드|dashboard/i.test(userInput);
            const localGuide = toolResult.intention ? CALL_API_GUIDES[toolResult.intention] : null;
            const originalGuide = localGuide || toolResult.callApiGuide || prompts.toolGuideCallApi;
            let intentionGuide = isSmsRequest
              ? prompts.toolGuideCallApiSms
              : originalGuide;

            // [P-층2] [REQUIRED PARAM] 메타 인스트럭션 자동 검증
            const requiredMatch = originalGuide && originalGuide.match(/\[REQUIRED\s*PARAM\]\s*(\w+)\s*:/i);
            if (requiredMatch && toolArgs[requiredMatch[1]] === undefined) {
              guideContent += `[ARG VALIDATION] "${requiredMatch[1]}" is REQUIRED for ${toolArgs.intention} but was OMITTED in this call. The data shape may not match user's intent. Re-call call_api with explicit ${requiredMatch[1]} based on user's question.\n\n`;
              if (TOOL_CALLING_LOG) console.log(`[Agent] ⚠️ REQUIRED PARAM missing: ${requiredMatch[1]} for ${toolArgs.intention}`);
            }

            // Override "text only" guide when data has 3+ items — use show_display //
            // [O] 가이드에 "show_display 호출 불필요" 메타 인스트럭션 명시된 경우만 텍스트 응답 허용
            const itemCount = toolResult.count || 0;
            const explicitlyTextOnly = intentionGuide && /show_display\s*(호출\s*)?(불필요|not\s*needed|unnecessary)/i.test(intentionGuide);

            const isVisualizationOnlyIntention = sessionRef.current?.getBuffer?.()?.visualizationOnly === true;
            const hasDefaultViz = !!(toolResult.defaultDisplay && VISUALIZATION_TOOLS.includes(toolResult.defaultDisplay));

            // [LAZY TOOL-SET D1] mid-turn viz 래치 — defaultDisplay 지정(비-VIZ 목록 포함)·visualizationOnly 인텐션은 다음 iteration부터 viz 도구 노출 //
            if (!vizToolsNeeded && (isVisualizationOnlyIntention || !!toolResult.defaultDisplay)) {
              vizToolsNeeded = true;
            }

            // viz path는 low-count(1+)에도 발동 — 차트는 1개 데이터로도 렌더 가능. 표 path는 기존 count≥3 유지
            const _vizThreshold = (isVisualizationRequest || isVisualizationOnlyIntention || hasDefaultViz) ? 1 : 3;
            if (itemCount >= 3 && intentionGuide && !explicitlyTextOnly && !isVisualizationRequest && !isVisualizationOnlyIntention && !hasDefaultViz) {
              // [B2 회귀 fix — backend 원본 가이드 보존] override 시 backend callApiGuide(인텐션별 정답 placeholder 안내 포함)를 prefix로 유지 → KPI 메시지를 그 뒤에 append. backend 원본 안내 사라져 LLM placeholder 잘못 선택하는 케이스(L1-SALES-005 → SUM vs SUMMARY) 차단
              // localGuide(CALL_API_GUIDES)가 있으면 우선 보존 — 인텐션별 정밀 placeholder/metric 가이드 포함
              const _priorityGuide = localGuide || toolResult.callApiGuide || '';
              intentionGuide = `${_priorityGuide ? _priorityGuide + '\n\n' : ''}[NEXT STEP] ${itemCount} items loaded. REQUIRED steps:\n1) Call show_display first to render as a table.\n2) Then call finish — your response MUST include the headline KPI (sum, average, ratio, total) as intro or outro text, NOT just raw rows. For statistical data, surface the bottom-line number (e.g. "총매출 X원", "재등록률 Y%", "총 N건") prominently. Pick the placeholder per [SELECTION RULE] in BUFFER_STATE — Backend Summary keys take priority over items aggregation for the SAME metric.`;
              if (TOOL_CALLING_LOG) console.log(`[Agent] 📋 Guide overridden: ${itemCount} items → show_display forced (with KPI outro)${_priorityGuide ? ' + priority guide preserved' : ''}`);
            }
            else if (itemCount >= _vizThreshold && intentionGuide && !explicitlyTextOnly && (isVisualizationRequest || isVisualizationOnlyIntention || hasDefaultViz)) {
              // 시각화 요청/visualizationOnly 인텐션/defaultDisplay 지정 인텐션 — show_display 강제 X, callApiGuide의 라우팅 + defaultDisplay 우선 적용. 텍스트-only 회피 보호는 유지
              const _priorityGuide = localGuide || toolResult.callApiGuide || '';
              const _vizHint = hasDefaultViz
                ? `**MUST call ${toolResult.defaultDisplay}** for this intention. Chart/visualization is the canonical display — render even with few data points (1 bar is still a chart). Call show_display ONLY if user explicitly typed 표/리스트/목록/list/table in the input.`
                : `Follow the routing above to select the appropriate display tool (show_display for tables, show_revenue_chart/show_sales_view/show_*_chart for visualizations).`;
              intentionGuide = `${_priorityGuide ? _priorityGuide + '\n\n' : ''}[NEXT STEP] ${itemCount} items loaded. ${_vizHint} After the display tool, call finish — your response MUST include the headline KPI (sum, average, ratio, total) as intro or outro text.`;
              if (TOOL_CALLING_LOG) console.log(`[Agent] 📋 Guide (viz-aware): ${itemCount} items → ${hasDefaultViz ? `defaultDisplay=${toolResult.defaultDisplay}` : 'display tool choice deferred'}${_priorityGuide ? ' + priority guide preserved' : ''}`);
            }

            // 인텐션별 가이드 주입 (DB 가이드 우선, 없으면 default) //
            // [GUIDE DEDUP D2] 동일 인텐션+동일 변형(sms/kpi/viz/plain) 재호출은 1줄 포인터로 대체, universalRules(8.8k chars)는 turn당 1회 —
            //   3기간 비교(같은 인텐션 3연속 call) 시 동일 가이드 3중 주입 제거 (토큰 2차 다이어트, dual sync with nodeAgent.mjs)
            const comp = sessionRef.current.getComparisonStatus();
            const _guideVariant = isSmsRequest ? 'sms'
              : ((itemCount >= 3 && !explicitlyTextOnly && !isVisualizationRequest && !isVisualizationOnlyIntention && !hasDefaultViz) ? 'kpi'
                : ((itemCount >= _vizThreshold && !explicitlyTextOnly && (isVisualizationRequest || isVisualizationOnlyIntention || hasDefaultViz)) ? 'viz' : 'plain'));
            const _guideKey = `${toolResult.intention || 'default'}|${_guideVariant}`;

            if (!_injectedCallApiGuides.has(_guideKey)) {
              _injectedCallApiGuides.add(_guideKey);
              guideContent += intentionGuide;
            }
            else {
              guideContent += `[GUIDE] Intention "${toolResult.intention || 'default'}" already called this turn — follow its [NEXT STEP] guide provided above.`;
            }

            if (!universalRulesInjected) {
              universalRulesInjected = true;
              guideContent += `\n\n${prompts.callApiUniversalRules}`;
            }

            // [DISTRIBUTION ROUTING] user input에 분포/비율/구성 키워드 + 특정 필드 감지 → compute(group) 강제 유도 (dual sync with nodeAgent.mjs)
            const _distUserInput = sessionRef.current?.getUserInput?.() || '';
            if (/분포|비율|구성|분류/.test(_distUserInput) && (toolResult.count || 0) >= 2) {
              const _distFieldHint = /성별/.test(_distUserInput) ? 'gender'
                : /연령|나이/.test(_distUserInput) ? 'birthDay", compute:"age'
                : /회원권/.test(_distUserInput) ? 'viewMembershipName'
                : null;

              if (_distFieldHint) {
                guideContent += `\n\n[DISTRIBUTION — MANDATORY] User asked for distribution/ratio (Korean: "${_distUserInput.slice(0, 30)}"). You MUST call compute(operation:"group", field:"${_distFieldHint}") DIRECTLY on full data. Do NOT call filter first — filtering removes other groups. compute(group) replaces buffer with grouped rows ({field, _count}). Then call show_display with columns: ["${_distFieldHint.split('"')[0]}", "_count"].`;
              }
            }

            // [P-rev] 데이터 기간 명시 — 멀티턴 turn에서 LLM이 정확한 도구 선택하도록
            if (toolArgs.startDate && toolArgs.endDate) {
              guideContent += `\n\n[DATA PERIOD] Loaded data covers ${toolArgs.startDate} ~ ${toolArgs.endDate}. For follow-up turn: if user requests a DIFFERENT period, re-call call_api with new dates (memory preserves enum like groupBy). If user wants filtering within the SAME period, use filter on existing data.`;
            }

            // [LOCKER LIST INTRO GUARD] user input에 "상세"/"정보" 단어가 있더라도 CHECK_LOCKER_LIST(overview) 응답에서는 intro에 "상세" echo 금지 //
            if (toolArgs.intention === 'CHECK_LOCKER_LIST' && /(?:상세|정보)/.test(userInput)) {
              guideContent += `\n\n[LOCKER LIST INTRO — MANDATORY] User input "${userInput}" contains '상세'/'정보' but you correctly chose CHECK_LOCKER_LIST (overview, since no locker number was specified). show_display.intro and finish() MUST use overview phrasing — '락커 현황' / '락커 목록' / '전체 락커' — NEVER echo '락커 상세 정보' / '락커 상세 내역' on LIST data. Echoing '상세' on overview misleads the user into expecting per-locker drilldown. Example: WRONG '락커 상세 정보 50건이야' / RIGHT '락커 현황 총 50건이야 (사용 중 N건, 비어있음 M건)'.`;
            }
            if (TOOL_CALLING_LOG) console.log(`[Agent] 📋 Intention guide (${toolResult.callApiGuide ? 'DB' : 'DEFAULT'}) + Universal rules injected`);

            // _merged 비교 데이터가 있고, 새 슬롯에 데이터를 로드했으면 intersect 유도
            const mergeOutputSlot = sessionRef.current._mergeOutputSlot;
            const mergedBuf = mergeOutputSlot ? sessionRef.current.getBuffer(mergeOutputSlot) : null;
            const hasChangeFields = mergedBuf?.items?.some(i =>
              Object.keys(i).some(k => k.startsWith('_change_'))
            );
            const newSlot = toolArgs.slot;

            if (hasChangeFields && newSlot && newSlot !== mergeOutputSlot) {
              guideContent += `\n\n[INTERSECT WITH PREVIOUS] You have comparison data in "${mergeOutputSlot}" slot with _change_ fields. New "${newSlot}" data just loaded. To combine them, call merge_slots with slots [{slotName:"${mergeOutputSlot}"}, {slotName:"${newSlot}"}] and mode:"inner". LLM decides matchKey and outputSlot based on data context.`;
              if (TOOL_CALLING_LOG) console.log(`[Agent] 📋 Intersect with ${mergeOutputSlot} guide injected`);
            }

            // 매출 인텐션 + 결제 수단 키워드 → payment_method 필터 강제 가이드 주입
            const isPaymentRequest = /카드|현금|포인트|링코페이|혼합|결제.*수단|수단.*별/i.test(userInput);
            const isSalesIntention = toolResult.intention && /SALES/i.test(toolResult.intention);

            if (isPaymentRequest && isSalesIntention) {
              guideContent += `\n\n[MANDATORY] 결제 수단 필터링 시 반드시 filter({ condition: { type: "payment_method", value: "..." } })를 사용하세요. cardPrice/cashPrice/itemName 등 다른 필드로 결제 수단을 필터링하지 마세요.`;
              if (TOOL_CALLING_LOG) console.log('[Agent] 📋 Payment method filter guide force-injected');
            }

            // 비교/변화 질문이면 버퍼 실존 기반으로 다음 단계 가이드 주입
            if (isComparisonRequest && !isSmsRequest && !comp.hasMerged) {
              if (comp.compareSlots.length > 0 && !comp.hasBase) {
                // [P26 fix] period-count-aware guide — N>=3 path tells LLM to load remaining compare slots first (dual sync with nodeAgent.mjs)
                const _requestedPeriodCount = sessionRef.current?._extractMonthSet?.(userInput)?.size || 0;
                guideContent += `\n\n${buildComparisonNextGuide({
                  requestedPeriodCount: _requestedPeriodCount,
                  loadedCompareCount: comp.compareSlots.length,
                  hasBase: false,
                })}`;
              }
              else if (comp.compareSlots.length > 0 && comp.hasBase) {
                guideContent += `\n\n${resolvePrompt('TOOL_GUIDE_COMPARISON_NEXT_MERGE', '')}`;
              }
              else if (comp.compareSlots.length === 0 && itemCount > 0) {
                // compare 슬롯 없이 데이터 로드됨 — 다기간 1회 API이거나 아직 비교 미시작
                // 데이터가 이미 충분하면 finish/show_display로 진행 유도
                guideContent += `\n\n[DATA LOADED] ${itemCount} items loaded. If this data already answers the user's question, proceed to show_display or finish(response). Do NOT call get_intentions again for the same category.`;
              }

              if (TOOL_CALLING_LOG) console.log(`[Agent] 📋 Comparison guide (status: base=${comp.hasBase}, compares=${comp.compareSlots.length}, merged=${comp.hasMerged})`);
            }

            // [P2-E CATALOG VALIDATION] catalog intention 호출 직후 LLM-as-judge guide
            // 사용자가 specific entity vs list 의도 의미적 판단 → 코드 가드는 fallback 안전망만
            if (toolResult.intention && /CATALOG_LIST/.test(toolResult.intention) && itemCount > 0) {
              const _catalogItems = sessionRef.current.getBuffer()?.items || [];
              const _catalogPreview = _catalogItems.slice(0, 15).map(it => {
                if (!it || typeof it !== 'object') return null;
                const names = Object.values(it).filter(v => typeof v === 'string' && v.trim() && v.length < 60).slice(0, 3);
                return names.length > 0 ? names.join(' / ') : null;
              }).filter(Boolean);
              if (_catalogPreview.length > 0) {
                guideContent += `\n\n[CATALOG VALIDATION] User input: "${userInput}". Active catalog (${itemCount} items):\n${_catalogPreview.map(p => `  - ${p}`).join('\n')}\n\nDistinguish user intent semantically (not by keyword):\n- SPECIFIC entity (e.g. "필라테스 수업 예약 상세", "X 회원권 가격"): if X is NOT in the catalog above, finish IMMEDIATELY with a response in the user's language naturally indicating X is not in the catalog. For Korean responses: use exactly ONE correct particle (받침 검사 — '필라테스' → '는', '회원권' → '은') — NEVER output "은(는)" / "이(가)" / "을(를)" dual-disclosure. Do NOT call show_display. If X IS in catalog, proceed with show_display.\n- LIST intent (e.g. "회원권 종류", "판매 상품 목록", "전체 코치", "모든 수업"): proceed with show_display normally — the user wants the whole catalog.\n\nThis disambiguation is SEMANTIC. Catalog list intent often uses "목록/종류/타입/전체/모든/list/all" but not always — judge by meaning, not keyword.`;
                if (TOOL_CALLING_LOG) console.log(`[Agent] 📋 CATALOG VALIDATION guide injected (${_catalogPreview.length} preview items)`);
              }
            }

            // 이전 대화에서 디스플레이가 있었으면 CROSS-REFERENCE 가이드 추가 (항목 이름 포함)
            const prevDisplayMsg = chatMessagesRef.current.findLast(m => m.displayItems);
            if (prevDisplayMsg?.displayItems) {
              const prevDisplay = prevDisplayMsg.displayItems;
              const prevItems = prevDisplay.items || [];
              const prevCols = prevDisplay.columns || [];
              // 첫 번째 텍스트 컬럼에서 항목 식별값 추출 (필드명 하드코딩 없음)
              const identCol = prevCols.find(c => {
                const sample = prevItems[0]?.[c];
                return typeof sample === 'string' && !/^\d{4}-/.test(sample);
              });

              if (identCol && prevItems.length > 0) {
                const topValues = prevItems.slice(0, 3).map((item, i) =>
                  item[identCol] ? `${i + 1}위="${item[identCol]}"` : null
                ).filter(Boolean);

                if (topValues.length > 0) {
                  const topName = prevItems[0]?.[identCol] || '';
                  guideContent += resolvePrompt('CROSS_REFERENCE_INJECT_PROMPT', '').replace('{topValues}', topValues.join(', ')).replace('{topName}', topName);
                  if (TOOL_CALLING_LOG) console.log('[Agent] 📋 Cross-reference guide injected:', topValues.join(', '));
                }
              }
            }

            if (TOOL_CALLING_LOG) console.log(`[Agent] 📋 Post-call_api guide injected${toolResult.callApiGuide ? ` (${toolResult.intention})` : ''}`);
          }

          if (['filter', 'sort', 'compute', 'customize_display', 'aggregate', 'derive'].includes(toolName) && toolResult.success) {
            if (toolName === 'filter' && toolResult.after === 0) {
              if (toolResult.fieldMissing) {
                const _session = sessionRef.current;
                const _activeBuf = _session?.getActiveBuf?.();
                const _activeSlotName = _session?.activeSlot || 'default';
                const _origCount = _activeBuf?.originalItems?.length ?? _activeBuf?.items?.length ?? 0;
                guideContent += `[NEXT STEP — CROSS-CATEGORY FILTER — MANDATORY]\nFilter field "${toolArgs.field}" is not in current "${_activeSlotName}" slot. Original "${_activeSlotName}" data preserved (${_origCount} items). The user's request IS SOLVABLE — execute these 3 steps to fulfill it:\n\n  Step 1: call_api(<intention containing "${toolArgs.field}">, slot:"<new>") — e.g., MEMBER for gender/birthDay, SCHEDULE for attendedCount.\n  Step 2: merge_slots([{slotName:"${_activeSlotName}"}, {slotName:"<new>"}], mode:"intersect", matchKey:"<memberId|userId|name>") — keeps "${_activeSlotName}" items intersected with new slot + spreads new slot fields including "${toolArgs.field}".\n  Step 3: filter(field:"${toolArgs.field}", ...) on merged slot, then show_display.\n\n[FORBIDDEN]\n• DO NOT respond with "cannot" / "성별 정보 없어요" / "다시 불러오면" — the field IS available via cross-category merge.\n• DO NOT skip Step 2 (merge_slots) — that loses the "${_activeSlotName}" context.\n• DO NOT filter "<new>" slot before merge — that produces wrong target (e.g., 여자 33명 instead of EXPIRING(${_origCount}) ∩ 여자).\n• DO NOT call finish until Steps 1-3 are attempted.`;
                if (TOOL_CALLING_LOG) console.log(`[Agent] 📋 Filter field missing: "${toolArgs.field}" → cross-category merge mandatory (slot=${_activeSlotName}, original=${_origCount})`);
              }
              else {
                guideContent += prompts.toolGuideFilterEmpty;
              }
            }
            else if (toolName === 'aggregate') {
              guideContent += prompts.toolGuideAggregate;
            }
            else if (toolName === 'compute' && toolArgs.operation === 'group') {
              guideContent += prompts.toolGuideAggregate;
            }
            else if (toolName === 'derive') {
              if (toolResult.computed === 0 && toolArgs.value && typeof toolArgs.value === 'string' && !/^\d/.test(toolArgs.value)) {
                const buf = sessionRef.current.getBuffer();
                const fieldExists = buf.items?.some(item => item[toolArgs.value] !== undefined);

                if (!fieldExists) {
                  guideContent += `[NEXT STEP] derive computed 0 — field "${toolArgs.value}" does not exist in current data. Load data containing "${toolArgs.value}" from another intention (use get_intentions to find which one has it), then merge_slots(mode:"inner") with current data by matching key, then retry derive.`;
                  if (TOOL_CALLING_LOG) console.log(`[Agent] 📋 Derive field missing: "${toolArgs.value}" → cross-intention merge guided`);
                }
                else {
                  guideContent += prompts.toolGuideDerive;
                }
              }
              else {
                guideContent += prompts.toolGuideDerive;
              }
            }
            else {
              guideContent += prompts.toolGuideDefault;
            }
          }

          if (toolName === 'recommend_sms' && toolResult.success) {
            guideContent += prompts.toolGuideSms;
            if (TOOL_CALLING_LOG) console.log('[Agent] 📋 Post-recommend_sms guide injected');
          }

          // [L15-AGGREGATE-SOLO-001 cycle 2] RATE COLUMN SEMANTIC DISCLOSURE — dual sync with nodeAgent.mjs
          //   본질: attendanceRate "예약 출석률(%)" + utilizationRate "정원 대비 출석률(%)"이 함께 표시되면 분모/분자가 달라 사용자에게 의미가 불명확
          //   Generic — case ID 의존 X. column 검사 기반.
          if (toolName === 'show_display' && toolResult.success && toolResult.displayResult?.columns) {
            const _dispCols = Array.isArray(toolResult.displayResult.columns) ? toolResult.displayResult.columns : [];
            const _hasAttendanceRate = _dispCols.includes('attendanceRate') || _dispCols.includes('attendedReservationRatio');
            const _hasUtilizationRate = _dispCols.includes('utilizationRate');
            if (_hasAttendanceRate && _hasUtilizationRate) {
              guideContent += `\n\n[RATE DISCLOSURE — MANDATORY OUTRO] Current display includes BOTH "예약 출석률(%)" (예약 대비 출석) AND "정원 대비 출석률(%)" (정원 대비 출석). Two distinct rate columns sharing the "출석률(%)" suffix create user-facing ambiguity (e.g. all rows show 100% 예약 출석률 while 정원 대비 출석률 varies 28-76% — looks like a contradiction without context). When you call finish(), the outro MUST briefly disclose what each rate means so the user can interpret the table. Recommended template: "예약 출석률은 예약 대비 출석 비율(출석/예약), 정원 대비 출석률은 정원 대비 출석 비율(출석/정원)이야." Place this disclosure as a separate trailing sentence in outro — NOT inside the table description. NEVER skip this disclosure when both rates appear together.`;
              if (TOOL_CALLING_LOG) console.log('[Agent] 📋 Rate disclosure (both rates) guide injected');
            }
            else if (_hasUtilizationRate && !_hasAttendanceRate) {
              guideContent += `\n\n[RATE DISCLOSURE — UTILIZATION ONLY] Display shows "정원 대비 출석률(%)" without 예약 출석률. The user has no context for what "정원" means. finish() outro MUST briefly disclose the formula. Recommended sentence: "정원 대비 출석률은 정원(회차×기본정원) 대비 실제 출석 비율이야." Place as trailing sentence.`;
              if (TOOL_CALLING_LOG) console.log('[Agent] 📋 Rate disclosure (utilization-only) guide injected');
            }
            else if (_hasAttendanceRate && !_hasUtilizationRate) {
              const _dispItems = Array.isArray(toolResult.displayResult.items) ? toolResult.displayResult.items : [];
              const _rateField = _dispCols.includes('attendanceRate') ? 'attendanceRate' : 'attendedReservationRatio';
              if (_dispItems.length >= 2 && _dispItems.every(it => it?.[_rateField] === 100)) {
                guideContent += `\n\n[RATE DISCLOSURE — ALL 100%] All rows show "예약 출석률(%) 100" because there are no absences in the loaded period. Without context the user may think the column is broken or uninformative. finish() outro SHOULD briefly note: "예약 출석률은 예약 대비 출석 비율이라 결석이 없으면 100%로 동일하게 나와." Place as trailing sentence so the user understands the rate column is correct, not stuck.`;
                if (TOOL_CALLING_LOG) console.log('[Agent] 📋 Rate disclosure (all 100%) guide injected');
              }
            }
          }

          // [UNDISPLAYED METRIC AUTO-APPEND] 미표시 슬롯 두번째 지표는 LLM 유도가 아닌 autoGenerateDisplay outro 결정적 보강으로 처리 //
          //   (aiUtils.buildUndisplayedSlotSummaryOutro — LLM stochastic/iteration cap 무관, 공유 함수라 dual sync 불필요)

          if (toolName === 'merge_slots' && toolResult.success) {
            guideContent += prompts.toolGuideMerge;
            if (TOOL_CALLING_LOG) console.log('[Agent] 📋 Post-merge_slots guide injected');
          }

          if (toolName === 'switch_slot' && toolResult.success) {
            guideContent += prompts.toolGuideSwitchSlot;
            if (TOOL_CALLING_LOG) console.log('[Agent] 📋 Post-switch_slot guide injected');
          }

          if (toolName === 'get_guide') {
            if (toolResult.type === 'guide_content') {
              guideContent += prompts.toolGuideGetGuideContent;
            }
            else if (toolResult.type === 'slug_list') {
              guideContent += prompts.toolGuideGetGuideSlugs;
            }
            else if (toolResult.type === 'brand' || toolResult.type === 'fact') {
              guideContent += prompts.toolGuideGetGuideDirect;
            }
            if (TOOL_CALLING_LOG) console.log(`[Agent] 📋 Post-get_guide injected (${toolResult.type})`);
          }

          // 가이드를 데이터 앞에 배치 — LLM이 데이터보다 가이드를 먼저 읽도록
          const toolContent = guideContent
            ? `${guideContent}\n\n${toolDataJson}`
            : toolDataJson;

          // tool 결과를 messages에 추가
          messages.push({
            role: "tool",
            tool_call_id: toolCall.id,
            content: toolContent,
          });
        }

        // finish 도구가 호출됐으면 최종 응답 처리 후 루프 종료 //
        if (finishCalled) {
          if (revenueChartResult) {
            const chartText = _resolveVizText(finishResponse || revenueChartResult.intro || '');
            revenueChartResult.intro = chartText;
            clearToolLoading(aiMessageId);
            if (chartText) {
              await typeMessage(aiMessageId, chartText);
            }
            updateAiMessage(aiMessageId, chartText, { revenueChartData: revenueChartResult, isNewResponse: true });
            finalText = chartText;
            log.push({ type: 'final', responseType: 'revenueChart', year: revenueChartResult.year, model: AGENT_MODEL });
          }
          else if (salesViewResult) {
            const salesText = _resolveVizText(finishResponse || salesViewResult.intro || '');
            salesViewResult.intro = salesText;
            clearToolLoading(aiMessageId);
            if (salesText) {
              await typeMessage(aiMessageId, salesText);
            }
            updateAiMessage(aiMessageId, salesText, { salesViewData: salesViewResult, isNewResponse: true });
            finalText = salesText;
            log.push({ type: 'final', responseType: 'salesView', weekCount: salesViewResult.weeksData?.length, model: AGENT_MODEL });
          }
          else if (vizResult) {
            const vizText = _resolveVizText(finishResponse || vizResult.payload?.intro || '');
            if (vizResult.payload) vizResult.payload.intro = vizText;
            clearToolLoading(aiMessageId);
            if (vizText) {
              await typeMessage(aiMessageId, vizText);
            }
            updateAiMessage(aiMessageId, vizText, { vizData: vizResult, isNewResponse: true });
            finalText = vizText;
            log.push({ type: 'final', responseType: vizResult.type, model: AGENT_MODEL });
          }
          else if (showDisplayResult) {
            await renderDisplay(showDisplayResult, finishResponse);
          }
          else {
            // [Cat8-REPEAT] text-only finish path: 동일 문장 반복 strip (dual sync with nodeAgent.mjs)
            finalText = stripAssistantHarnessLeak(stripRawIsoDatetime(stripDuplicateSentences(finishResponse)));
            await typeMessage(aiMessageId, finalText);
            log.push({ type: 'final', responseType: 'text', content: finalText, model: AGENT_MODEL });
          }
          break;
        }

        if (choice.finish_reason === 'stop') break;

      }
      catch (error) {
        console.error('[Agent] Error:', error);

        // Step 1-C — CL-1/2/3 자동 recovery (orphan tool_calls / empty_message)
        //   tool_result_missing → CL-2 handler가 orphan tool_call에 빈 result 보강
        //   empty_message → CL-3 handler가 placeholder system message push
        //   recovery 성공 시 RESUME_MARKER push + continue → 다음 iteration에서 LLM 재호출
        //   미등록 type (rate_limit/context_limit/invalid_*) → false 반환 → 기존 분기로 fall-through
        const recoveryType = classifyForRecovery(error);
        if (recoveryType && iteration < MAX_TOOL_ITERATIONS) {
          const { recovered } = await dispatchRecoveryWithMarker(recoveryType, { messages });
          if (recovered) {
            log.push({ type: 'recovery', errorType: recoveryType, iteration });
            if (TOOL_CALLING_LOG) console.log(`[Agent] ♻️  Recovered from ${recoveryType} via CL handler, continuing iteration`);
            continue;
          }
        }

        const errorType = classifyOpenAIError(error);
        const errorConfig = ERROR_CONFIG[errorType];

        if (errorType === 'OPENAI_RATE_LIMIT' && iteration < MAX_TOOL_ITERATIONS) {
          await new Promise(resolve => setTimeout(resolve, getBackoffDelay(iteration)));
          continue;
        }

        // 400 (context/structure error) → atomic unit 단위로 재정리 후 재시도 //
        if (errorType === 'OPENAI_CONTEXT_LIMIT' && messages.length > 4) {
          const before = messages.length;
          const errorCompacted = compactMessages(messages, Math.floor(messages.length * 0.6), 4);
          messages.length = 0;
          messages.push(...errorCompacted);
          messages.push({
            role: "system",
            content: `[Guide Refresh — context was compacted]\n${prompts.workflowGuide}\n\n${prompts.filterGuide}\n\n${prompts.callApiUniversalRules}`,
          });
          if (TOOL_CALLING_LOG) console.log(`[Agent] Error recovery compacted: ${before} → ${messages.length} messages (guide re-injected)`);
          continue;
        }

        finalText = errorConfig?.message || '오류가 발생했습니다.';
        updateAiMessage(aiMessageId, finalText);
        log.push({ type: 'error', errorType, message: finalText });

        break;
      }
    }

    if (iteration >= MAX_TOOL_ITERATIONS) {
      finalText = '요청 처리 중 최대 시도 횟수를 초과했습니다.';
      updateAiMessage(aiMessageId, finalText);
      log.push({ type: 'error', message: 'max iterations exceeded' });
      clearToolLoading(aiMessageId);
    }

    // [LAZY TOOL-SET D1] 다음 turn viz 래치 시드 — 본 turn viz 렌더(도구 호출 or 오케스트레이션) 시 follow-up turn에 viz 도구 유지 (dual sync with nodeAgent.mjs) //
    if (sessionRef.current) {
      sessionRef.current._lastTurnUsedViz = log.some(l => l.type === 'tool_call' && typeof l.tool === 'string' && l.tool.startsWith('show_') && l.tool !== 'show_display')
        || _ltvOrchestrated || _rankingOrchestrated || _memberStatusOrchestrated || _membershipPrefOrchestrated || _periodSalesOrchestrated || _pointManageOrchestrated;
      // [MEMBER-TREND CONTEXT] 직전 턴이 회원추이 차트였는지 per-turn 재계산(sticky 아님) — POSITIVE-3 맥락 게이트용 ("무료체험만" 등 약한 후속). dual sync nodeAgent //
      sessionRef.current._lastMemberTrendTurn = log.some(l => l.type === 'tool_call' && l.tool === 'show_member_trend');
    }

    // Agent Loop 도중 updateAiMessage로 저장된 SMS 추천 데이터를 꺼내서 saveSession에 전달
    const smsRecommendation = chatMessagesRef.current.find(m => m.id === aiMessageId)?.smsRecommendation || null;

    finalText = stripMarkdownBold(finalText);
    finalText = stripRawIsoDatetime(finalText);
    // [ROOT_CAUSE_7_META_LEAK] LLM이 harness format(`response to=functions.finish{...}`) 또는 corrupted token stream을 content에 직접 emit한 경우 strip (dual sync with nodeAgent.mjs).
    finalText = stripAssistantHarnessLeak(finalText);
    // [Group B compute/clarification dedup] 2026-05-30: universal sentence-level dedup — compute tool result / clarification text path도 커버 (dual sync with nodeAgent.mjs).
    //   본질: 기존 stripDuplicateSentences는 text-only finish path(line ~2624)에만 적용 → MAX_TEXT_ONLY nudge path / display path는 우회.
    //   universal final-pass에서 적용 — decimal 보호 + 표 영역 보존 (head 영역만 처리, table tail 그대로 통과).
    finalText = stripDuplicateSentences(finalText);

    // [L16-SQL-INTERSECT-EMPTY-EXPLAIN] SQL INTERSECT/EXCEPT 0건 응답 시 LLM이 slot breakdown을
    // 누락한 경우 결정적 augment (safety net). dual sync with nodeAgent.mjs augmentSqlEmptyFinalText
    finalText = augmentSqlEmptyFinalText(finalText, sessionRef.current);

    // [#10 MEMBER-DETAIL MULTI-ASPECT COMPLETION] 멀티-aspect 회원조회("마지막 출석일이랑 회원권 상태")에서 LLM이 둘째 aspect의 //
    //   call_api를 드롭해도 누락 aspect를 _lastMember.memberId로 force-fetch + headline 결정적 부착. show_display/text-only 양 경로 커버. //
    //   dual sync with nodeAgent.mjs. //
    finalText = await augmentMemberDetailAspectsFinalText(finalText, sessionRef.current, userInput);

    // [MULTI-AXIS REUSE SEED] call_api 없는 turn(직전 grouped 슬롯 재사용)에서도 입력이 2+ 축 명시 시 해당 axisKey 슬롯을 seed //
    //   → 재사용 경로(T2 show_display만)에서도 양축 표 보장. 게이팅: 입력 축 키워드 AND 해당 axisKey 슬롯 둘 다 존재 시만 (additive·과렌더 차단). dual sync with nodeAgent.mjs. //
    {
      const _MULTI_AXIS_REUSE_KW = [
        { axisKey: '_groupBy=day',    terms: ['요일별', '요일', '무슨 요일', '일별'] },
        { axisKey: '_groupBy=time',   terms: ['시간대별', '시간대', '시간별', '몇 시'] },
        { axisKey: '_groupBy=coach',  terms: ['코치별', '강사별'] },
        { axisKey: '_groupBy=lesson', terms: ['수업별', '레슨별', '클래스별'] },
      ];
      const _named = _MULTI_AXIS_REUSE_KW.filter(a => a.terms.some(t => (userInput || '').includes(t)));
      if (_named.length >= 2) {
        for (const ax of _named) {
          for (const [_slotName, _buf] of (sessionRef.current?.buffers || new Map())) {
            if (_buf && _buf.items?.length && _buf._currentAxisKey === ax.axisKey) { groupedSlotsThisTurn.add(_slotName); break; }
          }
        }
      }
    }
    // [MULTI-AXIS AUTO-RENDER] 본 turn 다축(_groupBy) 슬롯 중 미표출 축을 결정적으로 자동 렌더 (LLM switch_slot 누락 보강, dual sync with nodeAgent)
    //   요일별 AND 시간대별 같은 다축 질문에서 LLM이 1개 축만 표출해도, call_api로 로드한 나머지 grouped 축을 turn-end에 자동 표출.
    if (groupedSlotsThisTurn.size > 0 && (displaysThisTurn.length >= 1 || groupedSlotsThisTurn.size >= 2)) {
      const _shownAxes = new Set(displaysThisTurn.map(d => d.axisKey));
      const _prevActive = sessionRef.current?.activeSlot;
      for (const _slotName of groupedSlotsThisTurn) {
        const _buf = sessionRef.current?.buffers?.get(_slotName);
        if (!_buf || !(_buf.items?.length)) continue;
        const _axisKey = (typeof _buf._currentAxisKey === 'string' && _buf._currentAxisKey) || _slotName;
        if (_shownAxes.has(_axisKey)) continue;
        try {
          sessionRef.current.setActiveSlot(_slotName);
          const _r = await sessionRef.current.executeTool('show_display', { intro: '', outro: '', columns: null, columnLabels: null, max_count: null });
          if (_r?.success && _r.displayResult?.items?.length) {
            displaysThisTurn.push({ axisKey: _axisKey, display: _r.displayResult });
            _shownAxes.add(_axisKey);
          }
        } catch (e) { /* auto-render 실패 무시 */ }
      }
      if (_prevActive) { try { sessionRef.current.setActiveSlot(_prevActive); } catch (e) { /* noop */ } }
      if (displaysThisTurn.length > 1) {
        lastDisplayResult = displaysThisTurn[0].display;
        lastExtraDisplays = displaysThisTurn.slice(1).map(d => d.display);
        if (aiMessageId) {
          updateAiMessage(aiMessageId, lastDisplayResult.intro || '', { displayItems: lastDisplayResult, extraDisplays: lastExtraDisplays, isNewResponse: false });
        }
        const _allText = displaysThisTurn.map(d => renderDisplayAsText(d.display)).filter(Boolean).join('\n\n');
        if (_allText) finalText = stripAssistantHarnessLeak(_allText);
      }
    }

    // [MULTI-AXIS COMPOSITION FORCE-RENDER] compute(group)-path 다축 구성(나이대 AND 성별 등)에서 LLM이 일부 축만 표출하고 종료해도,
    //   입력에 명시된 나머지 group 축을 _preGroupOriginalItems(원본 list)에서 결정적으로 재집계+렌더. dual sync with nodeAgent.mjs.
    //   call_api groupBy(위 MULTI-AXIS AUTO-RENDER)는 2 슬롯 영속이라 force-render 가능하나, compute는 단일 슬롯 파괴적(buf.items 교체)이라
    //   미표출 축 데이터가 사라짐 → _preGroupOriginalItems에서 재파생. AUTO-COMPUTE("가이드만으로 LLM compute 결정성 확보 불가")와 동형.
    //   축 식별: 도메인 한정 member 구성 축 맵(가이드 WORKFLOW_GUIDE_MEMBER 라우팅 동형) + field 존재(sample)로 grounding.
    //   게이팅: 입력 2+ 축 명시 + grouped 멤버 버퍼인데 일부 축 미렌더 시만. 단일축 case(성별 분포만)는 미발동.
    {
      const _MEMBER_COMPOSITION_AXES = [
        { terms: ['나이대', '연령대', '나이', '연령'], field: 'birthDay', compute: 'age', axisKey: '_groupBy=birthDay' },
        { terms: ['성별', '남녀'], field: 'gender', compute: null, axisKey: '_groupBy=gender' },
        { terms: ['회원권'], field: 'viewMembershipName', compute: null, axisKey: '_groupBy=viewMembershipName' },
      ];
      let _compSlot = null, _compBuf = null;
      const _compBufs = sessionRef.current?.buffers;
      if (_compBufs) {
        for (const [_name, _b] of _compBufs) {
          if (_b && Array.isArray(_b._preGroupOriginalItems) && _b._preGroupOriginalItems.length
              && typeof _b._currentAxisKey === 'string' && _b._currentAxisKey.startsWith('_groupBy=')) {
            _compBuf = _b; _compSlot = _name; break;
          }
        }
      }
      if (_compBuf) {
        const _compSample = _compBuf._preGroupOriginalItems[0] || {};
        const _compNamed = _MEMBER_COMPOSITION_AXES.filter(a => (a.field in _compSample) && a.terms.some(t => userInput.includes(t)));
        const _compShown = new Set(displaysThisTurn.map(d => d.axisKey));
        const _compMissing = _compNamed.length >= 2 ? _compNamed.filter(a => !_compShown.has(a.axisKey)) : [];
        if (_compMissing.length) {
          const _compPrevActive = sessionRef.current?.activeSlot;
          try { sessionRef.current.setActiveSlot(_compSlot); } catch (e) { /* noop */ }
          for (const ax of _compMissing) {
            try {
              const _cr = await sessionRef.current.executeTool('compute', { operation: 'group', field: ax.field, ...(ax.compute ? { compute: ax.compute } : {}) });
              if (!_cr?.success) continue;
              const _r = await sessionRef.current.executeTool('show_display', { intro: '', outro: '', columns: null, columnLabels: null, max_count: null });
              if (_r?.success && _r.displayResult?.items?.length) {
                displaysThisTurn.push({ axisKey: ax.axisKey, display: _r.displayResult });
                _compShown.add(ax.axisKey);
              }
            } catch (e) { /* auto-compose 실패 무시 */ }
          }
          if (_compPrevActive) { try { sessionRef.current.setActiveSlot(_compPrevActive); } catch (e) { /* noop */ } }
          if (displaysThisTurn.length > 1) {
            lastDisplayResult = displaysThisTurn[0].display;
            lastExtraDisplays = displaysThisTurn.slice(1).map(d => d.display);
            if (aiMessageId) {
              updateAiMessage(aiMessageId, lastDisplayResult.intro || '', { displayItems: lastDisplayResult, extraDisplays: lastExtraDisplays, isNewResponse: false });
            }
            const _compAllText = displaysThisTurn.map(d => renderDisplayAsText(d.display)).filter(Boolean).join('\n\n');
            if (_compAllText) finalText = stripAssistantHarnessLeak(_compAllText);
          }
        }
      }
    }

    // [L9a-MEMBERSHIP-002 fix] 이전 turn 결과 저장 — 다음 turn SMS guard에서 참조 //
    sessionRef.current._previousFinalText = finalText || '';
    const _prevBufP = sessionRef.current.getBuffer?.() || {};

    sessionRef.current._previousBufferItemCount = Array.isArray(_prevBufP.items) ? _prevBufP.items.length : 0;

    return {
      log,
      conversationId,
      finalText,
      intention: lastIntention,
      elapsedMs: Date.now() - startTime,
      displayResult: lastDisplayResult,
      extraDisplays: lastExtraDisplays,
      smsRecommendation,
      salesViewData: salesViewResult,
      revenueChartData: revenueChartResult,
      vizData: vizResult,
      // [P2c] turn-fact용 버퍼 스냅샷 — aiTraceSchema 공유 빌더 (하니스와 동형) //
      bufferSnapshot: snapshotBuffers(sessionRef.current),
      // [OB-1 가드 트레이스] 본 turn 가드 발동 전수 + 거짓 no-data 리스크 플래그 (dual sync with nodeAgent.mjs) //
      guardFirings: (sessionRef.current._guardFirings || []).slice(),
      hadFalseNoDataRisk: computeFalseNoDataRisk(sessionRef.current._guardFirings),
    };
  }

  // 테스트용 ref 최신화 (매 렌더마다 runAgent 참조 갱신, useEffect 재실행 방지) //
  if (TOOL_CALLING_LOG) {
    testRunAgentRef.current = async (input) => {
      const aiMessageId = `test_${Date.now()}`;
      return runAgent(input, aiMessageId);
    };
  }

  // ============================================
  // 세션 관련 함수
  // ============================================
  // 세션 목록 초기 로드 //
  useEffect(() => {
    loadSessions();
  }, []);

  // 세션 목록 불러오기 //
  const loadSessions = () => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2('ai-chat/sessions', 'GET');

          if (responseData.status === 200) {
            setSessions(responseData.data || []);
          }
        }
        catch (error) { /* 무시 */ }
      }
    )();
  };

  // 세션 대화 내역 불러오기 //
  const loadConversations = (targetSessionId) => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2(`ai-chat/sessions/${targetSessionId}`, 'GET');

          if (responseData.status === 200 && responseData.data) {
            const messages = [];
            const restoredRatings = {};

            responseData.data.forEach(entry => {
              messages.push({ id: `msg_${uuidv4()}`, text: entry.requestMessage, isAi: false });

              // AI 응답 복원 — JSON(텍스트 + 테이블 + SMS) 또는 텍스트 원문 //
              let aiMsg = { id: `msg_${uuidv4()}`, isAi: true };
              try {
                const parsed = JSON.parse(entry.responseMessage);
                if (parsed?.text !== undefined) {
                  aiMsg.text = parsed.text || '';
                  if (parsed.displayItems) aiMsg.displayItems = parsed.displayItems;
                  if (Array.isArray(parsed.extraDisplays) && parsed.extraDisplays.length > 0) aiMsg.extraDisplays = parsed.extraDisplays;
                  if (parsed.smsRecommendation) aiMsg.smsRecommendation = restoreSmsFromSave(parsed.smsRecommendation);
                  if (parsed.salesViewData) aiMsg.salesViewData = parsed.salesViewData;
                  if (parsed.revenueChartData) aiMsg.revenueChartData = parsed.revenueChartData;
                  if (parsed.vizData) aiMsg.vizData = parsed.vizData;
                }
                else {
                  aiMsg.text = entry.responseMessage;
                }
              }
              catch {
                aiMsg.text = entry.responseMessage;
              }
              messages.push(aiMsg);

              // 복원된 메시지에 대해 conversationMetaRef 설정 //
              if (entry.id) {
                conversationMetaRef.current[aiMsg.id] = {
                  conversationId: entry.id,
                  userInput: entry.requestMessage,
                  intention: entry.intention || null,
                  elapsedMs: entry.elapsedMs || null,
                };

                // 저장된 사용자평가 복원 — 백엔드 ai-chat/sessions 응답의 score/reason/comment //
                //   (백엔드 미반영 시 score undefined → 복원 skip = 기존 동작 유지) //
                if (entry.score === 1 || entry.score === -1) {
                  restoredRatings[aiMsg.id] = {
                    score: entry.score,
                    reason: entry.reason ?? null,
                    comment: entry.comment ?? null,
                  };
                }
              }
            });

            const targetSession = sessions.find(s => s.sessionId === targetSessionId);

            setSessionId(targetSessionId);
            setSessionTitle(targetSession?.sessionTitle || null);
            setChatMessages(messages);
            setMessageRatings(restoredRatings);

            // 멀티턴 컨텍스트 복원 — session-state GET → buffers/messages rehydrate (저장본 없으면 fresh) //
            sessionRef.current = new AgentSession();
            try {
              const stateRes = await fetchModule.getDataVersion2(`ai-log/session-state/${targetSessionId}`, 'GET');

              if (stateRes.status === 200 && stateRes.data) {
                // P2c: 신행 gzip+base64 / 구행 평문 JSON 혼재 — decodeMaybeGzip이 자동 분기 //
                const buffersRaw = await decodeMaybeGzip(stateRes.data.buffersSnapshot);
                const messagesRaw = await decodeMaybeGzip(stateRes.data.agentMessages);
                sessionRef.current.rehydrate(buffersRaw ? JSON.parse(buffersRaw) : null);
                agentMessagesRef.current = messagesRaw ? JSON.parse(messagesRaw) : null;
              }
              else {
                agentMessagesRef.current = null;
              }
            }
            catch (error) {
              agentMessagesRef.current = null;
            }

            resetSpacer();
          }
        }
        catch (error) { /* 무시 */ }
      }
    )();
  };

  // 세션 저장 — P2c: 단일 POST ai-log/turn (구 ai-log/conversation 폐기, renderJson은 백엔드가 response_message에 적재) //
  const saveSession = async (userInput, aiResponse, intention, elapsedMs, conversationId, displayResult, aiMessageId, smsRecommendation, salesViewData, revenueChartData, vizData, extraDisplays, agentResult) => {
    if (aiMessageId) {
      conversationMetaRef.current[aiMessageId] = { conversationId, userInput, intention, elapsedMs };
    }
    let currentTitle = sessionTitle;

    if (!currentTitle) {
      currentTitle = await generateTitle(userInput);
      setSessionTitle(currentTitle);
    }

    const fetchModule = new FetchModule();

    try {
      await fetchModule.postDataVersion2('ai-chat/sessions/save', 'POST', {
        sessionId: sessionId,
        sessionTitle: currentTitle,
      });
    }
    catch (error) { /* 세션 저장 실패는 무시 */ }

    try {
      // renderJson — 화면 재현 전용 JSON (재방문 재렌더 소스, 기존 responseMessage 포맷 유지) //
      const renderJson = JSON.stringify({
        text: aiResponse,
        displayItems: displayResult || null,
        extraDisplays: (Array.isArray(extraDisplays) && extraDisplays.length > 0) ? extraDisplays : null,
        smsRecommendation: smsRecommendation ? buildSmsForSave(smsRecommendation) : null,
        salesViewData: salesViewData || null,
        revenueChartData: revenueChartData || null,
        vizData: vizData || null,
      });

      if (TOOL_CALLING_LOG) console.log('[Agent] Saving turn:', { sessionId, conversationId });
      await sendTurnTelemetry({ conversationId, userInput, finalText: aiResponse, intention, elapsedMs, renderJson, agentResult });
    }
    catch (error) { /* 대화 저장 실패 무시 — sendTurnTelemetry 내부 재시도/로깅 처리 */ }

    // 멀티턴 컨텍스트 저장 — 세션 재방문 시 buffers/messages rehydrate용 (세션당 최신 1건 upsert) //
    // P2c: gzip+base64 전환 (백엔드 스펙 정합) — gzip 실패 시 평문 폴백, 복원은 decodeMaybeGzip 양쪽 처리 //
    try {
      const agentMessagesPlain = JSON.stringify(agentMessagesRef.current || []);
      const buffersSnapshotPlain = JSON.stringify(sessionRef.current.serialize());

      await postWithRetry('ai-log/session-state', {
        sessionId: sessionId,
        agentMessages: (await gzipBase64(agentMessagesPlain)) ?? agentMessagesPlain,
        buffersSnapshot: (await gzipBase64(buffersSnapshotPlain)) ?? buffersSnapshotPlain,
        schemaVersion: '1.1',
      }, 'session-state');
    }
    catch (error) { /* 세션 상태 저장 실패 무시 */ }

    loadSessions();
  };

  // 세션 삭제 //
  const deleteSession = (targetSessionId) => {
    if (TOOL_CALLING_LOG) console.log('[Session] 🗑️ deleteSession:', targetSessionId);
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2(
            `ai-chat/sessions/${targetSessionId}`, 'DELETE'
          );

          if (TOOL_CALLING_LOG) console.log('[Session] 🗑️ delete response:', responseData.status);

          if (responseData.status === 200) {
            if (sessionId === targetSessionId) {
              if (TOOL_CALLING_LOG) console.log('[Session] 🗑️ 현재 세션 삭제 → 새 채팅 전환');
              handleNewChat();
            }

            loadSessions();
          }
        }
        catch (error) {
          if (TOOL_CALLING_LOG) console.error('[Session] 🗑️ delete error:', error);
        }
      }
    )();
  };

  // 새 채팅 세션 생성 //
  const handleNewChat = () => {
    const newSessionId = `session_${uuidv4()}`;
    setSessionId(newSessionId);
    setSessionTitle(null);
    setChatMessages([]);
    setMessageRatings({});
    agentMessagesRef.current = null;
    pendingSmsRef.current = null;
    resetSpacer();
    sessionRef.current.destroy();
    sessionRef.current = new AgentSession();
  };

  return {
    // 상태
    chatMessages,
    chatInput,
    isResponding,
    tokenExhausted,
    usagePercent,
    sessions,
    sessionId,
    chatMessagesContainerRef,
    messageRatings,

    // 액션
    setChatInput,
    handleSend,
    handleStop,
    handleNewChat,
    loadConversations,
    deleteSession,
    handleRateMessage,
    isMessageRatable,

    // SMS 핸들러 //
    handleSmsSend,
    handleIndividualSmsSend,
    handleIndividualSmsGenerate,

    // 테스트용 — dev 환경에서만 노출 //
    ...(TOOL_CALLING_LOG ? {
      __testRunAgent: testRunAgentRef,
      __testGetClient: () => clientRef.current,
      __testGetSession: () => sessionRef.current,
    } : {}),
  };
}
